/**
 * Window Manager Module
 * Handles window lifecycle, z-index management, dragging, resizing
 */

// Prevent redeclaration if script is loaded multiple times
if (typeof WindowManager === 'undefined') {
    class WindowManager {
        constructor() {
            this.windows = new Map();
            this.zIndexCounter = 1000;
            this.activeWindow = null;
            this.cascadeOffset = { x: 0, y: 0 };
            this.cascadeStep = 30; // Pixels to offset each new window
            this.openingWindows = new Set(); // Track windows currently being opened to prevent duplicates
            this.mobileBreakpoint = 767; // Mobile breakpoint threshold
            this.lastViewportWidth = window.innerWidth;
            this.resizeThreshold = 50; // Pixels change before triggering window updates
            this.initResponsiveListeners();
        }

        /**
         * Check if current viewport is mobile
         */
        isMobile() {
            return window.innerWidth <= this.mobileBreakpoint;
        }

        /**
         * Initialize responsive listeners for viewport changes
         */
        initResponsiveListeners() {
            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    this.handleViewportResize();
                }, 150);
            });
        }

        /**
         * Handle viewport resize - update all windows if crossing threshold
         */
        async handleViewportResize() {
            const currentWidth = window.innerWidth;
            const widthChange = Math.abs(currentWidth - this.lastViewportWidth);
            const crossedThreshold = currentWidth <= this.mobileBreakpoint !== this.lastViewportWidth <= this.mobileBreakpoint;

            // If we crossed the mobile/desktop threshold or had significant size change, update all windows
            if (crossedThreshold || widthChange >= this.resizeThreshold) {
                await this.updateAllWindowsForViewport();
                this.lastViewportWidth = currentWidth;
            } else {
                // Still constrain windows to viewport on any resize
                this.constrainAllWindows();
            }
        }

        /**
         * Add or remove the .st-window--mobile-fullscreen class based on the
         * current viewport. Idempotent — safe to call on every resize.
         * Only commerce windows (product/collection/cart) ever get the class.
         */
        applyMobileFullscreenClass(windowElement, windowId) {
            if (!windowElement || !windowId) return;
            const isMobile = window.matchMedia('(max-width: 767px)').matches;
            const isCommerceWindow =
                windowId === 'cart' ||
                windowId.startsWith('product-') ||
                windowId.startsWith('collection-');
            if (isMobile && isCommerceWindow) {
                windowElement.classList.add('st-window--mobile-fullscreen');
            } else {
                windowElement.classList.remove('st-window--mobile-fullscreen');
            }
        }

        /**
         * Update all open windows to use mobile/desktop sizes and positions based on current viewport
         */
        async updateAllWindowsForViewport() {
            const isMobile = this.isMobile();

            // Update all windows in parallel
            const updatePromises = [];
            this.windows.forEach((windowData, windowId) => {
                const windowElement = windowData.element;
                if (!windowElement) return;

                // Re-evaluate fullscreen class for the new viewport so commerce
                // windows opened on desktop snap to fullscreen on shrink, and
                // vice versa.
                this.applyMobileFullscreenClass(windowElement, windowId);

                // Get the section name from the window element
                const sectionName = windowElement.getAttribute('data-section-name');
                if (!sectionName) return;

                // Try to get mobile/desktop sizes from the original section
                updatePromises.push(this.updateWindowSizeFromSection(windowElement, sectionName, isMobile));

                // Update position from data attributes if they exist
                this.updateWindowPositionFromAttributes(windowElement, isMobile);

                // Update hero banner images if this is a hero banner window
                this.updateHeroBannerImage(windowElement, isMobile);
            });

            // Wait for all updates to complete
            await Promise.all(updatePromises);
        }

        /**
         * Update hero banner image based on mobile/desktop viewport
         */
        updateHeroBannerImage(windowElement, isMobile) {
            const heroElement = windowElement.querySelector('.st-hero-banner__hero');
            if (!heroElement) return;

            const desktopImage = heroElement.getAttribute('data-hero-image-desktop');
            const mobileImage = heroElement.getAttribute('data-hero-image-mobile');

            // Default to desktop image
            let imageUrl = desktopImage;

            // Only use mobile image if we're on mobile AND mobile image exists
            if (isMobile && mobileImage) {
                imageUrl = mobileImage;
            }

            // If no desktop image but mobile exists, use mobile as fallback
            if (!imageUrl && mobileImage) {
                imageUrl = mobileImage;
            }

            if (imageUrl) {
                heroElement.style.backgroundImage = `url('${imageUrl}')`;
            }
        }

        /**
         * Update window size from section data attributes
         */
        async updateWindowSizeFromSection(windowElement, sectionName, isMobile) {
            const windowId = windowElement.dataset.windowId;
            const windowData = this.windows.get(windowId);
            if (!windowData) return;

            // First, check if data attributes are stored directly on the window element
            let windowWidth = this.getWindowSizeAttribute(windowElement, isMobile, 'width');
            let windowHeight = this.getWindowSizeAttribute(windowElement, isMobile, 'height');

            // If we found attributes on the window element, use them directly
            if (windowWidth || windowHeight) {
                this.applyWindowSize(windowElement, windowData, windowWidth, windowHeight);
                return;
            }

            // Otherwise, fetch from section (fallback for windows created before this feature)
            try {
                const sectionUrl = `/?section_id=${sectionName}`;
                const response = await fetch(sectionUrl, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'text/html'
                    }
                });

                if (response.ok) {
                    const html = await response.text();
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    const sectionContent = doc.getElementById(`shopify-section-${sectionName}`);

                    if (sectionContent) {
                        // Find the actual content element - could be one with data-window-title or the first child div
                        let contentElement = sectionContent.querySelector('[data-window-title]');

                        // If no element with data-window-title, try to find the first div child (the actual section content)
                        if (!contentElement) {
                            contentElement = sectionContent.querySelector('div[data-width], div[data-width-mobile], div[data-height], div[data-height-mobile]') ||
                                sectionContent.firstElementChild ||
                                sectionContent;
                        }

                        // Check for mobile or desktop attributes - check both contentElement and sectionContent
                        windowWidth = this.getWindowSizeAttribute(contentElement, isMobile, 'width') ||
                            this.getWindowSizeAttribute(sectionContent, isMobile, 'width');
                        windowHeight = this.getWindowSizeAttribute(contentElement, isMobile, 'height') ||
                            this.getWindowSizeAttribute(sectionContent, isMobile, 'height');

                        // Store attributes on window element for future updates
                        if (windowWidth) {
                            windowElement.setAttribute('data-width', windowWidth);
                        }
                        if (windowHeight) {
                            windowElement.setAttribute('data-height', windowHeight);
                        }
                        const widthMobile = contentElement?.getAttribute('data-width-mobile') || sectionContent?.getAttribute('data-width-mobile');
                        const heightMobile = contentElement?.getAttribute('data-height-mobile') || sectionContent?.getAttribute('data-height-mobile');
                        if (widthMobile && !windowElement.getAttribute('data-width-mobile')) {
                            windowElement.setAttribute('data-width-mobile', widthMobile);
                        }
                        if (heightMobile && !windowElement.getAttribute('data-height-mobile')) {
                            windowElement.setAttribute('data-height-mobile', heightMobile);
                        }

                        // Store position attributes
                        const left = this.getWindowPositionAttribute(contentElement, isMobile, 'left') ||
                            this.getWindowPositionAttribute(sectionContent, isMobile, 'left');
                        const top = this.getWindowPositionAttribute(contentElement, isMobile, 'top') ||
                            this.getWindowPositionAttribute(sectionContent, isMobile, 'top');
                        const leftMobile = contentElement?.getAttribute('data-left-mobile') || sectionContent?.getAttribute('data-left-mobile');
                        const topMobile = contentElement?.getAttribute('data-top-mobile') || sectionContent?.getAttribute('data-top-mobile');

                        if (left && !windowElement.getAttribute('data-left')) {
                            windowElement.setAttribute('data-left', left);
                        }
                        if (top && !windowElement.getAttribute('data-top')) {
                            windowElement.setAttribute('data-top', top);
                        }
                        if (leftMobile && !windowElement.getAttribute('data-left-mobile')) {
                            windowElement.setAttribute('data-left-mobile', leftMobile);
                        }
                        if (topMobile && !windowElement.getAttribute('data-top-mobile')) {
                            windowElement.setAttribute('data-top-mobile', topMobile);
                        }

                        // Apply the sizes we found
                        this.applyWindowSize(windowElement, windowData, windowWidth, windowHeight);

                        // Apply position if found
                        if (left || top) {
                            this.updateWindowPositionFromAttributes(windowElement, isMobile);
                        }
                    }
                }
            } catch (error) {
                console.warn(`WindowManager: Could not update window size from section ${sectionName}:`, error);
            }
        }

        /**
         * Update window position from data attributes stored on window element
         */
        updateWindowPositionFromAttributes(windowElement, isMobile) {
            if (!windowElement) return;

            const windowData = this.windows.get(windowElement.dataset.windowId);
            if (!windowData) return;

            // Skip if window is maximized or minimized
            if (windowData.isMaximized || windowData.state === 'minimized') return;

            // Read position attributes
            const dataLeft = this.getWindowPositionAttribute(windowElement, isMobile, 'left');
            const dataTop = this.getWindowPositionAttribute(windowElement, isMobile, 'top');

            // Apply position if attributes exist
            if (dataLeft !== null && dataLeft !== undefined && dataTop !== null && dataTop !== undefined) {
                // Handle centering case
                if (dataLeft === '50%' && dataTop === '50%') {
                    windowElement.style.position = 'fixed';
                    windowElement.style.left = '50%';
                    windowElement.style.top = '50%';
                    windowElement.style.transform = 'translate(-50%, -50%)';
                    windowData.position = { x: '50%', y: '50%', centered: true };
                    console.log('[WindowManager] Updated window position to centered from data attributes');
                } else {
                    // Apply individual position values
                    if (dataLeft) {
                        windowElement.style.left = dataLeft;
                    }
                    if (dataTop) {
                        windowElement.style.top = dataTop;
                    }
                    // Update window data
                    windowData.position = {
                        x: dataLeft || windowData.position?.x || 0,
                        y: dataTop || windowData.position?.y || 0
                    };
                    console.log('[WindowManager] Updated window position from data attributes:', { left: dataLeft, top: dataTop });
                }
            } else if (dataLeft !== null && dataLeft !== undefined) {
                // Only left is set
                windowElement.style.left = dataLeft;
                const currentY = windowData.position?.y || parseInt(windowElement.style.top) || 0;
                windowData.position = { x: dataLeft, y: currentY };
                console.log('[WindowManager] Updated window position from data-left:', dataLeft);
            } else if (dataTop !== null && dataTop !== undefined) {
                // Only top is set
                windowElement.style.top = dataTop;
                const currentX = windowData.position?.x || parseInt(windowElement.style.left) || 0;
                windowData.position = { x: currentX, y: dataTop };
                console.log('[WindowManager] Updated window position from data-top:', dataTop);
            }

            // Constrain to viewport after position update
            this.constrainWindowToViewport(windowElement);
        }

        /**
         * Apply window size from data attributes
         */
        applyWindowSize(windowElement, windowData, windowWidth, windowHeight) {
            if (!windowWidth && !windowHeight) return;

            const desktop = document.getElementById('desktop');
            const viewportWidth = desktop ? desktop.getBoundingClientRect().width : window.innerWidth;

            if (windowWidth) {
                // Handle responsive values like percentages
                let finalWidth = windowWidth;
                // Ensure width has units (add px if it's just a number)
                if (!isNaN(finalWidth) && !finalWidth.includes('px') && !finalWidth.includes('%') && !finalWidth.includes('vw')) {
                    finalWidth = `${finalWidth}px`;
                } else if (finalWidth.includes('%')) {
                    const percent = parseFloat(finalWidth) / 100;
                    finalWidth = `${viewportWidth * percent}px`;
                } else if (!finalWidth.includes('px') && !isNaN(finalWidth)) {
                    finalWidth = `${Math.min(parseInt(finalWidth), viewportWidth - 40)}px`;
                }
                windowElement.style.width = finalWidth;
                windowData.size.width = finalWidth;
            }

            if (windowHeight && windowHeight !== 'auto') {
                // Ensure height has units (add px if it's just a number)
                let finalHeight = windowHeight;
                if (!isNaN(finalHeight) && !finalHeight.includes('px') && !finalHeight.includes('%') && !finalHeight.includes('vh')) {
                    finalHeight = `${finalHeight}px`;
                }
                windowElement.style.height = finalHeight;
                windowData.size.height = finalHeight;
            }

            // Constrain to viewport after size update
            this.constrainWindowToViewport(windowElement);
        }

        /**
         * Helper function to get data attributes for a product handle from product-app section
         * @param {string} productHandle - Product handle to look up
         * @returns {Object} Data attributes object
         */
        getProductDataAttributes(productHandle) {
            if (!productHandle) {
                console.log('[WindowManager] getProductDataAttributes: No product handle provided');
                return {};
            }

            console.log('[WindowManager] getProductDataAttributes: Looking for product-app section with handle:', productHandle);

            // Try to find product-app section for this product
            const productAppSection = document.querySelector(`.st-product-app-content[data-product-handle="${productHandle}"]`);

            if (!productAppSection) {
                // Try alternative selector - maybe the section is in a different structure
                const allProductAppSections = document.querySelectorAll('.st-product-app-content');
                console.log('[WindowManager] getProductDataAttributes: Found', allProductAppSections.length, 'product-app sections total');
                allProductAppSections.forEach((section, index) => {
                    const handle = section.getAttribute('data-product-handle');
                    console.log(`[WindowManager] getProductDataAttributes: Section ${index} has handle:`, handle);
                });
                return {};
            }

            console.log('[WindowManager] getProductDataAttributes: Found product-app section, checking attributes...');
            const dataAttributes = this.collectDataAttributes(productAppSection);
            console.log('[WindowManager] getProductDataAttributes: Collected dataAttributes:', dataAttributes);
            console.log('[WindowManager] getProductDataAttributes: Raw attributes on element:', {
                'data-width': productAppSection.getAttribute('data-width'),
                'data-height': productAppSection.getAttribute('data-height'),
                'data-left': productAppSection.getAttribute('data-left'),
                'data-top': productAppSection.getAttribute('data-top'),
                'data-width-mobile': productAppSection.getAttribute('data-width-mobile'),
                'data-height-mobile': productAppSection.getAttribute('data-height-mobile'),
                'data-left-mobile': productAppSection.getAttribute('data-left-mobile'),
                'data-top-mobile': productAppSection.getAttribute('data-top-mobile')
            });

            return dataAttributes;
        }

        /**
         * Helper function to collect data-width/data-height/data-left/data-top attributes from an element
         */
        collectDataAttributes(element) {
            const dataAttributes = {};
            if (element) {
                const width = element.getAttribute('data-width');
                const height = element.getAttribute('data-height');
                const widthMobile = element.getAttribute('data-width-mobile');
                const heightMobile = element.getAttribute('data-height-mobile');
                const left = element.getAttribute('data-left');
                const top = element.getAttribute('data-top');
                const leftMobile = element.getAttribute('data-left-mobile');
                const topMobile = element.getAttribute('data-top-mobile');

                if (width) dataAttributes['data-width'] = width;
                if (height) dataAttributes['data-height'] = height;
                if (widthMobile) dataAttributes['data-width-mobile'] = widthMobile;
                if (heightMobile) dataAttributes['data-height-mobile'] = heightMobile;
                if (left) dataAttributes['data-left'] = left;
                if (top) dataAttributes['data-top'] = top;
                if (leftMobile) dataAttributes['data-left-mobile'] = leftMobile;
                if (topMobile) dataAttributes['data-top-mobile'] = topMobile;
            }
            return dataAttributes;
        }

        /**
         * Helper function to get window size attribute with mobile/desktop fallback
         * @param {HTMLElement} element - Element to read from
         * @param {boolean} isMobile - Whether current viewport is mobile
         * @param {string} dimension - 'width' or 'height'
         * @returns {string|null} Attribute value or null if not found
         */
        getWindowSizeAttribute(element, isMobile, dimension) {
            if (!element) return null;

            const mobileAttr = `data-${dimension}-mobile`;
            const desktopAttr = `data-${dimension}`;

            if (isMobile) {
                // Try mobile first, fallback to desktop
                return element.getAttribute(mobileAttr) || element.getAttribute(desktopAttr);
            } else {
                // Desktop only
                return element.getAttribute(desktopAttr);
            }
        }

        /**
         * Helper function to get window position attribute with mobile/desktop fallback
         * @param {HTMLElement} element - Element to read from
         * @param {boolean} isMobile - Whether current viewport is mobile
         * @param {string} axis - 'left' or 'top'
         * @returns {string|null} Attribute value or null if not found
         */
        getWindowPositionAttribute(element, isMobile, axis) {
            if (!element) return null;

            const mobileAttr = `data-${axis}-mobile`;
            const desktopAttr = `data-${axis}`;

            if (isMobile) {
                // Try mobile first, fallback to desktop
                return element.getAttribute(mobileAttr) || element.getAttribute(desktopAttr);
            } else {
                // Desktop only
                return element.getAttribute(desktopAttr);
            }
        }

        /**
         * Register a new window
         */
        register(windowId, element) {
            this.windows.set(windowId, {
                id: windowId,
                element,
                zIndex: this.zIndexCounter++,
                state: 'open',
                position: { x: 0, y: 0 },
                size: { width: 0, height: 0 },
                isMaximized: false,
                previousState: null
            });
        }

        /**
         * Open a window
         * @param {string} windowId - Unique identifier for the window
         * @param {Object} options - Window options
         * @param {string} options.title - Window title
         * @param {string} options.icon - Icon URL (optional)
         * @param {string} options.content - HTML content (optional)
         * @param {string} options.section - Shopify section to load (optional)
         * @param {Object} options.position - Initial position {x, y} (optional)
         * @param {Object} options.size - Initial size {width, height} (optional)
         * @param {boolean} options.isModal - Whether window is a modal (default: false)
         * @param {boolean} options.canResize - Whether window can be resized (default: true)
         * @param {boolean} options.canMaximize - Whether window can be maximized (default: true)
         * @returns {HTMLElement} The window element
         */
        async open(windowId, options = {}) {
            try {
                // Check if window already exists
                const existingWindow = this.windows.get(windowId);
                if (existingWindow && existingWindow.element) {
                    // Window already exists, bring to front
                    this.bringToFront(windowId);
                    // Restore if minimized
                    if (existingWindow.state === 'minimized') {
                        this.restore(windowId);
                    }
                    return existingWindow.element;
                }

                // Check if window is currently being opened (prevent duplicate opens)
                if (this.openingWindows.has(windowId)) {
                    console.log(`[WindowManager] Window ${windowId} is already being opened, ignoring duplicate request`);
                    // Wait a bit and check again if window was registered
                    await new Promise(resolve => setTimeout(resolve, 100));
                    const retryWindow = this.windows.get(windowId);
                    if (retryWindow && retryWindow.element) {
                        this.bringToFront(windowId);
                        if (retryWindow.state === 'minimized') {
                            this.restore(windowId);
                        }
                        return retryWindow.element;
                    }
                    // If still not registered, allow the open to proceed (shouldn't happen, but safety check)
                }

                // Mark window as being opened
                this.openingWindows.add(windowId);

                // Default options
                const {
                    title = 'Window',
                    icon = null,
                    content = '',
                    section = null,
                    position = null,
                    size = { width: '600px', height: '400px' },
                    isModal = false,
                    canResize = true,
                    canMaximize = true
                } = options;

                // Check for data attributes early - if they exist, they override the size and position
                const isMobile = this.isMobile();
                let finalSize = { ...size };
                let finalPosition = position ? { ...position } : null;

                // Get theme settings for defaults
                const themeSettings = window.smashOS?.themeSettings || {};

                // If dataAttributes are provided in options, check them first
                if (options.dataAttributes && Object.keys(options.dataAttributes).length > 0) {
                    // Read data attributes directly from the options object
                    const widthMobile = options.dataAttributes['data-width-mobile'];
                    const widthDesktop = options.dataAttributes['data-width'];
                    const heightMobile = options.dataAttributes['data-height-mobile'];
                    const heightDesktop = options.dataAttributes['data-height'];

                    // Use mobile if on mobile and mobile exists, otherwise use desktop
                    let dataWidth = isMobile ? (widthMobile || widthDesktop) : widthDesktop;
                    let dataHeight = isMobile ? (heightMobile || heightDesktop) : heightDesktop;

                    // Ensure width has units
                    if (dataWidth && !isNaN(dataWidth) && !dataWidth.includes('px') && !dataWidth.includes('%') && !dataWidth.includes('vw')) {
                        dataWidth = `${dataWidth}px`;
                    }
                    // Ensure height has units (but allow 'auto')
                    if (dataHeight && dataHeight !== 'auto' && !isNaN(dataHeight) && !dataHeight.includes('px') && !dataHeight.includes('%') && !dataHeight.includes('vh')) {
                        dataHeight = `${dataHeight}px`;
                    }

                    // Override size with data attributes if they exist (always prioritize data attributes)
                    if (dataWidth) {
                        finalSize.width = dataWidth;
                        console.log('[WindowManager] Using data-width from options:', dataWidth);
                    }
                    if (dataHeight) {
                        finalSize.height = dataHeight;
                        console.log('[WindowManager] Using data-height from options:', dataHeight);
                    }

                    // Read position attributes
                    const leftMobile = options.dataAttributes['data-left-mobile'];
                    const leftDesktop = options.dataAttributes['data-left'];
                    const topMobile = options.dataAttributes['data-top-mobile'];
                    const topDesktop = options.dataAttributes['data-top'];

                    // Use mobile if on mobile and mobile exists, otherwise use desktop
                    let dataLeft = isMobile ? (leftMobile || leftDesktop) : leftDesktop;
                    let dataTop = isMobile ? (topMobile || topDesktop) : topDesktop;

                    // Override position with data attributes if they exist (always prioritize data attributes)
                    if (dataLeft !== null && dataLeft !== undefined) {
                        finalPosition = finalPosition || {};
                        finalPosition.x = dataLeft;
                        console.log('[WindowManager] Using data-left from options:', dataLeft);
                    }
                    if (dataTop !== null && dataTop !== undefined) {
                        finalPosition = finalPosition || {};
                        finalPosition.y = dataTop;
                        console.log('[WindowManager] Using data-top from options:', dataTop);
                    }
                } else {
                    // Use theme settings defaults if no data attributes provided
                    const themeSettings = window.smashOS?.themeSettings || {};
                    const isProductWindow = options.windowType === 'product';

                    // For product windows, use product-specific settings, otherwise use general defaults
                    const settingsPrefix = isProductWindow ? 'product_window_' : 'default_window_';

                    console.log('[WindowManager] Checking theme settings for defaults:', {
                        isProductWindow: isProductWindow,
                        hasThemeSettings: !!themeSettings && Object.keys(themeSettings).length > 0,
                        settingsPrefix: settingsPrefix,
                        width: themeSettings[`${settingsPrefix}width`],
                        height: themeSettings[`${settingsPrefix}height`],
                        left: themeSettings[`${settingsPrefix}left`],
                        top: themeSettings[`${settingsPrefix}top`]
                    });

                    if (themeSettings && Object.keys(themeSettings).length > 0) {
                        // Get product-specific or general defaults
                        const defaultWidth = isMobile
                            ? (themeSettings[`${settingsPrefix}width_mobile`] || themeSettings[`${settingsPrefix}width`])
                            : themeSettings[`${settingsPrefix}width`];
                        const defaultHeight = isMobile
                            ? (themeSettings[`${settingsPrefix}height_mobile`] || themeSettings[`${settingsPrefix}height`])
                            : themeSettings[`${settingsPrefix}height`];
                        const defaultLeft = isMobile
                            ? (themeSettings[`${settingsPrefix}left_mobile`] || themeSettings[`${settingsPrefix}left`])
                            : themeSettings[`${settingsPrefix}left`];
                        const defaultTop = isMobile
                            ? (themeSettings[`${settingsPrefix}top_mobile`] || themeSettings[`${settingsPrefix}top`])
                            : themeSettings[`${settingsPrefix}top`];

                        // Apply default size if theme settings are configured and size matches hardcoded defaults
                        // This allows theme settings to override the hardcoded 900px/700px defaults used by product/collection windows
                        if (defaultWidth && (!size.width || size.width === '600px' || size.width === '900px')) {
                            finalSize.width = defaultWidth;
                            console.log('[WindowManager] Using default window width from theme settings:', defaultWidth, '(replacing', size.width, ')');
                        }
                        if (defaultHeight && (!size.height || size.height === '400px' || size.height === '700px')) {
                            finalSize.height = defaultHeight;
                            console.log('[WindowManager] Using default window height from theme settings:', defaultHeight, '(replacing', size.height, ')');
                        }

                        // Apply default position if position wasn't explicitly provided
                        if (defaultLeft || defaultTop) {
                            finalPosition = finalPosition || {};
                            if (defaultLeft) {
                                finalPosition.x = defaultLeft;
                                console.log('[WindowManager] Using default window left from theme settings:', defaultLeft);
                            }
                            if (defaultTop) {
                                finalPosition.y = defaultTop;
                                console.log('[WindowManager] Using default window top from theme settings:', defaultTop);
                            }
                        }

                        // Store theme settings as data attributes so they persist and work for responsive updates
                        // Always store ALL theme settings (both desktop and mobile) if any exist
                        const hasAnyThemeSettings = themeSettings[`${settingsPrefix}width`] || themeSettings[`${settingsPrefix}height`] ||
                            themeSettings[`${settingsPrefix}left`] || themeSettings[`${settingsPrefix}top`] ||
                            themeSettings[`${settingsPrefix}width_mobile`] || themeSettings[`${settingsPrefix}height_mobile`] ||
                            themeSettings[`${settingsPrefix}left_mobile`] || themeSettings[`${settingsPrefix}top_mobile`];

                        if (hasAnyThemeSettings) {
                            // Create dataAttributes object from theme settings to store on window element
                            if (!options.dataAttributes) {
                                options.dataAttributes = {};
                            }

                            // Store all theme settings as data attributes (both desktop and mobile)
                            // This ensures they're available for responsive updates
                            // Only store if value is not null, undefined, or empty string
                            const widthSetting = themeSettings[`${settingsPrefix}width`];
                            const heightSetting = themeSettings[`${settingsPrefix}height`];
                            const leftSetting = themeSettings[`${settingsPrefix}left`];
                            const topSetting = themeSettings[`${settingsPrefix}top`];
                            const widthMobileSetting = themeSettings[`${settingsPrefix}width_mobile`];
                            const heightMobileSetting = themeSettings[`${settingsPrefix}height_mobile`];
                            const leftMobileSetting = themeSettings[`${settingsPrefix}left_mobile`];
                            const topMobileSetting = themeSettings[`${settingsPrefix}top_mobile`];

                            if (widthSetting && widthSetting.trim() !== '') {
                                options.dataAttributes['data-width'] = widthSetting;
                            }
                            if (heightSetting && heightSetting.trim() !== '') {
                                options.dataAttributes['data-height'] = heightSetting;
                            }
                            if (leftSetting && leftSetting.trim() !== '') {
                                options.dataAttributes['data-left'] = leftSetting;
                            }
                            if (topSetting && topSetting.trim() !== '') {
                                options.dataAttributes['data-top'] = topSetting;
                            }
                            if (widthMobileSetting && widthMobileSetting.trim() !== '') {
                                options.dataAttributes['data-width-mobile'] = widthMobileSetting;
                            }
                            if (heightMobileSetting && heightMobileSetting.trim() !== '') {
                                options.dataAttributes['data-height-mobile'] = heightMobileSetting;
                            }
                            if (leftMobileSetting && leftMobileSetting.trim() !== '') {
                                options.dataAttributes['data-left-mobile'] = leftMobileSetting;
                            }
                            if (topMobileSetting && topMobileSetting.trim() !== '') {
                                options.dataAttributes['data-top-mobile'] = topMobileSetting;
                            }

                            console.log(`[WindowManager] Stored ${isProductWindow ? 'product' : 'general'} theme settings as data attributes:`, options.dataAttributes);
                        }
                    }
                }

                // Create window element with proper structure
                const windowElement = document.createElement('section');
                windowElement.className = `st-window${isModal ? ' st-window--modal' : ''}`;
                windowElement.id = `window-${windowId}`;
                windowElement.dataset.windowId = windowId;
                windowElement.setAttribute('role', isModal ? 'dialog' : 'region');
                if (isModal) {
                    windowElement.setAttribute('aria-modal', 'true');
                }
                windowElement.setAttribute('aria-labelledby', `window-title-${windowId}`);
                windowElement.setAttribute('tabindex', '-1');

                // Auto-snap fullscreen on mobile — only for commerce windows
                // (product, collection, cart). Other windows keep their configured
                // size so content designed for a floating window doesn't stretch awkwardly.
                this.applyMobileFullscreenClass(windowElement, windowId);

                // Get taskbar height and calculate available space
                const taskbar = document.getElementById('taskbar');
                const taskbarHeight = taskbar ? taskbar.offsetHeight : parseInt(getComputedStyle(document.documentElement).getPropertyValue('--st-taskbar-height')) || 32;
                const minMargin = isMobile ? 16 : 20; // Minimum spacing from taskbar
                // Check if this is a hero banner window (special top margin on mobile)
                const isHeroBanner = windowId.includes('hero-banner') || options.section === 'window-hero-banner';
                const minMarginTop = (isMobile && isHeroBanner) ? 200 : (isMobile ? 16 : 20); // 200px for hero banner on mobile, 16px for others on mobile, 20px on desktop
                const availableHeight = window.innerHeight - taskbarHeight - minMarginTop - minMargin; // Top and bottom margins

                // Set initial size (using finalSize which may have been overridden by data attributes)
                if (typeof finalSize.width === 'string') {
                    windowElement.style.width = finalSize.width;
                } else if (finalSize.width) {
                    windowElement.style.width = `${finalSize.width}px`;
                }
                if (typeof finalSize.height === 'string') {
                    // If height is 'auto', allow it but constrain after content loads
                    if (finalSize.height === 'auto') {
                        windowElement.style.height = finalSize.height;
                    } else {
                        // Parse height value and constrain to available space
                        let heightValue = finalSize.height;
                        if (typeof finalSize.height === 'number') {
                            heightValue = `${finalSize.height}px`;
                        }
                        // Extract numeric value
                        const heightNum = parseFloat(heightValue);
                        if (!isNaN(heightNum)) {
                            const constrainedHeight = Math.min(heightNum, availableHeight);
                            windowElement.style.height = `${constrainedHeight}px`;
                        } else {
                            windowElement.style.height = heightValue;
                        }
                    }
                } else if (finalSize.height) {
                    const heightNum = typeof finalSize.height === 'number' ? finalSize.height : parseFloat(finalSize.height);
                    if (!isNaN(heightNum)) {
                        const constrainedHeight = Math.min(heightNum, availableHeight);
                        windowElement.style.height = `${constrainedHeight}px`;
                    } else {
                        windowElement.style.height = `${finalSize.height}px`;
                    }
                }

                // Set initial position (cascaded if not specified)
                if (finalPosition) {
                    if (finalPosition.x === '50%' && finalPosition.y === '50%') {
                        // Center on screen
                        windowElement.style.position = 'fixed';
                        windowElement.style.left = '50%';
                        windowElement.style.top = '50%';
                        windowElement.style.transform = 'translate(-50%, -50%)';
                    } else {
                        windowElement.style.left = typeof finalPosition.x === 'string' ? finalPosition.x : `${finalPosition.x}px`;
                        windowElement.style.top = typeof finalPosition.y === 'string' ? finalPosition.y : `${finalPosition.y}px`;
                    }
                } else {
                    // Cascade window (offset from previous windows)
                    this.cascadeWindow(windowElement);
                }

                // Ensure window doesn't exceed viewport dimensions and respects taskbar spacing
                this.constrainWindowToViewport(windowElement);

                // Store initial showMenuBar value on window element to prevent loadSectionContent from overriding it
                const initialShowMenuBar = options.showMenuBar !== undefined ? options.showMenuBar : true;
                windowElement.setAttribute('data-initial-show-menu-bar', initialShowMenuBar ? 'true' : 'false');

                // Build window structure
                this.buildWindowStructure(windowElement, windowId, title, icon, content, canResize, canMaximize, {
                    windowType: options.windowType || 'default',
                    menuItems: options.menuItems,
                    statusText: options.statusText,
                    showStatusBar: options.showStatusBar !== undefined ? options.showStatusBar : true,
                    showMenuBar: initialShowMenuBar
                });

                // Store section name for responsive updates
                if (section) {
                    windowElement.setAttribute('data-section-name', section);
                }

                // Store data attributes on window element for responsive updates
                if (options.dataAttributes && Object.keys(options.dataAttributes).length > 0) {
                    console.log('[WindowManager] Storing dataAttributes on window element:', options.dataAttributes);
                    Object.keys(options.dataAttributes).forEach(key => {
                        // Only store if value is not null, undefined, or empty string
                        const value = options.dataAttributes[key];
                        if (value !== null && value !== undefined && value !== '' && (typeof value !== 'string' || value.trim() !== '')) {
                            windowElement.setAttribute(key, value);
                            console.log(`[WindowManager] Stored ${key} = ${value} on window element`);
                        } else {
                            console.log(`[WindowManager] Skipping ${key} - value is empty or invalid:`, value);
                        }
                    });
                    const storedAttrs = {
                        'data-width': windowElement.getAttribute('data-width'),
                        'data-height': windowElement.getAttribute('data-height'),
                        'data-width-mobile': windowElement.getAttribute('data-width-mobile'),
                        'data-height-mobile': windowElement.getAttribute('data-height-mobile'),
                        'data-left': windowElement.getAttribute('data-left'),
                        'data-top': windowElement.getAttribute('data-top'),
                        'data-left-mobile': windowElement.getAttribute('data-left-mobile'),
                        'data-top-mobile': windowElement.getAttribute('data-top-mobile')
                    };
                    console.log('[WindowManager] Window element attributes after storing:', storedAttrs);

                    // Verify each attribute was stored
                    Object.keys(options.dataAttributes).forEach(key => {
                        const expected = options.dataAttributes[key];
                        const actual = windowElement.getAttribute(key);
                        if (expected && expected !== actual) {
                            console.warn(`[WindowManager] Attribute ${key} was not stored correctly! Expected: ${expected}, Got: ${actual}`);
                        } else if (expected) {
                            console.log(`[WindowManager] ✓ Attribute ${key} stored correctly: ${actual}`);
                        }
                    });
                } else {
                    console.log('[WindowManager] No dataAttributes provided in options:', {
                        hasDataAttributes: !!options.dataAttributes,
                        keys: options.dataAttributes ? Object.keys(options.dataAttributes) : []
                    });
                }

                // Load content from section if provided
                if (section) {
                    await this.loadSectionContent(windowElement, section, options.sectionParams);

                    // Re-verify data attributes are still present after loadSectionContent
                    if (options.dataAttributes && Object.keys(options.dataAttributes).length > 0) {
                        console.log('[WindowManager] Verifying data attributes after loadSectionContent...');
                        let needsRestore = false;
                        Object.keys(options.dataAttributes).forEach(key => {
                            const expected = options.dataAttributes[key];
                            const actual = windowElement.getAttribute(key);
                            if (expected && expected !== actual) {
                                console.warn(`[WindowManager] Attribute ${key} was lost after loadSectionContent! Restoring...`);
                                windowElement.setAttribute(key, expected);
                                needsRestore = true;
                            }
                        });
                        if (needsRestore) {
                            console.log('[WindowManager] Restored data attributes after loadSectionContent');
                        } else {
                            console.log('[WindowManager] ✓ All data attributes still present after loadSectionContent');
                        }
                    }
                }

                // Add to desktop
                const desktop = document.getElementById('desktop');
                if (desktop) {
                    desktop.appendChild(windowElement);
                }

                // Register the window with position and size
                const rect = windowElement.getBoundingClientRect();
                this.register(windowId, windowElement);
                const windowData = this.windows.get(windowId);

                // Remove from opening set now that window is registered
                this.openingWindows.delete(windowId);

                // Final check: Ensure data attributes are still present after registration
                if (options.dataAttributes && Object.keys(options.dataAttributes).length > 0) {
                    console.log('[WindowManager] Final check after registration - verifying data attributes...');
                    Object.keys(options.dataAttributes).forEach(key => {
                        const expected = options.dataAttributes[key];
                        const actual = windowElement.getAttribute(key);
                        if (expected && expected !== actual) {
                            console.warn(`[WindowManager] CRITICAL: Attribute ${key} missing after registration! Restoring immediately...`);
                            windowElement.setAttribute(key, expected);
                        }
                    });
                    console.log('[WindowManager] Data attributes after registration:', {
                        'data-width': windowElement.getAttribute('data-width'),
                        'data-height': windowElement.getAttribute('data-height'),
                        'data-left': windowElement.getAttribute('data-left'),
                        'data-top': windowElement.getAttribute('data-top')
                    });
                }

                // Apply custom zIndex if provided
                if (options.zIndex !== undefined) {
                    windowData.zIndex = options.zIndex;
                    windowElement.style.zIndex = options.zIndex;
                }

                // Store position - handle centered windows specially
                if (finalPosition && finalPosition.x === '50%' && finalPosition.y === '50%') {
                    windowData.position = {
                        x: '50%',
                        y: '50%',
                        centered: true
                    };
                } else {
                    windowData.position = {
                        x: parseInt(windowElement.style.left) || rect.left,
                        y: parseInt(windowElement.style.top) || rect.top
                    };
                }
                windowData.size = {
                    width: rect.width,
                    height: rect.height
                };

                // Initialize window shell if available
                if (window.WindowShell) {
                    // Wait a tick for DOM to be ready
                    setTimeout(() => {
                        if (!windowElement.windowShell) {
                            windowElement.windowShell = new window.WindowShell(windowElement);
                        }
                    }, 0);
                }

                // Add to taskbar
                if (window.smashOS && window.smashOS.desktopManager) {
                    window.smashOS.desktopManager.addWindowToTaskbar(windowId, title, icon);
                }

                // Set up event listeners for cleanup
                this.setupWindowListeners(windowElement, windowId);

                // Bring to front
                this.bringToFront(windowId);

                // Trigger window open event
                windowElement.dispatchEvent(new CustomEvent('window:open', {
                    detail: { windowId, options }
                }));

                return windowElement;
            } catch (error) {
                // If error occurs, remove from opening set
                this.openingWindows.delete(windowId);
                console.error(`[WindowManager] Error opening window ${windowId}:`, error);
                throw error;
            }
        }

        /**
         * Open media player and start playback automatically
         */
        async playDopeBeats() {
            console.log('[WindowManager] playDopeBeats called');

            // Find the desktop icon for the media player to get its section ID
            const mediaPlayerIcon = document.querySelector('[data-action-type="media-player"]');
            if (!mediaPlayerIcon) {
                console.warn('[WindowManager] Media player desktop icon not found');
                return;
            }

            const sectionId = mediaPlayerIcon.dataset.sectionId;
            if (!sectionId) {
                console.warn('[WindowManager] Media player section ID not found on icon');
                return;
            }

            // Open the app window
            const windowElement = await this.openAppWindow('media-player-app', sectionId);
            if (!windowElement) return;

            // Wait for initialization and trigger play
            setTimeout(() => {
                const playBtn = windowElement.querySelector('.st-media-player__btn--play') ||
                    windowElement.querySelector(`#play-btn-${sectionId}`);

                if (playBtn) {
                    console.log('[WindowManager] Triggering play for Dope Beats');
                    playBtn.click();
                } else {
                    // One retry for slower loads
                    setTimeout(() => {
                        const retryBtn = windowElement.querySelector('.st-media-player__btn--play') ||
                            windowElement.querySelector(`#play-btn-${sectionId}`);
                        if (retryBtn) retryBtn.click();
                    }, 500);
                }
            }, 1000);

            return windowElement;
        }

        /**
         * Hide the music tooltip in the taskbar
         */
        hideMusicTooltip() {
            const tooltip = document.querySelector('.st-taskbar__music-tooltip');
            if (tooltip) {
                tooltip.classList.add('is-hidden');
            }
        }

        /**
         * Get menu items for a window type
         * Returns array of objects with {label, url}
         */
        getMenuItems(windowType) {
            // Get theme settings from global theme settings object or DOM
            const themeSettings = window.smashOS?.themeSettings || {};

            console.log('[WindowManager] getMenuItems called for windowType:', windowType);
            console.log('[WindowManager] window.smashOS available:', typeof window.smashOS !== 'undefined');
            console.log('[WindowManager] themeSettings available:', typeof themeSettings !== 'undefined' && Object.keys(themeSettings).length > 0);
            console.log('[WindowManager] themeSettings keys:', Object.keys(themeSettings));
            console.log('[WindowManager] menu_collection_item_1_label:', themeSettings.menu_collection_item_1_label);
            console.log('[WindowManager] menu_collection_item_1_url:', themeSettings.menu_collection_item_1_url);

            // Get menu items from settings
            if (windowType === 'collection') {
                const menuItems = [];
                // Check for menu items 1-5
                for (let i = 1; i <= 5; i++) {
                    const labelKey = `menu_collection_item_${i}_label`;
                    const urlKey = `menu_collection_item_${i}_url`;
                    const label = themeSettings[labelKey];
                    const url = themeSettings[urlKey];

                    // Only add if label exists and is not empty
                    if (label && label.trim()) {
                        menuItems.push({
                            label: label,
                            url: url || ''
                        });
                    }
                }

                // Fallback to defaults if no items found
                if (menuItems.length === 0) {
                    return [
                        { label: 'View All', url: '/collections/all' },
                        { label: 'Share', url: '' },
                        { label: 'Help', url: '' }
                    ];
                }
                return menuItems;
            } else if (windowType === 'product') {
                // For now, use same as collection (can be extended later)
                return this.getMenuItems('collection');
            } else {
                // Default menu items
                return [
                    { label: 'View', url: '' },
                    { label: 'Help', url: '' }
                ];
            }
        }

        /**
         * Build window HTML structure
         */
        buildWindowStructure(element, windowId, title, icon, content, canResize, canMaximize, options = {}) {
            // Title bar
            const titlebar = document.createElement('header');
            titlebar.className = 'st-window__titlebar st-titlebar st-titlebar--inactive';
            titlebar.id = `window-titlebar-${windowId}`;
            titlebar.setAttribute('data-draggable', 'true');

            // Icon
            if (icon) {
                const iconImg = document.createElement('img');
                iconImg.src = icon;
                iconImg.alt = '';
                iconImg.className = 'st-titlebar__icon';
                iconImg.width = 24;
                iconImg.height = 24;
                iconImg.loading = 'lazy';
                titlebar.appendChild(iconImg);
            } else {
                const iconSpan = document.createElement('span');
                iconSpan.className = 'st-titlebar__icon';
                iconSpan.setAttribute('aria-hidden', 'true');
                iconSpan.textContent = '📄';
                titlebar.appendChild(iconSpan);
            }

            // Title
            const titleEl = document.createElement('h2');
            titleEl.className = 'st-titlebar__title';
            titleEl.id = `window-title-${windowId}`;
            titleEl.textContent = title;
            titlebar.appendChild(titleEl);

            // Controls
            const controls = document.createElement('div');
            controls.className = 'st-titlebar__controls';

            // Minimize button
            const minBtn = document.createElement('button');
            minBtn.className = 'st-titlebar__btn st-titlebar__btn--minimize';
            minBtn.setAttribute('aria-label', `Minimize ${title}`);
            minBtn.setAttribute('data-action', 'minimize');
            minBtn.setAttribute('data-window-id', windowId);
            const minImg = document.createElement('img');
            minImg.src = (window.smashOSAssets && window.smashOSAssets.minimizeIcon) || window.location.origin + '/assets/minimize.png';
            minImg.alt = '';
            minImg.className = 'st-titlebar__btn-icon';
            minImg.width = 20;
            minImg.height = 20;
            minImg.setAttribute('aria-hidden', 'true');
            minBtn.appendChild(minImg);
            controls.appendChild(minBtn);

            // Maximize button
            if (canMaximize) {
                const maxBtn = document.createElement('button');
                maxBtn.className = 'st-titlebar__btn st-titlebar__btn--maximize';
                maxBtn.setAttribute('aria-label', `Maximize ${title}`);
                maxBtn.setAttribute('data-action', 'maximize');
                maxBtn.setAttribute('data-window-id', windowId);
                const maxImg = document.createElement('img');
                maxImg.src = (window.smashOSAssets && window.smashOSAssets.maximizeIcon) || window.location.origin + '/assets/maximize.png';
                maxImg.alt = '';
                maxImg.className = 'st-titlebar__btn-icon';
                maxImg.width = 20;
                maxImg.height = 20;
                maxImg.setAttribute('aria-hidden', 'true');
                maxBtn.appendChild(maxImg);
                controls.appendChild(maxBtn);
            }

            // Close button
            const closeBtn = document.createElement('button');
            closeBtn.className = 'st-titlebar__btn st-titlebar__btn--close';
            closeBtn.setAttribute('aria-label', `Close ${title}`);
            closeBtn.setAttribute('data-action', 'close');
            closeBtn.setAttribute('data-window-id', windowId);
            const closeImg = document.createElement('img');
            closeImg.src = (window.smashOSAssets && window.smashOSAssets.closeIcon) || window.location.origin + '/assets/close.png';
            closeImg.alt = '';
            closeImg.className = 'st-titlebar__btn-icon';
            closeImg.width = 20;
            closeImg.height = 20;
            closeImg.setAttribute('aria-hidden', 'true');
            closeBtn.appendChild(closeImg);
            controls.appendChild(closeBtn);

            titlebar.appendChild(controls);
            element.appendChild(titlebar);

            // Menu bar (inline menu below titlebar) - only if showMenuBar is true
            if (options.showMenuBar !== false) {
                const menuBar = document.createElement('nav');
                menuBar.className = 'st-window__menubar st-menubar';
                menuBar.setAttribute('role', 'menubar');
                menuBar.setAttribute('aria-label', 'Window menu');

                // Get menu items based on window type
                const windowType = options.windowType || 'default';
                const menuItems = options.menuItems || this.getMenuItems(windowType);

                menuItems.forEach((item, index) => {
                    const menuItem = document.createElement('div');
                    menuItem.className = 'st-menubar__item';
                    menuItem.setAttribute('role', 'menuitem');
                    menuItem.setAttribute('tabindex', '0');

                    // Handle both string (legacy) and object (new) formats
                    if (typeof item === 'string') {
                        menuItem.textContent = item;
                        menuItem.setAttribute('data-menu-item', item.toLowerCase().replace(/\s+/g, '-'));
                    } else {
                        // Object format with label, url, and optional action
                        menuItem.textContent = item.label;
                        menuItem.setAttribute('data-menu-item', item.label.toLowerCase().replace(/\s+/g, '-'));
                        if (item.url && item.url.trim()) {
                            menuItem.setAttribute('data-menu-url', item.url);
                            menuItem.style.cursor = 'pointer';
                        }
                        if (item.action && item.action.trim()) {
                            menuItem.setAttribute('data-menu-action', item.action);
                            menuItem.setAttribute('data-action', 'open-window');
                            menuItem.setAttribute('data-action-type', item.action);
                            menuItem.style.cursor = 'pointer';
                        }
                    }

                    menuBar.appendChild(menuItem);
                });

                // Add click handlers for menu items with URLs or actions
                menuBar.addEventListener('click', (e) => {
                    const menuItem = e.target.closest('.st-menubar__item');
                    if (menuItem) {
                        let handled = false;

                        // Handle action first (for window opening, etc.)
                        if (menuItem.dataset.action === 'open-window' && menuItem.dataset.actionType) {
                            // Use the same handler as CTA buttons
                            e.stopPropagation();
                            const actionType = menuItem.dataset.actionType;
                            const actionValue = menuItem.dataset.actionValue || '';
                            const sectionId = menuItem.dataset.sectionId || '';

                            // Handle different action types
                            if (actionType.includes('-app')) {
                                // Find desktop icon with matching section ID or action type
                                let targetIcon = null;
                                if (sectionId) {
                                    targetIcon = document.querySelector(`[data-section-id="${sectionId}"]`);
                                }
                                if (!targetIcon && actionValue) {
                                    targetIcon = document.querySelector(`[data-action-type="${actionType}"][data-action-value="${actionValue}"]`);
                                }
                                if (!targetIcon) {
                                    targetIcon = document.querySelector(`[data-action-type="${actionType}"]`);
                                }

                                if (targetIcon) {
                                    targetIcon.click();
                                    handled = true;
                                }
                            } else if (actionType === 'collection' || actionType === 'product') {
                                // Open collection/product window
                                if (actionValue) {
                                    this.openCollectionWindow(actionValue);
                                    handled = true;
                                }
                            } else if (actionType === 'page') {
                                // Open page window
                                if (actionValue) {
                                    this.openPage(actionValue);
                                    handled = true;
                                }
                            }
                        } else if (menuItem.dataset.menuUrl) {
                            const url = menuItem.dataset.menuUrl;
                            if (url && url.trim()) {
                                e.stopPropagation();
                                window.open(url, '_blank', 'noopener,noreferrer');
                                handled = true;
                            }
                        }

                        // If no action was handled, show error message
                        if (!handled) {
                            e.stopPropagation();
                            this.showMenuNotImplementedError(menuItem.textContent);
                        }
                    }
                });

                element.appendChild(menuBar);
            }

            // Address bar (for product windows) - only if showAddressBar is true
            if (options.showAddressBar === true) {
                const addressBar = document.createElement('div');
                addressBar.className = 'st-window__addressbar st-addressbar';
                addressBar.id = `window-addressbar-${windowId}`;

                const addressSelect = document.createElement('select');
                addressSelect.className = 'st-addressbar__select st-input';
                addressSelect.setAttribute('aria-label', 'Address');
                addressBar.appendChild(addressSelect);

                element.appendChild(addressBar);
            }

            // Window body
            const body = document.createElement('div');
            body.className = 'st-window__body st-scrollbar';
            body.id = `window-body-${windowId}`;
            body.setAttribute('role', 'region');
            body.setAttribute('aria-label', 'Window content');
            if (content) {
                body.innerHTML = content;

                // Execute any scripts in the inserted content
                // Scripts inserted via innerHTML don't execute automatically
                setTimeout(() => {
                    const scripts = body.querySelectorAll('script');
                    scripts.forEach((oldScript) => {
                        try {
                            const newScript = document.createElement('script');
                            Array.from(oldScript.attributes).forEach(attr => {
                                newScript.setAttribute(attr.name, attr.value);
                            });
                            newScript.textContent = oldScript.textContent;
                            // Insert before the old script, then remove old one
                            oldScript.parentNode.insertBefore(newScript, oldScript);
                            oldScript.remove();
                        } catch (error) {
                            console.error('[WindowManager] Error executing script:', error, oldScript);
                        }
                    });
                }, 0);
            }
            element.appendChild(body);

            // Initialize scrollbar states for this window body
            setTimeout(() => {
                this.updateScrollbarStates(body);
                body.addEventListener('scroll', () => {
                    this.updateScrollbarStates(body);
                });
                const resizeObserver = new ResizeObserver(() => {
                    this.updateScrollbarStates(body);
                });
                resizeObserver.observe(body);
            }, 0);

            // Status bar (at bottom of window) - only if showStatusBar is true
            if (options.showStatusBar !== false) {
                const statusBar = document.createElement('div');
                statusBar.className = 'st-window__statusbar st-statusbar';
                statusBar.id = `window-statusbar-${windowId}`;

                const statusText = document.createElement('div');
                statusText.className = 'st-statusbar__text';
                statusText.textContent = options.statusText || '';
                statusBar.appendChild(statusText);

                // Resize handle (window enlarger) on the right
                if (canResize) {
                    const resizeHandle = document.createElement('div');
                    resizeHandle.className = 'st-statusbar__resize-handle st-window__resize-handle';
                    resizeHandle.setAttribute('data-resize', 'se'); // Southeast corner resize
                    resizeHandle.setAttribute('aria-label', 'Resize window');
                    resizeHandle.setAttribute('title', 'Drag to resize');

                    // Set background image for dragger
                    const draggerUrl = getComputedStyle(document.documentElement).getPropertyValue('--dragger-image').trim();
                    if (draggerUrl && draggerUrl !== 'none' && draggerUrl !== '') {
                        resizeHandle.style.backgroundImage = draggerUrl;
                    } else if (window.smashOSAssets && window.smashOSAssets.draggerIcon) {
                        // Fallback: use smashOSAssets
                        resizeHandle.style.backgroundImage = `url('${window.smashOSAssets.draggerIcon}')`;
                    } else {
                        // Final fallback: construct URL directly
                        resizeHandle.style.backgroundImage = `url('${window.location.origin}/assets/dragger.png')`;
                    }
                    resizeHandle.style.backgroundSize = 'contain';
                    resizeHandle.style.backgroundRepeat = 'no-repeat';
                    resizeHandle.style.backgroundPosition = 'center';

                    statusBar.appendChild(resizeHandle);
                }

                element.appendChild(statusBar);
            }

            // Resize handles (edges/corners)
            if (canResize) {
                const directions = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
                directions.forEach(dir => {
                    const handle = document.createElement('div');
                    handle.className = `st-window__resize-handle st-window__resize-handle--${dir}`;
                    handle.setAttribute('data-resize', dir);
                    element.appendChild(handle);
                });
            }
        }

        /**
         * Load content from Shopify section
         * Uses Shopify's section rendering API: /?section_id=section-name
         */
        async loadSectionContent(windowElement, sectionName, params = {}) {
            const body = windowElement.querySelector('.st-window__body');
            if (!body) return;

            try {
                // Use Shopify's section rendering API
                // Format: /?section_id=section-name&param=value
                let sectionUrl = `/?section_id=${sectionName}`;

                // Add query parameters if provided
                if (params && Object.keys(params).length > 0) {
                    const queryParams = new URLSearchParams();
                    Object.keys(params).forEach(key => {
                        queryParams.append(key, params[key]);
                    });
                    sectionUrl += '&' + queryParams.toString();
                    console.log('WindowManager: Loading section with URL:', sectionUrl);
                }
                const response = await fetch(sectionUrl, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'text/html'
                    }
                });

                if (response.ok) {
                    const html = await response.text();
                    // Parse the HTML response
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');

                    // Find the section content
                    // Shopify wraps sections in a div with id="shopify-section-{section-name}"
                    const sectionId = `shopify-section-${sectionName}`;
                    let sectionContent = doc.getElementById(sectionId);

                    if (!sectionContent) {
                        // Try alternative selector
                        sectionContent = doc.querySelector(`[data-section-type="${sectionName}"]`) ||
                            doc.querySelector(`[id*="${sectionName}"]`) ||
                            doc.body;
                    }

                    if (sectionContent) {
                        // Extract inner HTML
                        // If section includes window-shell, extract just the body content
                        const windowShell = sectionContent.querySelector('.st-window__body');
                        if (windowShell) {
                            body.innerHTML = windowShell.innerHTML;
                        } else {
                            // Use the section content directly
                            body.innerHTML = sectionContent.innerHTML;
                        }

                        // Execute any scripts in the inserted content (scripts don't run via innerHTML)
                        // Use setTimeout to ensure DOM is ready
                        setTimeout(() => {
                            const scripts = body.querySelectorAll('script');
                            scripts.forEach((oldScript) => {
                                try {
                                    const newScript = document.createElement('script');
                                    Array.from(oldScript.attributes).forEach(attr => {
                                        newScript.setAttribute(attr.name, attr.value);
                                    });
                                    newScript.textContent = oldScript.textContent;
                                    // Append to body instead of replacing to ensure execution
                                    document.body.appendChild(newScript);
                                    // Remove old script
                                    oldScript.remove();
                                } catch (error) {
                                    console.error('[WindowManager] Error executing script:', error);
                                    // Try eval as fallback (less secure but works)
                                    try {
                                        eval(oldScript.textContent);
                                    } catch (evalError) {
                                        console.error('[WindowManager] Error evaluating script:', evalError);
                                    }
                                }
                            });
                        }, 0);

                        // Read data attributes from section content and apply to window
                        // Only update title/status if they weren't explicitly set during window creation
                        const contentElement = body.querySelector('[data-window-title]') || body.firstElementChild;
                        if (contentElement) {
                            // Update window title if specified in section AND title wasn't already set from options
                            // Check if the current title matches the default (meaning it wasn't set from options)
                            const currentTitleEl = windowElement.querySelector('.st-titlebar__title');
                            const currentTitle = currentTitleEl ? currentTitleEl.textContent : '';
                            // Only update if the title looks like a default (starts with "Page:") or if contentElement has a title
                            if (contentElement.dataset.windowTitle && (!currentTitle || currentTitle.startsWith('Page:'))) {
                                const titleEl = windowElement.querySelector('.st-titlebar__title');
                                if (titleEl) {
                                    titleEl.textContent = contentElement.dataset.windowTitle;
                                }
                            }

                            // Update status bar text if specified
                            if (contentElement.dataset.statusText) {
                                const statusText = windowElement.querySelector('.st-statusbar__text');
                                if (statusText) {
                                    statusText.textContent = contentElement.dataset.statusText;
                                }
                            }

                            // Update menu bar visibility if specified
                            // Only update if window wasn't created with an explicit showMenuBar value
                            // (respect the initial showMenuBar setting from window creation)
                            const initialShowMenuBar = windowElement.getAttribute('data-initial-show-menu-bar');
                            if (contentElement.dataset.showMenuBar !== undefined && initialShowMenuBar === null) {
                                const menuBar = windowElement.querySelector('.st-window__menubar');
                                const showMenuBar = contentElement.dataset.showMenuBar === 'true';
                                console.log('[WindowManager] Setting menu bar visibility from content:', {
                                    showMenuBar,
                                    rawValue: contentElement.dataset.showMenuBar,
                                    menuBarExists: !!menuBar
                                });
                                if (menuBar) {
                                    menuBar.style.display = showMenuBar ? '' : 'none';
                                    console.log('[WindowManager] Menu bar display set to:', menuBar.style.display || 'default');
                                } else if (showMenuBar) {
                                    // Menu bar doesn't exist but should be shown - need to create it
                                    console.warn('[WindowManager] Menu bar should be shown but doesn\'t exist, creating it');
                                    // Create menu bar if it doesn't exist
                                    const titlebar = windowElement.querySelector('.st-window__titlebar');
                                    if (titlebar) {
                                        const newMenuBar = document.createElement('nav');
                                        newMenuBar.className = 'st-window__menubar st-menubar';
                                        newMenuBar.setAttribute('role', 'menubar');
                                        newMenuBar.setAttribute('aria-label', 'Window menu');

                                        // Add default menu items for richtext windows
                                        const menuItems = [
                                            { label: 'Learn More', url: '' },
                                            { label: 'Share', url: '' }
                                        ];

                                        menuItems.forEach(item => {
                                            const menuItem = document.createElement('div');
                                            menuItem.className = 'st-menubar__item';
                                            menuItem.setAttribute('role', 'menuitem');
                                            menuItem.setAttribute('tabindex', '0');
                                            menuItem.textContent = item.label;
                                            if (item.url) {
                                                menuItem.setAttribute('data-menu-url', item.url);
                                                menuItem.style.cursor = 'pointer';
                                            }
                                            newMenuBar.appendChild(menuItem);
                                        });

                                        titlebar.insertAdjacentElement('afterend', newMenuBar);
                                        console.log('[WindowManager] Menu bar created and inserted');
                                    }
                                }
                            }
                        }

                        // Special handling for product window: load product data after section loads
                        if (sectionName === 'window-product' && params.product) {
                            const productHandle = params.product;
                            setTimeout(() => {
                                this.setupProductWindowHandlers(windowElement, productHandle);
                            }, 100);
                        }

                        // Special handling for cart window: load cart data after section loads
                        if (sectionName === 'window-cart') {
                            setTimeout(async () => {
                                await this.loadCartData(windowElement);
                            }, 100);
                        }

                        // Special handling for hero banner: inject image and apply settings if passed via params
                        if (sectionName === 'window-hero-banner') {
                            const heroDiv = body.querySelector('.st-hero-banner__hero');
                            const overlayDiv = body.querySelector('.st-hero-banner__overlay');

                            // Setup responsive text sizing based on window size
                            this.setupHeroBannerResponsiveText(windowElement);

                            // Inject slogan if passed via params (Shopify section rendering API doesn't reliably pass query params to Liquid)
                            if (params && params.slogan !== undefined && params.slogan !== null) {
                                let sloganEl = overlayDiv?.querySelector('.st-hero-banner__slogan');
                                if (params.slogan && params.slogan.trim() !== '') {
                                    // If slogan element exists, update it; otherwise create it
                                    if (sloganEl) {
                                        sloganEl.textContent = params.slogan;
                                    } else if (overlayDiv) {
                                        // Create slogan element if it doesn't exist
                                        sloganEl = document.createElement('h2');
                                        sloganEl.className = 'st-hero-banner__slogan';
                                        sloganEl.textContent = params.slogan;
                                        // Insert before CTA button if it exists, otherwise at the beginning
                                        const ctaButton = overlayDiv.querySelector('.st-hero-banner__cta');
                                        if (ctaButton) {
                                            overlayDiv.insertBefore(sloganEl, ctaButton);
                                        } else {
                                            overlayDiv.insertBefore(sloganEl, overlayDiv.firstChild);
                                        }
                                    }
                                    console.log('[WindowManager] Injected slogan from params:', params.slogan);
                                } else {
                                    // Empty slogan - remove the element if it exists
                                    if (sloganEl) {
                                        sloganEl.remove();
                                        console.log('[WindowManager] Removed slogan element (empty slogan provided)');
                                    }
                                }
                            }

                            // Set data attributes for desktop and mobile images
                            // params.hero_image = desktop image (from data-hero-image)
                            // params.hero_image_mobile = mobile image (from data-hero-image-mobile)
                            if (params && heroDiv) {
                                if (params.hero_image) {
                                    heroDiv.setAttribute('data-hero-image-desktop', params.hero_image);
                                    console.log('[WindowManager] Set data-hero-image-desktop to:', params.hero_image);
                                }
                                // Only set mobile attribute if mobile image is explicitly provided
                                if (params.hero_image_mobile) {
                                    heroDiv.setAttribute('data-hero-image-mobile', params.hero_image_mobile);
                                    console.log('[WindowManager] Set data-hero-image-mobile to:', params.hero_image_mobile);
                                }

                                // Apply initial background image based on current viewport
                                // Default to desktop image, only use mobile if on mobile viewport
                                const isMobile = this.isMobile();
                                let imageUrl = params.hero_image; // Default to desktop

                                // Only use mobile if we're on mobile AND mobile image exists
                                if (isMobile && params.hero_image_mobile) {
                                    imageUrl = params.hero_image_mobile;
                                }

                                // Fallback to mobile if desktop not available
                                if (!imageUrl && params.hero_image_mobile) {
                                    imageUrl = params.hero_image_mobile;
                                }

                                if (imageUrl) {
                                    heroDiv.style.backgroundImage = `url('${imageUrl}')`;
                                    heroDiv.style.backgroundSize = 'cover';
                                    heroDiv.style.backgroundPosition = 'center';
                                    heroDiv.style.backgroundRepeat = 'no-repeat';
                                }
                            }

                            // Apply text position, alignment, and opacity if passed via params
                            if (params && overlayDiv) {
                                // Remove existing position classes
                                overlayDiv.className = overlayDiv.className.replace(/st-hero-banner__overlay--[a-z-]+/g, '').trim();

                                // Add new position and alignment classes
                                const textPosition = params.text_position || 'center';
                                const textAlignment = params.text_alignment || 'center';
                                overlayDiv.classList.add(`st-hero-banner__overlay--${textPosition}`);
                                overlayDiv.classList.add(`st-hero-banner__overlay--align-${textAlignment}`);

                                // Apply opacity
                                const opacity = params.overlay_opacity || 40;
                                const opacityDecimal = parseFloat(opacity) / 100;
                                overlayDiv.style.background = `rgba(0, 0, 0, ${opacityDecimal})`;

                                console.log('[WindowManager] Applied hero banner settings:', {
                                    textPosition,
                                    textAlignment,
                                    opacity: opacityDecimal
                                });
                            }

                            // Setup CTA button click handler to open windows using event delegation
                            // Also inject CTA values from params if they exist (since query params might not work in section rendering API)
                            const ctaButton = body.querySelector('.st-hero-banner__cta[data-action="open-window"]');
                            if (ctaButton && params) {
                                // Always inject values from params to ensure they're set correctly
                                if (params.cta_action_type) {
                                    ctaButton.setAttribute('data-action-type', params.cta_action_type);
                                    console.log('[WindowManager] Injected CTA action type from params:', params.cta_action_type);
                                }
                                if (params.cta_action_value) {
                                    ctaButton.setAttribute('data-action-value', params.cta_action_value);
                                    console.log('[WindowManager] Injected CTA action value from params:', params.cta_action_value);
                                }
                                if (params.cta_section_id) {
                                    ctaButton.setAttribute('data-section-id', params.cta_section_id);
                                    console.log('[WindowManager] Injected CTA section ID from params:', params.cta_section_id);
                                }
                            }

                            // Log button state after injection
                            if (ctaButton) {
                                console.log('[WindowManager] CTA button final state:', {
                                    'data-action-type': ctaButton.getAttribute('data-action-type'),
                                    'data-action-value': ctaButton.getAttribute('data-action-value'),
                                    'data-section-id': ctaButton.getAttribute('data-section-id')
                                });
                            }

                            // Use event delegation on the window body to handle dynamically added buttons
                            const handleCTAClick = async (e) => {
                                const button = e.target.closest('.st-hero-banner__cta[data-action="open-window"]');
                                if (!button) return;

                                e.preventDefault();
                                e.stopPropagation();

                                console.log('[WindowManager] CTA button clicked - event fired!');

                                // Read values dynamically from button at click time
                                const actionType = button.dataset.actionType || button.getAttribute('data-action-type');
                                const actionValue = button.dataset.actionValue || button.getAttribute('data-action-value');
                                const sectionId = button.dataset.sectionId || button.getAttribute('data-section-id');

                                console.log('[WindowManager] CTA button clicked:', {
                                    actionType,
                                    actionValue,
                                    sectionId,
                                    button: button,
                                    rawAttributes: {
                                        'data-action-type': button.getAttribute('data-action-type'),
                                        'data-action-value': button.getAttribute('data-action-value'),
                                        'data-section-id': button.getAttribute('data-section-id')
                                    }
                                });

                                if (!actionType) {
                                    console.warn('[WindowManager] CTA button has no action type');
                                    return;
                                }

                                try {
                                    // Handle different action types similar to desktop icon clicks
                                    if (actionType === 'collection-app') {
                                        // For collection-app, prioritize section ID, then try to find desktop icon, then fall back to openCollection
                                        if (sectionId && sectionId.trim() !== '') {
                                            console.log('[WindowManager] Opening collection app with section ID:', sectionId);
                                            await this.openAppWindow('collection-app', sectionId);
                                        } else if (actionValue && actionValue.trim() !== '') {
                                            // Try to find desktop icon with this collection handle to get section ID
                                            // First try collection-app icons, then regular collection icons
                                            const desktopIcon = document.querySelector(`[data-action-type="collection-app"][data-action-value="${actionValue}"], [data-action-type="collection"][data-action-value="${actionValue}"]`);
                                            if (desktopIcon && desktopIcon.dataset.sectionId) {
                                                console.log('[WindowManager] Found desktop icon, opening collection app with section ID:', desktopIcon.dataset.sectionId);
                                                await this.openAppWindow('collection-app', desktopIcon.dataset.sectionId);
                                            } else {
                                                console.log('[WindowManager] No desktop icon found, opening collection with handle:', actionValue);
                                                const result = await this.openCollection(actionValue);
                                                console.log('[WindowManager] openCollection result:', result);
                                            }
                                        } else {
                                            console.warn('[WindowManager] Collection-app action requires either section ID or action value');
                                        }
                                    } else if (actionType === 'collection') {
                                        if (sectionId && sectionId.trim() !== '') {
                                            console.log('[WindowManager] Opening collection app with section ID:', sectionId);
                                            await this.openAppWindow('collection-app', sectionId);
                                        } else if (actionValue && actionValue.trim() !== '') {
                                            console.log('[WindowManager] Opening collection with handle:', actionValue);
                                            const result = await this.openCollection(actionValue);
                                            console.log('[WindowManager] openCollection result:', result);
                                        } else {
                                            console.warn('[WindowManager] Collection action requires either section ID or action value');
                                        }
                                    } else if (actionType === 'product') {
                                        if (sectionId && sectionId.trim() !== '') {
                                            await this.openAppWindow('product-app', sectionId);
                                        } else if (actionValue && actionValue.trim() !== '') {
                                            await this.openProduct(actionValue);
                                        } else {
                                            console.warn('[WindowManager] Product action requires either section ID or action value');
                                        }
                                    } else if (actionType === 'page') {
                                        if (actionValue === 'hero-banner' || actionValue === 'home') {
                                            await this.openHeroBanner();
                                        } else if (actionValue && actionValue.trim() !== '') {
                                            await this.openPage(actionValue);
                                        } else {
                                            console.warn('[WindowManager] Page action requires action value');
                                        }
                                    } else if (actionType === 'hero-banner-app' || actionType === 'product-app') {
                                        if (sectionId && sectionId.trim() !== '') {
                                            await this.openAppWindow(actionType, sectionId);
                                        } else {
                                            console.warn('[WindowManager] App action requires section ID');
                                        }
                                    }
                                } catch (error) {
                                    console.error('[WindowManager] Error opening window from CTA button:', error);
                                }
                            };

                            // Attach event listener to body using event delegation
                            if (body) {
                                body.addEventListener('click', handleCTAClick);
                                console.log('[WindowManager] CTA button handler attached via event delegation');

                                // Store handler reference for cleanup if needed
                                if (!body._ctaClickHandler) {
                                    body._ctaClickHandler = handleCTAClick;
                                }
                            }
                        }

                        // Special handling for richtext window: inject content and CTA values
                        if (sectionName === 'window-richtext') {
                            const richtextWindow = body.querySelector('.st-richtext-window');

                            console.log('[WindowManager] Processing richtext window:', {
                                richtextWindowExists: !!richtextWindow,
                                params: params ? Object.keys(params) : null,
                                hasHeading: params ? !!params.heading : false,
                                hasContent: params ? !!params.content : false,
                                contentLength: params && params.content ? params.content.length : 0,
                                bodyHTML: body.innerHTML.substring(0, 300)
                            });

                            if (!richtextWindow) {
                                console.warn('[WindowManager] Richtext window element not found in body');
                                return;
                            }

                            // Inject top image if passed via params
                            if (params && params.top_image) {
                                console.log('[WindowManager] Injecting top image:', params.top_image);
                                let topImageDiv = richtextWindow.querySelector('.st-richtext-window__top-image');
                                if (!topImageDiv) {
                                    topImageDiv = document.createElement('div');
                                    topImageDiv.className = 'st-richtext-window__top-image';
                                    // Insert at the beginning
                                    if (richtextWindow.firstChild) {
                                        richtextWindow.insertBefore(topImageDiv, richtextWindow.firstChild);
                                    } else {
                                        richtextWindow.appendChild(topImageDiv);
                                    }
                                }
                                topImageDiv.innerHTML = `<img src="${this.escapeHtml(params.top_image)}" alt="${this.escapeHtml(params.top_image_alt || 'Top image')}" loading="lazy" width="800" height="auto">`;
                            }

                            // Always inject heading if passed via params (overwrite any existing)
                            if (params && params.heading) {
                                console.log('[WindowManager] Injecting heading:', params.heading);
                                let headingEl = richtextWindow.querySelector('.st-richtext-window__heading');
                                if (headingEl) {
                                    headingEl.textContent = params.heading;
                                } else {
                                    headingEl = document.createElement('h2');
                                    headingEl.className = 'st-richtext-window__heading st-richtext-window__heading--wordart';
                                    headingEl.textContent = params.heading;
                                    // Insert after top image or at the beginning
                                    const topImage = richtextWindow.querySelector('.st-richtext-window__top-image');
                                    if (topImage && topImage.nextSibling) {
                                        richtextWindow.insertBefore(headingEl, topImage.nextSibling);
                                    } else if (topImage) {
                                        richtextWindow.insertBefore(headingEl, topImage.nextSibling);
                                    } else if (richtextWindow.firstChild) {
                                        richtextWindow.insertBefore(headingEl, richtextWindow.firstChild);
                                    } else {
                                        richtextWindow.appendChild(headingEl);
                                    }
                                }
                            }

                            // Always inject content HTML if passed via params (overwrite any existing)
                            if (params && params.content) {
                                console.log('[WindowManager] Injecting content HTML, length:', params.content.length);
                                let contentEl = richtextWindow.querySelector('.st-richtext-window__content');
                                if (contentEl) {
                                    contentEl.innerHTML = params.content;
                                    console.log('[WindowManager] Content injected into existing element, new innerHTML length:', contentEl.innerHTML.length);
                                } else {
                                    console.log('[WindowManager] Creating new content element');
                                    contentEl = document.createElement('div');
                                    contentEl.className = 'st-richtext-window__content';
                                    contentEl.innerHTML = params.content;
                                    // Insert after heading if it exists, otherwise at the beginning
                                    const heading = richtextWindow.querySelector('.st-richtext-window__heading');
                                    if (heading && heading.nextSibling) {
                                        richtextWindow.insertBefore(contentEl, heading.nextSibling);
                                    } else if (heading) {
                                        richtextWindow.appendChild(contentEl);
                                    } else {
                                        richtextWindow.insertBefore(contentEl, richtextWindow.firstChild);
                                    }
                                    console.log('[WindowManager] Content element created and appended, innerHTML length:', contentEl.innerHTML.length);
                                }
                            } else {
                                console.warn('[WindowManager] No content to inject:', {
                                    hasParams: !!params,
                                    hasContent: params ? !!params.content : false,
                                    contentValue: params && params.content ? params.content.substring(0, 50) : null
                                });
                            }

                            // Inject bottom image if passed via params (after content, before CTA)
                            if (params && params.bottom_image) {
                                console.log('[WindowManager] Injecting bottom image:', params.bottom_image);
                                let bottomImageDiv = richtextWindow.querySelector('.st-richtext-window__bottom-image');
                                if (!bottomImageDiv) {
                                    bottomImageDiv = document.createElement('div');
                                    bottomImageDiv.className = 'st-richtext-window__bottom-image';
                                    // Insert before CTA actions or at the end
                                    const actionsDiv = richtextWindow.querySelector('.st-richtext-window__actions');
                                    if (actionsDiv) {
                                        richtextWindow.insertBefore(bottomImageDiv, actionsDiv);
                                    } else {
                                        richtextWindow.appendChild(bottomImageDiv);
                                    }
                                }
                                bottomImageDiv.innerHTML = `<img src="${this.escapeHtml(params.bottom_image)}" alt="${this.escapeHtml(params.bottom_image_alt || 'Bottom image')}" loading="lazy" width="800" height="auto">`;
                            }

                            // Inject CTA button if passed via params AND has an action configured
                            if (params && params.cta_text && richtextWindow) {
                                // Only show button if there's an action configured (action_type, action_value, or section_id)
                                const hasAction = params.cta_action_type || params.cta_action_value || params.cta_section_id;
                                if (hasAction) {
                                    let ctaButton = richtextWindow.querySelector('.st-richtext-window__cta[data-action="open-window"]');
                                    if (!ctaButton) {
                                        const actionsDiv = document.createElement('div');
                                        actionsDiv.className = 'st-richtext-window__actions';
                                        ctaButton = document.createElement('button');
                                        ctaButton.type = 'button';
                                        ctaButton.className = 'st-button st-button--primary st-richtext-window__cta';
                                        ctaButton.setAttribute('data-action', 'open-window');
                                        actionsDiv.appendChild(ctaButton);
                                        richtextWindow.appendChild(actionsDiv);
                                    }
                                    ctaButton.textContent = params.cta_text;

                                    // Inject CTA values from params
                                    if (params.cta_action_type) {
                                        ctaButton.setAttribute('data-action-type', params.cta_action_type);
                                    }
                                    if (params.cta_action_value) {
                                        ctaButton.setAttribute('data-action-value', params.cta_action_value);
                                    }
                                    if (params.cta_section_id) {
                                        ctaButton.setAttribute('data-section-id', params.cta_section_id);
                                    }
                                }
                            }

                            // Use event delegation for CTA button (reuse same handler as hero banner)
                            if (body && body._ctaClickHandler) {
                                // Handler already attached from hero banner, but we need to extend it for richtext
                                const existingHandler = body._ctaClickHandler;
                                const extendedHandler = async (e) => {
                                    // First try richtext button
                                    const richtextButton = e.target.closest('.st-richtext-window__cta[data-action="open-window"]');
                                    if (richtextButton) {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        const actionType = richtextButton.dataset.actionType || richtextButton.getAttribute('data-action-type');
                                        const actionValue = richtextButton.dataset.actionValue || richtextButton.getAttribute('data-action-value');
                                        const sectionId = richtextButton.dataset.sectionId || richtextButton.getAttribute('data-section-id');

                                        if (!actionType) return;

                                        try {
                                            // Reuse same logic as hero banner handler
                                            if (actionType === 'collection-app') {
                                                if (sectionId && sectionId.trim() !== '') {
                                                    await this.openAppWindow('collection-app', sectionId);
                                                } else if (actionValue && actionValue.trim() !== '') {
                                                    const desktopIcon = document.querySelector(`[data-action-type="collection-app"][data-action-value="${actionValue}"], [data-action-type="collection"][data-action-value="${actionValue}"]`);
                                                    if (desktopIcon && desktopIcon.dataset.sectionId) {
                                                        await this.openAppWindow('collection-app', desktopIcon.dataset.sectionId);
                                                    } else {
                                                        await this.openCollection(actionValue);
                                                    }
                                                }
                                            } else if (actionType === 'collection') {
                                                if (sectionId && sectionId.trim() !== '') {
                                                    await this.openAppWindow('collection-app', sectionId);
                                                } else if (actionValue && actionValue.trim() !== '') {
                                                    await this.openCollection(actionValue);
                                                }
                                            } else if (actionType === 'product') {
                                                if (sectionId && sectionId.trim() !== '') {
                                                    await this.openAppWindow('product-app', sectionId);
                                                } else if (actionValue && actionValue.trim() !== '') {
                                                    await this.openProduct(actionValue);
                                                }
                                            } else if (actionType === 'page') {
                                                if (actionValue === 'hero-banner' || actionValue === 'home') {
                                                    await this.openHeroBanner();
                                                } else if (actionValue && actionValue.trim() !== '') {
                                                    await this.openPage(actionValue);
                                                }
                                            } else if (actionType === 'hero-banner-app' || actionType === 'product-app' || actionType === 'richtext-app') {
                                                if (sectionId && sectionId.trim() !== '') {
                                                    await this.openAppWindow(actionType, sectionId);
                                                }
                                            }
                                        } catch (error) {
                                            console.error('[WindowManager] Error opening window from richtext CTA button:', error);
                                        }
                                        return;
                                    }
                                    // Fall back to existing handler for hero banner buttons
                                    if (existingHandler) {
                                        existingHandler(e);
                                    }
                                };
                                body.removeEventListener('click', existingHandler);
                                body.addEventListener('click', extendedHandler);
                                body._ctaClickHandler = extendedHandler;
                            } else if (body) {
                                // No existing handler, create new one
                                const handleCTAClick = async (e) => {
                                    const button = e.target.closest('.st-richtext-window__cta[data-action="open-window"]');
                                    if (!button) return;

                                    e.preventDefault();
                                    e.stopPropagation();

                                    const actionType = button.dataset.actionType || button.getAttribute('data-action-type');
                                    const actionValue = button.dataset.actionValue || button.getAttribute('data-action-value');
                                    const sectionId = button.dataset.sectionId || button.getAttribute('data-section-id');

                                    if (!actionType) return;

                                    try {
                                        if (actionType === 'collection-app') {
                                            if (sectionId && sectionId.trim() !== '') {
                                                await this.openAppWindow('collection-app', sectionId);
                                            } else if (actionValue && actionValue.trim() !== '') {
                                                const desktopIcon = document.querySelector(`[data-action-type="collection-app"][data-action-value="${actionValue}"], [data-action-type="collection"][data-action-value="${actionValue}"]`);
                                                if (desktopIcon && desktopIcon.dataset.sectionId) {
                                                    await this.openAppWindow('collection-app', desktopIcon.dataset.sectionId);
                                                } else {
                                                    await this.openCollection(actionValue);
                                                }
                                            }
                                        } else if (actionType === 'collection') {
                                            if (sectionId && sectionId.trim() !== '') {
                                                await this.openAppWindow('collection-app', sectionId);
                                            } else if (actionValue && actionValue.trim() !== '') {
                                                await this.openCollection(actionValue);
                                            }
                                        } else if (actionType === 'product') {
                                            if (sectionId && sectionId.trim() !== '') {
                                                await this.openAppWindow('product-app', sectionId);
                                            } else if (actionValue && actionValue.trim() !== '') {
                                                await this.openProduct(actionValue);
                                            }
                                        } else if (actionType === 'page') {
                                            if (actionValue === 'hero-banner' || actionValue === 'home') {
                                                await this.openHeroBanner();
                                            } else if (actionValue && actionValue.trim() !== '') {
                                                await this.openPage(actionValue);
                                            }
                                        } else if (actionType === 'hero-banner-app' || actionType === 'product-app' || actionType === 'richtext-app') {
                                            if (sectionId && sectionId.trim() !== '') {
                                                await this.openAppWindow(actionType, sectionId);
                                            }
                                        }
                                    } catch (error) {
                                        console.error('[WindowManager] Error opening window from richtext CTA button:', error);
                                    }
                                };
                                body.addEventListener('click', handleCTAClick);
                                body._ctaClickHandler = handleCTAClick;
                            }
                        }

                        // If window height is auto, calculate and set height after content loads
                        // BUT only if we don't have a data-height attribute (which should be used instead)
                        const isMobile = this.isMobile();
                        const hasDataHeight = this.getWindowSizeAttribute(windowElement, isMobile, 'height') ||
                            this.getWindowSizeAttribute(windowElement, false, 'height'); // Check desktop too
                        if ((windowElement.style.height === 'auto' || windowElement.style.height === '') && !hasDataHeight) {
                            // Wait for images to load, then calculate height
                            setTimeout(() => {
                                const titlebarHeight = windowElement.querySelector('.st-window__titlebar')?.offsetHeight || 0;
                                const menubarHeight = windowElement.querySelector('.st-window__menubar')?.offsetHeight || 0;
                                const statusbarHeight = windowElement.querySelector('.st-window__statusbar')?.offsetHeight || 0;
                                const bodyHeight = body.scrollHeight;
                                const totalHeight = titlebarHeight + menubarHeight + bodyHeight + statusbarHeight;

                                windowElement.style.height = `${totalHeight}px`;
                                // Update window data
                                const windowData = this.windows.get(windowElement.dataset.windowId);
                                if (windowData) {
                                    windowData.size.height = totalHeight;
                                }
                            }, 100);
                        } else if (hasDataHeight) {
                            // If we have data-height, use it instead of auto-calculating
                            let dataHeight = this.getWindowSizeAttribute(windowElement, isMobile, 'height');

                            if (dataHeight && dataHeight !== 'auto') {
                                // Ensure height has units
                                if (!isNaN(dataHeight) && !dataHeight.includes('px') && !dataHeight.includes('%') && !dataHeight.includes('vh')) {
                                    dataHeight = `${dataHeight}px`;
                                }
                                windowElement.style.height = dataHeight;
                                const windowData = this.windows.get(windowElement.dataset.windowId);
                                if (windowData) {
                                    windowData.size.height = dataHeight;
                                }
                            }
                        }

                        // If we have a collection parameter, fetch the collection's products via JSON API
                        // This is needed because request.query_parameters might not work with section rendering API
                        if (params && params.collection && sectionName === 'window-collection') {
                            const collectionHandle = params.collection;
                            console.log('WindowManager: Loading collection products for:', collectionHandle);

                            // Get products_per_row from section settings if available
                            const productsPerRow = params.productsPerRow || 3;

                            // Load collection data via JSON API and render dynamically
                            await this.loadCollectionData(body, collectionHandle, windowElement, {
                                productsPerRow: productsPerRow
                            });

                            // Constrain window to viewport after content loads
                            this.constrainWindowToViewport(windowElement);
                            return; // Exit early, content is loaded via JSON API
                        }

                        // Constrain window to viewport after content loads
                        this.constrainWindowToViewport(windowElement);
                    } else {
                        console.warn(`WindowManager: Could not find section content for ${sectionName}`);
                        body.innerHTML = '<p>Content could not be loaded.</p>';
                    }
                } else {
                    console.warn(`WindowManager: Could not load section ${sectionName} (${response.status})`);
                    body.innerHTML = '<p>Content could not be loaded. Please refresh the page.</p>';
                }
            } catch (error) {
                console.error(`WindowManager: Error loading section ${sectionName}:`, error);
                body.innerHTML = '<p>Error loading content. Please refresh the page.</p>';
            }
        }

        /**
         * Center window on screen
         */
        centerWindow(windowElement) {
            const desktop = document.getElementById('desktop');
            if (!desktop) return;

            const desktopRect = desktop.getBoundingClientRect();
            const taskbar = document.getElementById('taskbar');
            const taskbarHeight = taskbar ? taskbar.offsetHeight : 0;
            const availableHeight = desktopRect.height - taskbarHeight;

            const windowWidth = windowElement.offsetWidth || 600;
            const windowHeight = windowElement.offsetHeight || 400;

            const left = (desktopRect.width - windowWidth) / 2;
            const top = (availableHeight - windowHeight) / 2;

            windowElement.style.left = `${Math.max(0, left)}px`;
            windowElement.style.top = `${Math.max(0, top)}px`;
            windowElement.style.position = 'absolute';
        }

        /**
         * Cascade window (offset from previous windows)
         * Windows open slightly offset to the right and bottom so they don't stack on top
         */
        cascadeWindow(windowElement) {
            const desktop = document.getElementById('desktop');
            if (!desktop) return;

            const desktopRect = desktop.getBoundingClientRect();
            const taskbar = document.getElementById('taskbar');
            const taskbarHeight = taskbar ? taskbar.offsetHeight : parseInt(getComputedStyle(document.documentElement).getPropertyValue('--st-taskbar-height')) || 32;
            const isMobile = this.isMobile();
            const minMargin = isMobile ? 16 : 20; // Minimum spacing from taskbar
            // Check if this is a hero banner window (special top margin on mobile)
            const windowId = windowElement.id.replace('window-', '');
            const isHeroBanner = windowId.includes('hero-banner') || windowElement.getAttribute('data-section-name') === 'window-hero-banner';
            const minMarginTop = (isMobile && isHeroBanner) ? 200 : (isMobile ? 16 : 20); // 200px for hero banner on mobile, 16px for others on mobile, 20px on desktop
            const availableHeight = desktopRect.height - taskbarHeight - minMarginTop - minMargin; // Top and bottom margins

            const windowWidth = windowElement.offsetWidth || 600;
            let windowHeight = windowElement.offsetHeight || 400;

            // Constrain window height to available space
            if (windowHeight > availableHeight) {
                windowHeight = availableHeight;
                windowElement.style.height = `${windowHeight}px`;
            }

            // Count open windows (not minimized) to determine cascade position
            const openWindowsCount = Array.from(this.windows.values())
                .filter(w => w.element && !w.element.classList.contains('st-window--minimized'))
                .length;

            // Start from a base position (offset to avoid desktop icons)
            // Desktop icons are typically in the left column, so start windows further right
            const baseX = 200; // Increased from 50 to avoid icon column
            const baseY = minMarginTop; // Use top margin (200px on mobile, 20px on desktop)

            // Calculate cascade offset
            const cascadeX = baseX + (openWindowsCount * this.cascadeStep);
            const cascadeY = baseY + (openWindowsCount * this.cascadeStep);

            // Calculate final position
            let finalX = cascadeX;
            let finalY = cascadeY;

            // Ensure window doesn't go off-screen and respects taskbar spacing
            const maxX = desktopRect.width - windowWidth;
            const maxY = availableHeight - windowHeight + minMargin; // Add margin back for top position

            // If cascade would go off-screen, reset to base position
            if (finalX > maxX || finalY > maxY) {
                // Reset cascade offset and start from base
                this.cascadeOffset = { x: baseX, y: baseY };
                finalX = baseX;
                finalY = baseY;
            } else {
                // Update cascade offset for next window
                this.cascadeOffset = { x: cascadeX, y: cascadeY };
            }

            // Clamp to viewport with minimum margins
            finalX = Math.max(minMargin, Math.min(finalX, maxX));
            finalY = Math.max(minMarginTop, Math.min(finalY, maxY));

            // Ensure window doesn't overlap taskbar (bottom constraint)
            const windowBottom = finalY + windowHeight;
            const maxBottom = desktopRect.height - taskbarHeight - minMargin;
            if (windowBottom > maxBottom) {
                finalY = Math.max(minMargin, maxBottom - windowHeight);
            }

            windowElement.style.left = `${finalX}px`;
            windowElement.style.top = `${finalY}px`;
            windowElement.style.position = 'absolute';
        }

        /**
         * Constrain a single window to viewport (prevent overflow)
         */
        constrainWindowToViewport(windowElement) {
            const desktop = document.getElementById('desktop');
            if (!desktop) return;

            const desktopRect = desktop.getBoundingClientRect();
            const taskbar = document.getElementById('taskbar');
            const taskbarHeight = taskbar ? taskbar.offsetHeight : parseInt(getComputedStyle(document.documentElement).getPropertyValue('--st-taskbar-height')) || 32;
            const isMobile = this.isMobile();
            const minMargin = isMobile ? 16 : 20; // Minimum spacing from taskbar
            // Check if this is a hero banner window (special top margin on mobile)
            const windowId = windowElement.id.replace('window-', '');
            const isHeroBanner = windowId.includes('hero-banner') || windowElement.getAttribute('data-section-name') === 'window-hero-banner';
            const minMarginTop = (isMobile && isHeroBanner) ? 200 : (isMobile ? 16 : 20); // 200px for hero banner on mobile, 16px for others on mobile, 20px on desktop
            const viewportWidth = desktopRect.width;
            const viewportHeight = desktopRect.height - taskbarHeight - minMarginTop - minMargin; // Top and bottom margins
            const maxBottom = desktopRect.height - taskbarHeight - minMargin; // Maximum bottom position

            const rect = windowElement.getBoundingClientRect();
            let currentWidth = rect.width;
            let currentHeight = rect.height;
            const isCentered = windowElement.style.transform && windowElement.style.transform.includes('translate(-50%, -50%)') ||
                (windowElement.style.left && windowElement.style.left.includes('%') &&
                    windowElement.style.top && windowElement.style.top.includes('%'));

            // Always constrain width and height to viewport, even for centered windows
            let widthChanged = false;
            if (currentWidth > viewportWidth) {
                windowElement.style.width = `${viewportWidth}px`;
                currentWidth = viewportWidth;
                widthChanged = true;
            }

            // Constrain height to available space (viewport minus margins)
            if (currentHeight > viewportHeight) {
                windowElement.style.height = `${viewportHeight}px`;
                currentHeight = viewportHeight;
            }

            // Only constrain position for non-centered windows
            if (!isCentered) {
                const leftStyle = windowElement.style.left;
                const topStyle = windowElement.style.top;

                // Check if position uses calc() or percentage (e.g., right-aligned windows like cart)
                const isRightAligned = leftStyle && (leftStyle.includes('calc') || leftStyle.includes('%'));
                const isTopPercentage = topStyle && topStyle.includes('%');

                let currentX = parseInt(leftStyle) || rect.left;
                let currentY = parseInt(topStyle) || rect.top;

                // Only constrain horizontal position if not using calc/percentage positioning
                if (!isRightAligned) {
                    if (currentX + currentWidth > viewportWidth) {
                        currentX = Math.max(minMargin, viewportWidth - currentWidth);
                        windowElement.style.left = `${currentX}px`;
                    }
                    if (currentX < minMargin) {
                        windowElement.style.left = `${minMargin}px`;
                    }
                }
                // If right-aligned, ensure it doesn't go off-screen by checking the right edge
                else if (isRightAligned) {
                    const rightEdge = rect.right;
                    if (rightEdge > desktopRect.width - minMargin) {
                        // Window goes too far right, adjust the calc() value
                        const rightMargin = 20; // Desktop right margin
                        windowElement.style.left = `calc(100% - ${currentWidth + rightMargin}px)`;
                    }
                }

                // Only constrain vertical position if not using percentage positioning
                if (!isTopPercentage) {
                    // Constrain vertical position - ensure minimum spacing from top and bottom
                    const windowBottom = currentY + currentHeight;
                    if (windowBottom > maxBottom) {
                        // Window goes too far down, move it up
                        currentY = Math.max(minMarginTop, maxBottom - currentHeight);
                        windowElement.style.top = `${currentY}px`;
                    }
                    if (currentY < minMarginTop) {
                        currentY = minMarginTop;
                        windowElement.style.top = `${minMarginTop}px`;
                    }
                }
            } else if (widthChanged) {
                // If we changed the width of a centered window, re-center it
                // The transform will handle centering automatically
            }
        }

        /**
         * Set up window event listeners for cleanup
         */
        setupWindowListeners(windowElement, windowId) {
            // Store listeners for cleanup
            if (!windowElement._windowListeners) {
                windowElement._windowListeners = [];
            }

            // Listen for window events
            const handleMinimize = () => {
                const windowData = this.windows.get(windowId);
                if (windowData) {
                    windowData.state = 'minimized';
                }
            };

            const handleMaximize = () => {
                const windowData = this.windows.get(windowId);
                if (windowData) {
                    windowData.isMaximized = true;
                    // Store previous state
                    if (windowElement.windowShell && windowElement.windowShell.previousState) {
                        windowData.previousState = { ...windowElement.windowShell.previousState };
                    }
                }
            };

            const handleRestore = () => {
                const windowData = this.windows.get(windowId);
                if (windowData) {
                    windowData.isMaximized = false;
                    windowData.state = 'open';
                }
            };

            windowElement.addEventListener('window:minimize', handleMinimize);
            windowElement.addEventListener('window:maximize', handleMaximize);
            windowElement.addEventListener('window:restore', handleRestore);

            windowElement._windowListeners.push(
                { type: 'window:minimize', handler: handleMinimize },
                { type: 'window:maximize', handler: handleMaximize },
                { type: 'window:restore', handler: handleRestore }
            );
        }

        /**
         * Open collection window
         */
        async openCollection(collectionHandle, options = {}) {
            if (!collectionHandle) {
                console.warn('WindowManager: No collection handle provided');
                return;
            }

            const windowId = `collection-${collectionHandle}`;

            // Check if window already exists
            const existingWindow = this.windows.get(windowId);
            if (existingWindow && existingWindow.element) {
                // If data attributes are provided, update them on the existing window
                const dataAttributes = options.dataAttributes || {};
                if (dataAttributes && Object.keys(dataAttributes).length > 0) {
                    console.log('[WindowManager] Updating dataAttributes on existing collection window:', dataAttributes);
                    const windowElement = existingWindow.element;
                    Object.keys(dataAttributes).forEach(key => {
                        const value = dataAttributes[key];
                        if (value !== null && value !== undefined) {
                            windowElement.setAttribute(key, value);
                        }
                    });
                    // Update window size and position from data attributes if they exist
                    const isMobile = this.isMobile();
                    this.updateWindowSizeFromSection(windowElement, 'window-collection', isMobile);
                    this.updateWindowPositionFromAttributes(windowElement, isMobile);
                }
                this.bringToFront(windowId);
                // Restore if minimized
                if (existingWindow.state === 'minimized') {
                    this.restore(windowId);
                }
                return existingWindow.element;
            }

            // Check if window is currently being opened (prevent duplicate opens)
            if (this.openingWindows.has(windowId)) {
                console.log(`[WindowManager] Collection window ${windowId} is already being opened`);
                // Wait a bit and check again
                await new Promise(resolve => setTimeout(resolve, 100));
                const retryWindow = this.windows.get(windowId);
                if (retryWindow && retryWindow.element) {
                    this.bringToFront(windowId);
                    if (retryWindow.state === 'minimized') {
                        this.restore(windowId);
                    }
                    return retryWindow.element;
                }
            }

            // Get icon from desktop icon if available
            const desktopIcon = document.querySelector(`[data-action-type="collection"][data-action-value="${collectionHandle}"]`);
            const iconUrl = desktopIcon ? desktopIcon.querySelector('img')?.src : (options.icon || null);

            // Use provided title or default
            let collectionTitle = options.title || `Collection: ${collectionHandle}`;

            // Get data attributes from options if provided (from collection-app section)
            const dataAttributes = options.dataAttributes || {};
            console.log('[WindowManager] openCollection dataAttributes:', dataAttributes);

            // Open collection window with section
            // Store collection handle for use in loadSectionContent
            // Only use hardcoded size if no data attributes are provided
            const defaultSize = (dataAttributes && Object.keys(dataAttributes).length > 0)
                ? undefined // Let open() use data attributes
                : { width: '900px', height: '700px' }; // Fallback to defaults

            const openOptions = {
                title: collectionTitle,
                icon: iconUrl,
                section: 'window-collection',
                sectionParams: {
                    collection: collectionHandle,
                    productsPerRow: options.productsPerRow || 3
                }, // Pass collection handle and products per row
                canResize: true,
                canMaximize: true,
                windowType: 'collection',
                statusText: '0 Products', // Will be updated after products load
                dataAttributes: dataAttributes // Pass data attributes to window
            };

            // Only add size if we don't have data attributes
            if (defaultSize) {
                openOptions.size = defaultSize;
            }

            const windowElement = await this.open(windowId, openOptions);

            // Store collection handle as data attribute for the section to read
            if (windowElement) {
                windowElement.setAttribute('data-collection-handle', collectionHandle);
            }

            // Note: Product click handlers will be set up in loadCollectionData
            // No need for setTimeout here since loadCollectionData handles it

            return windowElement;
        }

        /**
         * Open product window
         */
        async openProduct(productHandle, options = {}) {
            if (!productHandle) {
                console.warn('WindowManager: No product handle provided');
                return;
            }

            const windowId = `product-${productHandle}`;

            // Check if window already exists
            const existingWindow = this.windows.get(windowId);
            if (existingWindow && existingWindow.element) {
                // If data attributes are provided, update them on the existing window
                const dataAttributes = options.dataAttributes || {};
                if (dataAttributes && Object.keys(dataAttributes).length > 0) {
                    console.log('[WindowManager] Updating dataAttributes on existing product window:', dataAttributes);
                    const windowElement = existingWindow.element;
                    Object.keys(dataAttributes).forEach(key => {
                        const value = dataAttributes[key];
                        if (value !== null && value !== undefined) {
                            windowElement.setAttribute(key, value);
                        }
                    });
                    // Update window size and position from data attributes if they exist
                    const isMobile = this.isMobile();
                    this.updateWindowSizeFromSection(windowElement, 'window-product', isMobile);
                    this.updateWindowPositionFromAttributes(windowElement, isMobile);
                }
                this.bringToFront(windowId);
                // Restore if minimized
                if (existingWindow.state === 'minimized') {
                    this.restore(windowId);
                }
                return existingWindow.element;
            }

            // Check if window is currently being opened (prevent duplicate opens)
            if (this.openingWindows.has(windowId)) {
                console.log(`[WindowManager] Product window ${windowId} is already being opened`);
                // Wait a bit and check again
                await new Promise(resolve => setTimeout(resolve, 100));
                const retryWindow = this.windows.get(windowId);
                if (retryWindow && retryWindow.element) {
                    this.bringToFront(windowId);
                    if (retryWindow.state === 'minimized') {
                        this.restore(windowId);
                    }
                    return retryWindow.element;
                }
            }

            // Get icon from desktop icon if available
            const desktopIcon = document.querySelector(`[data-action-type="product"][data-action-value="${productHandle}"]`);
            const iconUrl = desktopIcon ? desktopIcon.querySelector('img')?.src : null;

            // Fetch product title (optional enhancement)
            let productTitle = options.title || `Product: ${productHandle}`;

            // Get data attributes from options if provided (from product-app section)
            let dataAttributes = options.dataAttributes || {};

            // If no data attributes provided, try to get them from product-app section
            if (!dataAttributes || Object.keys(dataAttributes).length === 0) {
                console.log('[WindowManager] openProduct: No dataAttributes in options, trying to get from product-app section');
                dataAttributes = this.getProductDataAttributes(productHandle);
            }

            console.log('[WindowManager] openProduct dataAttributes (final):', dataAttributes);

            // Open product window with section
            // Only use hardcoded size if no data attributes are provided
            const defaultSize = (dataAttributes && Object.keys(dataAttributes).length > 0)
                ? undefined // Let open() use data attributes
                : { width: '900px', height: '700px' }; // Fallback to defaults

            const openOptions = {
                title: productTitle,
                icon: iconUrl,
                section: 'window-product',
                sectionParams: { product: productHandle }, // Pass product handle
                canResize: true,
                canMaximize: true,
                windowType: 'product',
                showMenuBar: true,
                menuItems: [
                    { label: 'View All', action: 'collection' },
                    { label: 'Share', url: '' },
                    { label: 'Help', url: '' }
                ],
                showAddressBar: true,
                statusText: 'Press ENTER to ACE your cart.',
                dataAttributes: dataAttributes // Pass data attributes to window
            };

            // Only add size if we don't have data attributes
            if (defaultSize) {
                openOptions.size = defaultSize;
            }

            const windowElement = await this.open(windowId, openOptions);

            // After window loads, set up product form handlers
            setTimeout(() => {
                this.setupProductWindowHandlers(windowElement, productHandle);

                // Final verification: Check if data attributes are still present
                // Also try to get them again if they're missing (in case product-app section loads later)
                if (!dataAttributes || Object.keys(dataAttributes).length === 0) {
                    console.log('[WindowManager] No data attributes in openProduct, trying to get from product-app section again...');
                    const retryDataAttributes = this.getProductDataAttributes(productHandle);
                    if (retryDataAttributes && Object.keys(retryDataAttributes).length > 0) {
                        console.log('[WindowManager] Found data attributes on retry, storing them now:', retryDataAttributes);
                        Object.keys(retryDataAttributes).forEach(key => {
                            const value = retryDataAttributes[key];
                            if (value !== null && value !== undefined) {
                                windowElement.setAttribute(key, value);
                                console.log(`[WindowManager] Stored ${key} = ${value} on window element (retry)`);
                            }
                        });
                        dataAttributes = retryDataAttributes;
                    }
                }

                if (dataAttributes && Object.keys(dataAttributes).length > 0) {
                    console.log('[WindowManager] Final verification of data attributes after setupProductWindowHandlers...');
                    let needsFinalRestore = false;
                    Object.keys(dataAttributes).forEach(key => {
                        const expected = dataAttributes[key];
                        const actual = windowElement.getAttribute(key);
                        if (expected && expected !== actual) {
                            console.warn(`[WindowManager] Attribute ${key} was lost after setupProductWindowHandlers! Restoring...`);
                            windowElement.setAttribute(key, expected);
                            needsFinalRestore = true;
                        }
                    });
                    if (needsFinalRestore) {
                        console.log('[WindowManager] Restored data attributes after setupProductWindowHandlers');
                    } else {
                        console.log('[WindowManager] ✓ All data attributes still present after setupProductWindowHandlers');
                    }

                    // Final DOM inspection
                    const finalAttrs = {
                        'data-width': windowElement.getAttribute('data-width'),
                        'data-height': windowElement.getAttribute('data-height'),
                        'data-left': windowElement.getAttribute('data-left'),
                        'data-top': windowElement.getAttribute('data-top'),
                        'data-width-mobile': windowElement.getAttribute('data-width-mobile'),
                        'data-height-mobile': windowElement.getAttribute('data-height-mobile'),
                        'data-left-mobile': windowElement.getAttribute('data-left-mobile'),
                        'data-top-mobile': windowElement.getAttribute('data-top-mobile')
                    };
                    console.log('[WindowManager] FINAL DOM INSPECTION - Data attributes on window element:', finalAttrs);
                    console.log('[WindowManager] Window element ID:', windowElement.id);
                    console.log('[WindowManager] Window element outerHTML snippet:', windowElement.outerHTML.substring(0, 500));
                } else {
                    console.warn('[WindowManager] No data attributes available for product window:', productHandle);
                }
            }, 500);

            return windowElement;
        }

        /**
         * Open cart window
         */
        async openCart() {
            const windowId = 'cart';

            // Check if window already exists - use querySelector to find by ID as well
            const existingWindow = this.windows.get(windowId);
            const existingElement = document.getElementById(`window-${windowId}`);

            if (existingWindow && existingWindow.element && existingElement) {
                this.bringToFront(windowId);
                // Restore if minimized
                if (existingWindow.state === 'minimized') {
                    this.restore(windowId);
                }
                // Refresh cart content
                await this.loadCartData(existingWindow.element);
                return existingWindow.element;
            }

            // If element exists in DOM but not in windows map, clean it up
            if (existingElement && !existingWindow) {
                console.warn('[WindowManager] Found orphaned cart window in DOM, removing it');
                existingElement.remove();
            }

            // Check if window is currently being opened
            if (this.openingWindows.has(windowId)) {
                await new Promise(resolve => setTimeout(resolve, 100));
                const retryWindow = this.windows.get(windowId);
                if (retryWindow && retryWindow.element) {
                    this.bringToFront(windowId);
                    if (retryWindow.state === 'minimized') {
                        this.restore(windowId);
                    }
                    await this.loadCartData(retryWindow.element);
                    return retryWindow.element;
                }
            }

            // Open cart window on the right side (desktop) or centered (mobile)
            const taskbar = document.getElementById('taskbar');
            const taskbarHeight = taskbar ? taskbar.offsetHeight : 48;
            const isMobile = window.innerWidth <= 767;

            let windowWidth, windowHeight, positionX, positionY;

            if (isMobile) {
                // Mobile: cover most of screen with margins, not covering taskbar
                const sideMargin = 16; // Left/right margin
                const topMargin = 16; // Top margin (16px on mobile, same as other windows)
                const bottomMargin = 16; // Bottom margin
                windowWidth = window.innerWidth - (sideMargin * 2);
                // Height accounts for taskbar at bottom, so we need: viewport - taskbar - top margin - bottom margin
                windowHeight = window.innerHeight - taskbarHeight - topMargin - bottomMargin;
                positionX = sideMargin; // Left margin (as number, not string)
                positionY = topMargin; // Top margin (16px on mobile, as number, not string)
            } else {
                // Desktop: positioned on the right side
                windowWidth = 900;
                const rightMargin = 20;
                const topMargin = 20;
                const bottomMargin = 20;
                windowHeight = window.innerHeight - taskbarHeight - topMargin - bottomMargin;
                positionX = `calc(100% - ${windowWidth + rightMargin}px)`;
                positionY = `${topMargin}px`;
            }

            const windowElement = await this.open(windowId, {
                title: 'Shopping Cart',
                icon: (window.smashOSAssets && window.smashOSAssets.cartIcon) || null,
                section: 'window-cart',
                size: { width: `${windowWidth}px`, height: `${windowHeight}px` },
                position: {
                    x: positionX,
                    y: positionY
                },
                canResize: true,
                canMaximize: true,
                windowType: 'cart',
                showMenuBar: false,
                statusText: ''
            });

            // Ensure position is correct after window opens (especially on mobile)
            if (isMobile && windowElement) {
                // Force position to top margin on mobile
                windowElement.style.position = 'absolute';
                windowElement.style.left = `${positionX}px`;
                windowElement.style.top = `${positionY}px`;
                windowElement.style.transform = 'none'; // Remove any centering transform
            }

            // Load cart data after window opens
            setTimeout(async () => {
                await this.loadCartData(windowElement);
            }, 100);

            return windowElement;
        }

        /**
         * Load cart data from Shopify
         */
        async loadCartData(windowElement) {
            const container = windowElement.querySelector('.st-cart-window');
            if (!container) return;

            try {
                const response = await fetch('/cart.js', {
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                if (response.ok) {
                    const cart = await response.json();
                    this.renderCartContent(container, cart);
                } else {
                    container.innerHTML = '<div class="st-cart-window__empty"><p>Error loading cart.</p></div>';
                }
            } catch (error) {
                console.error('Error loading cart:', error);
                container.innerHTML = '<div class="st-cart-window__empty"><p>Error loading cart. Please try again.</p></div>';
            }
        }

        /**
         * Safe DOM builder. Strings go through textContent, not a parser.
         */
        el(tag, props = {}, children = []) {
            const node = document.createElement(tag);
            for (const [k, v] of Object.entries(props)) {
                if (v == null || v === false) continue;
                if (k === 'className') node.className = v;
                else if (k === 'dataset') Object.assign(node.dataset, v);
                else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2).toLowerCase(), v);
                else if (k === 'text') node.textContent = v;
                else if (k === 'ariaLabel') node.setAttribute('aria-label', v);
                else if (k === 'ariaLive') node.setAttribute('aria-live', v);
                else if (k === 'ariaHidden') node.setAttribute('aria-hidden', String(v));
                else node.setAttribute(k, v);
            }
            (Array.isArray(children) ? children : [children]).forEach(c => {
                if (c == null || c === false) return;
                node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
            });
            return node;
        }

        /**
         * Render cart content — unified renderer used at all viewports.
         * Delegates to the safe DOM-builder path (formerly mobile-only).
         */
        renderCartContent(container, cart) {
            if (!cart || !container) return;
            return this.renderCartContentMobile(container, cart);
        }

        /**
         * Cart rendering for mobile-native mode. Builds DOM via this.el()
         * so user content cannot inject HTML. Desktop path (renderCartContent)
         * stays byte-identical above.
         */
        renderCartContentMobile(container, cart) {
            const items = (cart && cart.items) || [];
            const itemCount = items.reduce((n, it) => n + (it.quantity || 0), 0);

            // Title-bar item count
            const windowEl = container.closest('.st-window');
            const titleEl = windowEl && windowEl.querySelector('.st-titlebar__title');
            if (titleEl) {
                const base = 'Shopping Cart';
                titleEl.textContent = itemCount > 0
                    ? `${base} · ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`
                    : base;
            }

            container.replaceChildren();
            if (items.length === 0) {
                container.appendChild(this.buildCartMobileEmpty());
                return;
            }

            // Scroll area
            const scroll = this.el('div', { className: 'st-cart-mobile' });
            const ship = this.buildCartMobileFreeShipping(cart);
            if (ship) scroll.appendChild(ship);
            const itemsWrap = this.el('div', { className: 'st-cart-mobile__items' });
            items.forEach(item => itemsWrap.appendChild(this.buildCartMobileItem(item)));
            scroll.appendChild(itemsWrap);
            scroll.appendChild(this.buildCartMobilePromo());
            container.appendChild(scroll);

            // Sticky summary (sibling to scroll area, so position: sticky works)
            container.appendChild(this.buildCartMobileSummary(cart));

            this.bindCartMobileEvents(container, cart);
        }

        // Stubs — filled in by later tasks
        buildCartMobileEmpty() {
            const imgUrl = (window.smashOSAssets && window.smashOSAssets.emptyCart) || '';
            const frameChildren = imgUrl ? [this.el('img', { src: imgUrl, alt: '', ariaHidden: true })] : [];
            const frame = this.el('div', { className: 'st-cart-mobile__empty-frame' }, frameChildren);

            const backBtn = this.el('button', {
                type: 'button',
                className: 'st-cart-mobile__btn',
                dataset: { cartAction: 'continue' },
                text: '← Back to desktop',
                onClick: (e) => { e.preventDefault(); this.close('cart'); },
            });

            return this.el('div', { className: 'st-cart-mobile__empty' }, [
                frame,
                this.el('div', { className: 'st-cart-mobile__empty-title', text: 'Your cart is empty' }),
                this.el('div', { className: 'st-cart-mobile__empty-sub', text: 'Open Tees, Caps or Socks from the desktop to start shopping.' }),
                backBtn,
            ]);
        }
        buildCartMobileFreeShipping(cart) {
            const threshold = this.getFreeShippingThreshold();
            if (!threshold || threshold <= 0) return null;

            const total = cart.total_price || 0;
            const thresholdCents = threshold * 100;
            const remaining = Math.max(0, thresholdCents - total);
            const pct = Math.min(100, Math.round((total / thresholdCents) * 100));

            if (remaining <= 0) {
                return this.el('div', { className: 'st-cart-mobile__shipping st-cart-mobile__shipping--unlocked' }, [
                    this.el('div', { className: 'st-cart-mobile__shipping-title', text: '✓ Free shipping unlocked' }),
                ]);
            }

            const fill = this.el('div', { className: 'st-cart-mobile__shipping-bar-fill' });
            fill.style.width = pct + '%';

            const bar = this.el('div', {
                className: 'st-cart-mobile__shipping-bar',
                role: 'progressbar',
                'aria-valuemin': '0',
                'aria-valuemax': '100',
                'aria-valuenow': String(pct),
            }, [fill]);

            return this.el('div', { className: 'st-cart-mobile__shipping' }, [
                this.el('div', { className: 'st-cart-mobile__shipping-title', text: `⚡ Free shipping at ${this.formatMoney(thresholdCents)}` }),
                bar,
                this.el('div', { className: 'st-cart-mobile__shipping-label', text: `${this.formatMoney(total)} of ${this.formatMoney(thresholdCents)} · add ${this.formatMoney(remaining)} for free shipping` }),
            ]);
        }

        getFreeShippingThreshold() {
            const sectionSetting = window.smashOS && window.smashOS.cartWindowSettings && window.smashOS.cartWindowSettings.freeShippingThreshold;
            if (sectionSetting) return Number(sectionSetting);
            const el = document.querySelector('#window-cart [data-free-shipping-threshold]');
            if (el) return Number(el.dataset.freeShippingThreshold);
            return 90; // dollars; matches pre-unification desktop behavior
        }
        buildCartMobileItem(item) {
            const variantText = (item.variant_title && item.variant_title !== 'Default Title')
                ? item.variant_title.replace(' / ', ' · ')
                : null;

            const img = item.image ? this.el('img', { src: item.image, alt: item.product_title, loading: 'lazy' }) : null;
            const imageFrame = this.el('div', { className: 'st-cart-mobile__item-image' }, img ? [img] : []);

            const meta = this.el('div', { className: 'st-cart-mobile__item-meta' }, [
                this.el('div', { className: 'st-cart-mobile__item-title', text: item.product_title }),
                variantText ? this.el('div', { className: 'st-cart-mobile__item-variant', text: variantText }) : null,
                this.el('div', { className: 'st-cart-mobile__item-price', text: this.formatMoney(item.line_price) }),
            ].filter(Boolean));

            const head = this.el('div', { className: 'st-cart-mobile__item-head' }, [imageFrame, meta]);

            const minusBtn = this.el('button', {
                type: 'button',
                className: 'st-cart-mobile__qty-btn',
                dataset: { qtyDelta: '-1', variantId: String(item.variant_id) },
                ariaLabel: 'Decrease quantity',
                text: '−',
            });
            const qtyValue = this.el('span', { className: 'st-cart-mobile__qty-value', ariaLive: 'polite', text: String(item.quantity) });
            const plusBtn = this.el('button', {
                type: 'button',
                className: 'st-cart-mobile__qty-btn',
                dataset: { qtyDelta: '1', variantId: String(item.variant_id) },
                ariaLabel: 'Increase quantity',
                text: '+',
            });
            const qty = this.el('div', { className: 'st-cart-mobile__qty', role: 'group', ariaLabel: 'Quantity' }, [minusBtn, qtyValue, plusBtn]);

            const removeBtn = this.el('button', {
                type: 'button',
                className: 'st-cart-mobile__remove',
                dataset: { variantId: String(item.variant_id) },
                ariaLabel: `Remove ${item.product_title}`,
            }, [
                this.el('span', { className: 'st-cart-mobile__remove-icon', ariaHidden: true, text: '🗑' }),
                this.el('span', { className: 'st-cart-mobile__remove-label', text: 'Remove' }),
            ]);

            const controls = this.el('div', { className: 'st-cart-mobile__item-controls' }, [qty, removeBtn]);

            return this.el('div', { className: 'st-cart-mobile__item', dataset: { variantId: String(item.variant_id) } }, [head, controls]);
        }
        buildCartMobilePromo() {
            const input = this.el('input', {
                type: 'text',
                className: 'st-input st-cart-mobile__promo-input',
                placeholder: 'Enter code',
                autocomplete: 'off',
                autocapitalize: 'characters',
                spellcheck: 'false',
            });
            const applyBtn = this.el('button', {
                type: 'button',
                className: 'st-cart-mobile__btn st-cart-mobile__promo-apply',
                dataset: { cartAction: 'apply-promo' },
                text: 'Apply',
            });
            const feedback = this.el('div', { className: 'st-cart-mobile__promo-feedback', ariaLive: 'polite' });

            const body = this.el('div', { className: 'st-cart-mobile__promo-body' }, [input, applyBtn, feedback]);

            const summary = this.el('summary', { className: 'st-cart-mobile__promo-summary' }, [
                this.el('span', { text: 'Promo code' }),
                this.el('span', { className: 'st-cart-mobile__promo-caret', ariaHidden: true, text: '▾' }),
            ]);

            return this.el('details', { className: 'st-cart-mobile__promo' }, [summary, body]);
        }

        async applyDiscountCode(code) {
            try {
                await fetch(`/discount/${encodeURIComponent(code)}`, { redirect: 'manual', credentials: 'same-origin' });
                const cartResp = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
                const cart = await cartResp.json();
                const windowEl = document.querySelector('.st-window[data-window-id="cart"]') ||
                    (this.windows.get('cart') && this.windows.get('cart').element);
                if (windowEl) this.loadCartData(windowEl);
                return { ok: !!(cart.total_discount && cart.total_discount > 0), message: 'Code accepted but no discount applied' };
            } catch (err) {
                console.error('[WindowManager] discount apply failed', err);
                return { ok: false, message: 'Could not apply code. Try again.' };
            }
        }
        buildCartMobileSummary(cart) {
            const subtotal = this.formatMoney(cart.items_subtotal_price || cart.total_price || 0);
            const total = this.formatMoney(cart.total_price || 0);

            const row = (label, valueNode, modifier) => {
                return this.el('div', { className: 'st-cart-mobile__summary-row' + (modifier ? ' ' + modifier : '') }, [
                    this.el('span', { text: label }),
                    this.el('span', { className: 'st-cart-mobile__summary-dots', ariaHidden: true }),
                    valueNode,
                ]);
            };

            const rows = this.el('div', { className: 'st-cart-mobile__summary-rows' }, [
                row('Subtotal', this.el('span', { text: subtotal })),
                row('Shipping', this.el('span', { className: 'st-cart-mobile__summary-muted', text: 'Calculated at checkout' })),
                row('TOTAL', this.el('span', { text: total }), 'st-cart-mobile__summary-row--total'),
            ]);

            const continueBtn = this.el('button', {
                type: 'button',
                className: 'st-cart-mobile__btn st-cart-mobile__btn--secondary',
                dataset: { cartAction: 'continue' },
                text: '← Keep shopping',
            });
            const checkoutLink = this.el('a', {
                href: '/checkout',
                className: 'st-cart-mobile__btn st-cart-mobile__btn--primary',
                dataset: { cartAction: 'checkout' },
                text: 'CHECKOUT →',
            });

            const actions = this.el('div', { className: 'st-cart-mobile__summary-actions' }, [continueBtn, checkoutLink]);
            return this.el('div', { className: 'st-cart-mobile__summary' }, [rows, actions]);
        }
        bindCartMobileEvents(container, cart) {
            const handler = (e) => {
                // Qty stepper
                const qtyBtn = e.target.closest('.st-cart-mobile__qty-btn');
                if (qtyBtn) {
                    e.preventDefault();
                    const variantId = qtyBtn.dataset.variantId;
                    const delta = parseInt(qtyBtn.dataset.qtyDelta, 10);
                    // Read current qty live from the DOM so we never see a stale
                    // closure-captured value after a prior render.
                    const itemEl = qtyBtn.closest('.st-cart-mobile__item');
                    const qtyValueEl = itemEl && itemEl.querySelector('.st-cart-mobile__qty-value');
                    const currentQty = qtyValueEl ? parseInt(qtyValueEl.textContent, 10) || 1 : 1;
                    // Stepper clamps to a minimum of 1 — explicit Remove removes.
                    const nextQty = Math.max(1, currentQty + delta);
                    if (nextQty === currentQty) return;
                    const windowEl = container.closest('.st-window');
                    this.updateCartItem(variantId, nextQty, windowEl);
                    return;
                }

                const removeBtn = e.target.closest('.st-cart-mobile__remove');
                if (removeBtn) {
                    e.preventDefault();
                    const variantId = removeBtn.dataset.variantId;
                    const windowEl = container.closest('.st-window');
                    this.updateCartItem(variantId, 0, windowEl);
                    return;
                }

                const continueBtn = e.target.closest('[data-cart-action="continue"]');
                if (continueBtn) {
                    e.preventDefault();
                    this.close('cart');
                    return;
                }
                // data-cart-action="checkout" is a plain <a>; browser navigates.

                const applyPromo = e.target.closest('[data-cart-action="apply-promo"]');
                if (applyPromo) {
                    e.preventDefault();
                    const scope = applyPromo.closest('.st-cart-mobile__promo-body') || container;
                    const input = scope.querySelector('.st-cart-mobile__promo-input');
                    const feedback = scope.querySelector('.st-cart-mobile__promo-feedback');
                    const code = (input && input.value || '').trim();
                    if (!code) {
                        if (feedback) { feedback.textContent = 'Enter a code first.'; feedback.dataset.state = 'error'; }
                        return;
                    }
                    applyPromo.disabled = true;
                    this.applyDiscountCode(code).then(result => {
                        if (feedback) {
                            feedback.textContent = result.ok
                                ? `✓ Code "${code}" applied`
                                : (result.message || 'Code not valid');
                            feedback.dataset.state = result.ok ? 'success' : 'error';
                        }
                        applyPromo.disabled = false;
                    });
                    return;
                }
            };
            // Remove any handler from a prior render so we never stack listeners
            // with stale closures over old cart data.
            if (container._cartMobileClickHandler) {
                container.removeEventListener('click', container._cartMobileClickHandler);
            }
            container.addEventListener('click', handler);
            container._cartMobileClickHandler = handler;
        }

        // setupCartWindowHandlers removed: the unified renderer wires its own
        // delegated click handler in bindCartMobileEvents. The old cloning-to-reset
        // pattern was destroying those handlers on every cart reload.

        /**
         * Update cart item quantity
         */
        async updateCartItem(variantId, quantity, windowElement) {
            try {
                const response = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        id: variantId,
                        quantity: quantity
                    })
                });

                if (response.ok) {
                    // Reload cart data
                    await this.loadCartData(windowElement);
                    // Update cart count
                    if (window.smashOS && window.smashOS.desktopManager) {
                        window.smashOS.desktopManager.updateCartCount();
                    }
                } else {
                    console.error('Failed to update cart item');
                }
            } catch (error) {
                console.error('Error updating cart item:', error);
            }
        }

        /**
         * Open page window (for hero banner, about, richtext, media player, etc.)
         */
        async openPage(pageHandle, options = {}) {
            if (!pageHandle) {
                console.warn('WindowManager: No page handle provided');
                return;
            }

            const windowId = `page-${pageHandle}`;

            // Check if window already exists
            const existingWindow = this.windows.get(windowId);
            if (existingWindow && existingWindow.element) {
                this.bringToFront(windowId);
                // Restore if minimized
                if (existingWindow.state === 'minimized') {
                    this.restore(windowId);
                }
                return existingWindow.element;
            }

            // Check if window is currently being opened (prevent duplicate opens)
            if (this.openingWindows.has(windowId)) {
                console.log(`[WindowManager] Page window ${windowId} is already being opened`);
                // Wait a bit and check again
                await new Promise(resolve => setTimeout(resolve, 100));
                const retryWindow = this.windows.get(windowId);
                if (retryWindow && retryWindow.element) {
                    this.bringToFront(windowId);
                    if (retryWindow.state === 'minimized') {
                        this.restore(windowId);
                    }
                    return retryWindow.element;
                }
            }

            // Determine section based on page handle or options
            let sectionName = options.section;

            // Map common page handles to sections
            if (!sectionName) {
                const sectionMap = {
                    'hero-banner': 'window-hero-banner',
                    'home': 'window-hero-banner',
                    'about': 'window-about',
                    'richtext': 'window-richtext',
                    'media-player': 'window-media-player'
                };
                sectionName = sectionMap[pageHandle] || `window-${pageHandle}`;
            }

            // Try to load section settings first to get data attributes
            let windowTitle = options.title || `Page: ${pageHandle}`;
            let statusText = options.statusText || '';
            let showMenuBar = options.showMenuBar !== undefined ? options.showMenuBar : true;
            let menuItems = options.menuItems;
            let windowWidth = options.size?.width;
            let windowHeight = options.size?.height;
            // Start with dataAttributes from options if provided (from app sections), otherwise empty object
            const dataAttributes = options.dataAttributes ? { ...options.dataAttributes } : {};

            try {
                const sectionUrl = `/?section_id=${sectionName}`;
                const response = await fetch(sectionUrl, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'text/html'
                    }
                });

                if (response.ok) {
                    const html = await response.text();
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    const sectionContent = doc.getElementById(`shopify-section-${sectionName}`);

                    if (sectionContent) {
                        const contentElement = sectionContent.querySelector('[data-window-title]');
                        if (contentElement) {
                            // Only use section's title if no title was explicitly passed in options
                            if (!options.title) {
                                windowTitle = contentElement.dataset.windowTitle || windowTitle;
                            }
                            // Only use section's statusText if no statusText was explicitly passed in options
                            if (!options.statusText) {
                                statusText = contentElement.dataset.statusText || statusText;
                            }
                            // Don't override showMenuBar from options - it's already set correctly from richtext-app
                            // Only override if showMenuBar wasn't explicitly passed in options
                            if (options.showMenuBar === undefined) {
                                showMenuBar = contentElement.dataset.showMenuBar === 'true';
                            }

                            // Read data-width and data-height attributes (check mobile first if on mobile)
                            const isMobile = this.isMobile();
                            windowWidth = this.getWindowSizeAttribute(contentElement, isMobile, 'width');
                            windowHeight = this.getWindowSizeAttribute(contentElement, isMobile, 'height');
                        }

                        // Also check the section element itself for data-width and data-height
                        const isMobile = this.isMobile();
                        if (sectionContent && !windowWidth) {
                            windowWidth = this.getWindowSizeAttribute(sectionContent, isMobile, 'width');
                        }
                        if (sectionContent && !windowHeight) {
                            windowHeight = this.getWindowSizeAttribute(sectionContent, isMobile, 'height');
                        }

                        // Store data attributes for responsive updates - only if not already provided from options
                        // (app sections like hero-banner-app already provide these, so don't overwrite)
                        if (Object.keys(dataAttributes).length === 0) {
                            const elementForAttributes = sectionContent.querySelector('[data-window-title]') ||
                                sectionContent.querySelector('div[data-width], div[data-width-mobile], div[data-height], div[data-height-mobile]') ||
                                sectionContent.firstElementChild ||
                                sectionContent;

                            if (elementForAttributes) {
                                if (elementForAttributes.getAttribute('data-width')) {
                                    dataAttributes['data-width'] = elementForAttributes.getAttribute('data-width');
                                }
                                if (elementForAttributes.getAttribute('data-height')) {
                                    dataAttributes['data-height'] = elementForAttributes.getAttribute('data-height');
                                }
                                if (elementForAttributes.getAttribute('data-width-mobile')) {
                                    dataAttributes['data-width-mobile'] = elementForAttributes.getAttribute('data-width-mobile');
                                }
                                if (elementForAttributes.getAttribute('data-height-mobile')) {
                                    dataAttributes['data-height-mobile'] = elementForAttributes.getAttribute('data-height-mobile');
                                }
                            }

                            // Also check sectionContent itself
                            if (sectionContent.getAttribute('data-width') && !dataAttributes['data-width']) {
                                dataAttributes['data-width'] = sectionContent.getAttribute('data-width');
                            }
                            if (sectionContent.getAttribute('data-height') && !dataAttributes['data-height']) {
                                dataAttributes['data-height'] = sectionContent.getAttribute('data-height');
                            }
                            if (sectionContent.getAttribute('data-width-mobile') && !dataAttributes['data-width-mobile']) {
                                dataAttributes['data-width-mobile'] = sectionContent.getAttribute('data-width-mobile');
                            }
                            if (sectionContent.getAttribute('data-height-mobile') && !dataAttributes['data-height-mobile']) {
                                dataAttributes['data-height-mobile'] = sectionContent.getAttribute('data-height-mobile');
                            }
                        }
                    }
                }
            } catch (error) {
                console.warn(`WindowManager: Could not load ${sectionName} settings:`, error);
            }

            // Get icon from desktop icon if available
            const desktopIcon = document.querySelector(`[data-action-type="page"][data-action-value="${pageHandle}"]`);
            const iconUrl = desktopIcon ? desktopIcon.querySelector('img')?.src : (options.icon || null);

            // Build size object - use data attributes if available, otherwise fall back to options or defaults
            const finalSize = {};
            if (windowWidth) {
                finalSize.width = windowWidth;
            } else if (options.size?.width) {
                finalSize.width = options.size.width;
            } else {
                finalSize.width = '900px';
            }

            if (windowHeight) {
                finalSize.height = windowHeight;
            } else if (options.size?.height) {
                finalSize.height = options.size.height;
            } else {
                finalSize.height = '700px';
            }

            return await this.open(windowId, {
                title: windowTitle,
                icon: iconUrl,
                section: sectionName,
                sectionParams: options.sectionParams || {},
                size: finalSize,
                position: options.position || null,
                canResize: options.canResize !== false,
                canMaximize: options.canMaximize !== false,
                showMenuBar: showMenuBar,
                menuItems: menuItems,
                statusText: statusText,
                dataAttributes: dataAttributes // Pass data attributes to be stored on window element
            });
        }

        /**
         * Get window content from app section
         */
        getWindowContentFromAppSection(sectionId) {
            // Find the section element
            const sectionElement = document.querySelector(`[id="shopify-section-${sectionId}"]`);
            if (!sectionElement) return null;

            // Find the app content container (hidden div with window content)
            const appContent = sectionElement.querySelector('[data-section-id]');
            if (!appContent) return null;

            // Clone the content
            const clone = appContent.cloneNode(true);
            clone.removeAttribute('data-section-id');
            clone.removeAttribute('style');
            clone.removeAttribute('data-window-title');
            clone.removeAttribute('data-window-icon');

            // Extract additional data attributes
            const collectionHandle = appContent.getAttribute('data-collection-handle');
            const productHandle = appContent.getAttribute('data-product-handle');
            const productsPerRow = appContent.getAttribute('data-products-per-row');
            const showMenuBar = appContent.getAttribute('data-show-menu-bar');
            const statusText = appContent.getAttribute('data-status-text');
            const displayByDefault = appContent.getAttribute('data-display-by-default');

            return {
                content: clone.innerHTML,
                title: appContent.getAttribute('data-window-title') || 'Window',
                icon: appContent.getAttribute('data-window-icon') || null,
                collectionHandle: collectionHandle,
                productHandle: productHandle,
                productsPerRow: productsPerRow ? parseInt(productsPerRow, 10) : null,
                showMenuBar: showMenuBar,
                statusText: statusText,
                displayByDefault: displayByDefault
            };
        }

        /**
         * Open app window (hero-banner-app, about-app, collection-app, etc.)
         */
        async openAppWindow(sectionType, sectionId) {
            const windowId = `${sectionType}-${sectionId}`;

            // Hide music tooltip if this is a media player window
            if (sectionType === 'media-player-app') {
                this.hideMusicTooltip();
            }

            // Check if window already exists
            const existingWindow = this.windows.get(windowId);
            if (existingWindow && existingWindow.element) {
                this.bringToFront(windowId);
                if (existingWindow.state === 'minimized') {
                    this.restore(windowId);
                }
                return existingWindow.element;
            }

            // Check if window is currently being opened (prevent duplicate opens)
            if (this.openingWindows.has(windowId)) {
                console.log(`[WindowManager] App window ${windowId} is already being opened`);
                // Wait a bit and check again
                await new Promise(resolve => setTimeout(resolve, 100));
                const retryWindow = this.windows.get(windowId);
                if (retryWindow && retryWindow.element) {
                    this.bringToFront(windowId);
                    if (retryWindow.state === 'minimized') {
                        this.restore(windowId);
                    }
                    return retryWindow.element;
                }
            }

            // Special handling for collection and product apps (use section rendering API)
            if (sectionType === 'collection-app') {
                const appContent = this.getWindowContentFromAppSection(sectionId);
                if (appContent && appContent.collectionHandle) {
                    console.log('WindowManager: Opening collection with handle:', appContent.collectionHandle);

                    // Get data attributes from app section content div
                    const sectionElement = document.querySelector(`[id="shopify-section-${sectionId}"]`);
                    const contentDiv = sectionElement?.querySelector('.st-collection-app-content');
                    const dataAttributes = contentDiv ? this.collectDataAttributes(contentDiv) : {};
                    console.log('[WindowManager] collection-app dataAttributes collected:', dataAttributes);

                    // Check if the actual collection window is already open or opening
                    // (not the app window ID, but the collection window ID)
                    const collectionWindowId = `collection-${appContent.collectionHandle}`;
                    const existingCollectionWindow = this.windows.get(collectionWindowId);
                    if (existingCollectionWindow && existingCollectionWindow.element) {
                        // Collection window already exists, bring it to front
                        this.bringToFront(collectionWindowId);
                        if (existingCollectionWindow.state === 'minimized') {
                            this.restore(collectionWindowId);
                        }
                        return existingCollectionWindow.element;
                    }
                    // Check if collection window is currently being opened
                    if (this.openingWindows.has(collectionWindowId)) {
                        console.log(`[WindowManager] Collection window ${collectionWindowId} is already being opened from app window`);
                        await new Promise(resolve => setTimeout(resolve, 100));
                        const retryWindow = this.windows.get(collectionWindowId);
                        if (retryWindow && retryWindow.element) {
                            this.bringToFront(collectionWindowId);
                            if (retryWindow.state === 'minimized') {
                                this.restore(collectionWindowId);
                            }
                            return retryWindow.element;
                        }
                    }
                    return await this.openCollection(appContent.collectionHandle, {
                        title: appContent.title,
                        icon: appContent.icon,
                        productsPerRow: appContent.productsPerRow,
                        dataAttributes: dataAttributes
                    });
                } else {
                    console.warn('WindowManager: No collection handle found for section', sectionId, appContent);
                }
            }

            // Special handling for hero-banner-app
            if (sectionType === 'hero-banner-app') {
                const appContent = this.getWindowContentFromAppSection(sectionId);
                if (appContent) {
                    // Get window settings from data attributes
                    const windowTitle = appContent.title || "Where The '90S Died and The Party Started";
                    const windowIcon = appContent.icon || null;
                    // showMenuBar is stored as string, so check for 'false'
                    const showMenuBar = appContent.showMenuBar !== 'false';
                    const statusText = appContent.statusText || "Smash or Pass? For Smash, press F1";

                    // Get content settings from app section
                    const sectionElement = document.querySelector(`[id="shopify-section-${sectionId}"]`);
                    const contentDiv = sectionElement?.querySelector('.st-hero-banner-app-content');
                    // Use getAttribute for both to ensure correct reading (dataset can be unreliable with hyphens)
                    // data-hero-image = desktop image (from section.settings.hero_image, width 1200)
                    // data-hero-image-mobile = mobile image (from section.settings.hero_image_mobile, width 800)
                    const heroImage = contentDiv?.getAttribute('data-hero-image'); // Desktop
                    const heroImageMobile = contentDiv?.getAttribute('data-hero-image-mobile'); // Mobile

                    console.log('[WindowManager] Hero image attributes read:', {
                        'data-hero-image (desktop)': heroImage,
                        'data-hero-image-mobile (mobile)': heroImageMobile
                    });
                    // Use getAttribute for all data attributes to ensure reliable reading
                    const slogan = contentDiv?.getAttribute('data-slogan');
                    const ctaText = contentDiv?.getAttribute('data-cta-text');
                    const ctaActionType = contentDiv?.getAttribute('data-cta-action-type');
                    const ctaActionValue = contentDiv?.getAttribute('data-cta-action-value');
                    const ctaSectionId = contentDiv?.getAttribute('data-cta-section-id');
                    const galleryHint = contentDiv?.getAttribute('data-gallery-hint');
                    const textAlignment = contentDiv?.getAttribute('data-text-alignment');
                    const textPosition = contentDiv?.getAttribute('data-text-position');
                    const overlayOpacity = contentDiv?.getAttribute('data-overlay-opacity');

                    console.log('[WindowManager] Hero banner content attributes read:', {
                        'slogan': slogan,
                        'ctaText': ctaText,
                        'textAlignment': textAlignment,
                        'textPosition': textPosition,
                        'overlayOpacity': overlayOpacity
                    });

                    // Read data-width and data-height attributes (check mobile first if on mobile)
                    const isMobile = this.isMobile();
                    let windowWidth = this.getWindowSizeAttribute(contentDiv, isMobile, 'width');
                    // Ensure width has units (add px if it's just a number)
                    if (windowWidth && !isNaN(windowWidth) && !windowWidth.includes('px') && !windowWidth.includes('%') && !windowWidth.includes('vw')) {
                        windowWidth = `${windowWidth}px`;
                    }

                    let windowHeight = this.getWindowSizeAttribute(contentDiv, isMobile, 'height');
                    // Ensure height has units (add px if it's just a number, but allow 'auto')
                    if (windowHeight && windowHeight !== 'auto' && !isNaN(windowHeight) && !windowHeight.includes('px') && !windowHeight.includes('%') && !windowHeight.includes('vh')) {
                        windowHeight = `${windowHeight}px`;
                    }

                    console.log('[WindowManager] Hero banner app content:', {
                        heroImage,
                        slogan,
                        ctaText,
                        ctaActionType,
                        ctaActionValue,
                        ctaSectionId,
                        contentDiv: !!contentDiv,
                        allDataAttributes: contentDiv ? Object.keys(contentDiv.dataset) : []
                    });

                    // Debug: log the actual HTML attribute values
                    if (contentDiv) {
                        console.log('[WindowManager] Raw data attributes:', {
                            'data-cta-action-type': contentDiv.getAttribute('data-cta-action-type'),
                            'data-cta-action-value': contentDiv.getAttribute('data-cta-action-value'),
                            'data-cta-section-id': contentDiv.getAttribute('data-cta-section-id')
                        });
                    }

                    // Build sectionParams object (only include non-empty values)
                    const sectionParams = {};
                    if (heroImage && heroImage.trim() !== '') sectionParams.hero_image = heroImage;
                    if (heroImageMobile && heroImageMobile.trim() !== '') sectionParams.hero_image_mobile = heroImageMobile;
                    // Always include slogan if it exists (even if empty string, to override default)
                    if (slogan !== null && slogan !== undefined) {
                        sectionParams.slogan = slogan;
                        console.log('[WindowManager] Adding slogan to sectionParams:', slogan, '(length:', slogan.length, ')');
                    } else {
                        console.log('[WindowManager] Slogan not found or is null/undefined');
                    }
                    if (ctaText && ctaText.trim() !== '') sectionParams.cta_text = ctaText;
                    if (ctaActionType && ctaActionType.trim() !== '') sectionParams.cta_action_type = ctaActionType;
                    if (ctaActionValue && ctaActionValue.trim() !== '') sectionParams.cta_action_value = ctaActionValue;
                    if (ctaSectionId && ctaSectionId.trim() !== '') sectionParams.cta_section_id = ctaSectionId;
                    if (galleryHint && galleryHint.trim() !== '') sectionParams.gallery_hint = galleryHint;
                    if (textAlignment && textAlignment.trim() !== '') sectionParams.text_alignment = textAlignment;
                    if (textPosition && textPosition.trim() !== '') sectionParams.text_position = textPosition;
                    if (overlayOpacity && overlayOpacity.trim() !== '') sectionParams.overlay_opacity = overlayOpacity;

                    // Build size object - use data attributes if available, otherwise use responsive default
                    const windowSize = {};
                    if (windowWidth) {
                        // Ensure width has units (add px if it's just a number)
                        if (!isNaN(windowWidth) && !windowWidth.includes('px') && !windowWidth.includes('%') && !windowWidth.includes('vw')) {
                            windowSize.width = `${windowWidth}px`;
                        } else {
                            windowSize.width = windowWidth;
                        }
                    } else {
                        // Default to responsive: use viewport width minus padding, max 1000px
                        const desktop = document.getElementById('desktop');
                        const viewportWidth = desktop ? desktop.getBoundingClientRect().width : window.innerWidth;
                        windowSize.width = `${Math.min(1000, viewportWidth - 40)}px`;
                    }

                    if (windowHeight) {
                        // Ensure height has units (add px if it's just a number, but allow 'auto')
                        if (windowHeight !== 'auto' && !isNaN(windowHeight) && !windowHeight.includes('px') && !windowHeight.includes('%') && !windowHeight.includes('vh')) {
                            windowSize.height = `${windowHeight}px`;
                        } else {
                            windowSize.height = windowHeight;
                        }
                    } else {
                        windowSize.height = 'auto';
                    }

                    // Collect data attributes from contentDiv to store on window element
                    const dataAttributes = this.collectDataAttributes(contentDiv);
                    console.log('[WindowManager] Hero banner app dataAttributes collected:', dataAttributes);
                    console.log('[WindowManager] Final sectionParams before openPage:', sectionParams);
                    console.log('[WindowManager] Slogan in sectionParams:', sectionParams.slogan);

                    // Open window using window-hero-banner section, passing content settings
                    // Use auto height so status bar sits right below content
                    return await this.openPage('hero-banner', {
                        title: windowTitle,
                        icon: windowIcon,
                        section: 'window-hero-banner',
                        sectionParams: sectionParams,
                        size: windowSize,
                        canResize: true,
                        canMaximize: true,
                        showMenuBar: showMenuBar,
                        statusText: statusText,
                        dataAttributes: dataAttributes // Pass data attributes to be stored on window element
                    });
                } else {
                    console.warn('WindowManager: No content found for hero-banner-app section', sectionId);
                    // Fallback: try to open hero banner without app section data
                    return await this.openHeroBanner();
                }
            }

            if (sectionType === 'product-app') {
                const appContent = this.getWindowContentFromAppSection(sectionId);
                if (appContent && appContent.productHandle) {
                    // Get data attributes from app section content div
                    const sectionElement = document.querySelector(`[id="shopify-section-${sectionId}"]`);
                    console.log('[WindowManager] product-app sectionElement found:', !!sectionElement, 'sectionId:', sectionId);
                    const contentDiv = sectionElement?.querySelector('.st-product-app-content');
                    console.log('[WindowManager] product-app contentDiv found:', !!contentDiv);
                    if (contentDiv) {
                        console.log('[WindowManager] product-app contentDiv attributes:', {
                            'data-width': contentDiv.getAttribute('data-width'),
                            'data-height': contentDiv.getAttribute('data-height'),
                            'data-left': contentDiv.getAttribute('data-left'),
                            'data-top': contentDiv.getAttribute('data-top'),
                            'data-width-mobile': contentDiv.getAttribute('data-width-mobile'),
                            'data-height-mobile': contentDiv.getAttribute('data-height-mobile'),
                            'data-left-mobile': contentDiv.getAttribute('data-left-mobile'),
                            'data-top-mobile': contentDiv.getAttribute('data-top-mobile')
                        });
                    }
                    const dataAttributes = contentDiv ? this.collectDataAttributes(contentDiv) : {};
                    console.log('[WindowManager] product-app dataAttributes collected:', dataAttributes);

                    // Check if the actual product window is already open or opening
                    const productWindowId = `product-${appContent.productHandle}`;
                    const existingProductWindow = this.windows.get(productWindowId);
                    if (existingProductWindow && existingProductWindow.element) {
                        this.bringToFront(productWindowId);
                        if (existingProductWindow.state === 'minimized') {
                            this.restore(productWindowId);
                        }
                        return existingProductWindow.element;
                    }
                    // Check if product window is currently being opened
                    if (this.openingWindows.has(productWindowId)) {
                        console.log(`[WindowManager] Product window ${productWindowId} is already being opened from app window`);
                        await new Promise(resolve => setTimeout(resolve, 100));
                        const retryWindow = this.windows.get(productWindowId);
                        if (retryWindow && retryWindow.element) {
                            this.bringToFront(productWindowId);
                            if (retryWindow.state === 'minimized') {
                                this.restore(productWindowId);
                            }
                            return retryWindow.element;
                        }
                    }
                    return await this.openProduct(appContent.productHandle, {
                        dataAttributes: dataAttributes
                    });
                }
            }

            // Special handling for media-player-app
            if (sectionType === 'media-player-app') {
                const sectionElement = document.querySelector(`[id="shopify-section-${sectionId}"]`);
                const contentDiv = sectionElement?.querySelector('.st-media-player-app-content');
                if (contentDiv) {
                    // Get window settings from data attributes
                    const windowTitle = contentDiv.dataset.windowTitle || 'Media Player';
                    const windowIcon = contentDiv.dataset.windowIcon || null;

                    // Read data-width and data-height attributes (check mobile first if on mobile)
                    const isMobile = this.isMobile();
                    let windowWidth = this.getWindowSizeAttribute(contentDiv, isMobile, 'width');
                    let windowHeight = this.getWindowSizeAttribute(contentDiv, isMobile, 'height');

                    // Build size object - use data attributes if available, otherwise use compact defaults for audio
                    const windowSize = {};
                    if (windowWidth) {
                        windowSize.width = windowWidth;
                    } else {
                        // Default to compact size for audio player
                        windowSize.width = '320px';
                    }

                    if (windowHeight) {
                        windowSize.height = windowHeight;
                    } else {
                        // Default to compact size for audio player
                        windowSize.height = 'auto';
                    }

                    // Get the content HTML from the hidden div
                    const contentHtml = contentDiv.innerHTML;

                    // Collect data attributes from contentDiv to store on window element
                    const dataAttributes = this.collectDataAttributes(contentDiv);
                    console.log('[WindowManager] Media player app dataAttributes collected:', dataAttributes);

                    // Open window with the content
                    return await this.open(windowId, {
                        title: windowTitle,
                        icon: windowIcon,
                        content: contentHtml,
                        size: windowSize,
                        canResize: true,
                        canMaximize: true,
                        dataAttributes: dataAttributes
                    });
                } else {
                    console.warn('WindowManager: No content found for media-player-app section', sectionId);
                }
            }

            // Special handling for richtext-app
            if (sectionType === 'richtext-app') {
                const sectionElement = document.querySelector(`[id="shopify-section-${sectionId}"]`);
                const contentDiv = sectionElement?.querySelector('.st-richtext-app-content');
                if (contentDiv) {
                    // Get window settings from data attributes
                    const windowTitle = contentDiv.dataset.windowTitle || 'Information';
                    const windowIcon = contentDiv.dataset.windowIcon || null;
                    // showMenuBar is stored as string "true" or "false", convert to boolean
                    const showMenuBar = contentDiv.dataset.showMenuBar === 'true';
                    const statusText = contentDiv.dataset.statusText || '';

                    // Read data-width and data-height attributes (check mobile first if on mobile)
                    const isMobile = this.isMobile();
                    let windowWidth = this.getWindowSizeAttribute(contentDiv, isMobile, 'width');
                    let windowHeight = this.getWindowSizeAttribute(contentDiv, isMobile, 'height');

                    // Get menu bar items from data attributes
                    // Data attributes are kebab-case: data-menu-item1-text becomes dataset.menuItem1Text
                    const menuItems = [];
                    for (let i = 1; i <= 5; i++) {
                        const labelKey = `menuItem${i}Text`; // data-menu-item1-text -> menuItem1Text
                        const urlKey = `menuItem${i}Url`; // data-menu-item1-url -> menuItem1Url
                        const actionKey = `menuItem${i}Action`; // data-menu-item1-action -> menuItem1Action
                        const label = contentDiv.dataset[labelKey];
                        const url = contentDiv.dataset[urlKey] || '';
                        const action = contentDiv.dataset[actionKey] || '';

                        if (label && label.trim()) {
                            menuItems.push({
                                label: label,
                                url: url,
                                action: action
                            });
                        }
                    }

                    console.log('[WindowManager] Menu items parsed:', menuItems);

                    console.log('[WindowManager] Richtext app settings:', {
                        windowTitle,
                        showMenuBar,
                        showMenuBarRaw: contentDiv.dataset.showMenuBar,
                        statusText,
                        menuItemsCount: menuItems.length
                    });

                    // Get content settings from app section
                    const heading = contentDiv.dataset.heading;
                    // Get HTML content - it's HTML-encoded in the data attribute, so we need to decode it
                    const contentHtmlEncoded = contentDiv.getAttribute('data-content');
                    // Decode HTML entities (Liquid auto-escapes HTML in data attributes)
                    let contentHtml = null;
                    if (contentHtmlEncoded) {
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = contentHtmlEncoded;
                        contentHtml = tempDiv.textContent ? tempDiv.innerHTML : contentHtmlEncoded;
                    }
                    const topImage = contentDiv.getAttribute('data-top-image');
                    const topImageAlt = contentDiv.getAttribute('data-top-image-alt');
                    const bottomImage = contentDiv.getAttribute('data-bottom-image');
                    const bottomImageAlt = contentDiv.getAttribute('data-bottom-image-alt');
                    const ctaText = contentDiv.dataset.ctaText;
                    const ctaActionType = contentDiv.getAttribute('data-cta-action-type');
                    const ctaActionValue = contentDiv.getAttribute('data-cta-action-value');
                    const ctaSectionId = contentDiv.getAttribute('data-cta-section-id');

                    console.log('[WindowManager] Richtext app content from data attributes:', {
                        heading,
                        contentHtmlExists: !!contentHtml,
                        contentHtmlLength: contentHtml ? contentHtml.length : 0,
                        contentHtmlPreview: contentHtml ? contentHtml.substring(0, 200) + '...' : null,
                        ctaText,
                        ctaActionType,
                        showMenuBar,
                        statusText,
                        allDataAttributes: Array.from(contentDiv.attributes).map(attr => attr.name)
                    });

                    // Build sectionParams object
                    const sectionParams = {};
                    if (heading && heading.trim() !== '') sectionParams.heading = heading;
                    // Always include content if it exists (already decoded above)
                    if (contentHtml && contentHtml.trim() !== '') {
                        sectionParams.content = contentHtml;
                        console.log('[WindowManager] Content added to sectionParams, length:', contentHtml.length);
                    }
                    // Include images if they exist
                    if (topImage && topImage.trim() !== '') {
                        sectionParams.top_image = topImage;
                        if (topImageAlt && topImageAlt.trim() !== '') {
                            sectionParams.top_image_alt = topImageAlt;
                        }
                    }
                    if (bottomImage && bottomImage.trim() !== '') {
                        sectionParams.bottom_image = bottomImage;
                        if (bottomImageAlt && bottomImageAlt.trim() !== '') {
                            sectionParams.bottom_image_alt = bottomImageAlt;
                        }
                    }
                    // Only include CTA if there's both text and an action configured
                    const hasCtaAction = (ctaActionType && ctaActionType.trim() !== '') ||
                        (ctaActionValue && ctaActionValue.trim() !== '') ||
                        (ctaSectionId && ctaSectionId.trim() !== '');
                    if (ctaText && ctaText.trim() !== '' && hasCtaAction) {
                        sectionParams.cta_text = ctaText;
                        if (ctaActionType && ctaActionType.trim() !== '') sectionParams.cta_action_type = ctaActionType;
                        if (ctaActionValue && ctaActionValue.trim() !== '') sectionParams.cta_action_value = ctaActionValue;
                        if (ctaSectionId && ctaSectionId.trim() !== '') sectionParams.cta_section_id = ctaSectionId;
                    }
                    // Pass show_menu_bar so the section can output the correct data attribute
                    sectionParams.show_menu_bar = showMenuBar ? 'true' : 'false';

                    console.log('[WindowManager] Final sectionParams:', {
                        hasHeading: !!sectionParams.heading,
                        hasContent: !!sectionParams.content,
                        contentLength: sectionParams.content ? sectionParams.content.length : 0,
                        contentPreview: sectionParams.content ? sectionParams.content.substring(0, 100) : null,
                        showMenuBar: showMenuBar
                    });

                    // Build size object - use data attributes if available, otherwise use defaults
                    const windowSize = {};
                    if (windowWidth) {
                        windowSize.width = windowWidth;
                    } else {
                        windowSize.width = '800px';
                    }

                    if (windowHeight) {
                        windowSize.height = windowHeight;
                    } else {
                        windowSize.height = '600px';
                    }

                    // Collect data attributes from contentDiv to store on window element
                    const dataAttributes = this.collectDataAttributes(contentDiv);
                    console.log('[WindowManager] Richtext app dataAttributes collected:', dataAttributes);

                    // Open window using window-richtext section
                    return await this.openPage('richtext', {
                        title: windowTitle,
                        icon: windowIcon,
                        section: 'window-richtext',
                        sectionParams: sectionParams,
                        size: windowSize,
                        canResize: true,
                        canMaximize: true,
                        showMenuBar: showMenuBar,
                        menuItems: menuItems.length > 0 ? menuItems : undefined,
                        statusText: statusText,
                        dataAttributes: dataAttributes // Pass data attributes to be stored on window element
                    });
                } else {
                    console.warn('WindowManager: No content found for richtext-app section', sectionId);
                }
            }

            // Get content from app section
            const appContent = this.getWindowContentFromAppSection(sectionId);
            if (!appContent) {
                console.warn(`WindowManager: Could not find content for section ${sectionId}`);
                // Fallback to section rendering API
                return await this.openPage(sectionType.replace('-app', ''), {
                    section: sectionType,
                    size: { width: '900px', height: '700px' },
                    canResize: true,
                    canMaximize: true
                });
            }

            // Get icon from desktop icon if available
            const desktopIcon = document.querySelector(`[data-section-id="${sectionId}"]`);
            const iconUrl = desktopIcon ? desktopIcon.querySelector('img')?.src : appContent.icon;

            return await this.open(windowId, {
                title: appContent.title,
                icon: iconUrl,
                content: appContent.content,
                size: { width: '900px', height: '700px' },
                canResize: true,
                canMaximize: true
            });
        }

        /**
         * Open hero banner window
         */
        async openHeroBanner(blockId = null) {
            // Try to get content from block template first
            if (blockId) {
                const blockContent = this.getWindowContentFromBlock(blockId);
                if (blockContent) {
                    const windowId = `hero-banner-${blockId}`;

                    // Build responsive size - use viewport width minus padding, max 1000px
                    const desktop = document.getElementById('desktop');
                    const viewportWidth = desktop ? desktop.getBoundingClientRect().width : window.innerWidth;
                    const responsiveWidth = `${Math.min(1000, viewportWidth - 40)}px`;

                    return await this.open(windowId, {
                        title: blockContent.title,
                        icon: blockContent.icon,
                        content: blockContent.content,
                        size: { width: responsiveWidth, height: '800px' },
                        canResize: true,
                        canMaximize: true
                    });
                }
            }

            // Fallback to section rendering API
            // First, load the section to get its settings
            const sectionUrl = `/?section_id=window-hero-banner`;
            let windowWidth = null;
            let windowHeight = null;

            try {
                const response = await fetch(sectionUrl, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'text/html'
                    }
                });

                if (response.ok) {
                    const html = await response.text();
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    const sectionContent = doc.getElementById('shopify-section-window-hero-banner');

                    let windowTitle = "Where The '90S Died and The Party Started";
                    let statusText = "Smash or Pass? For Smash, press F1";
                    let showMenuBar = false;

                    if (sectionContent) {
                        const contentElement = sectionContent.querySelector('[data-window-title]');
                        if (contentElement) {
                            windowTitle = contentElement.dataset.windowTitle || windowTitle;
                            statusText = contentElement.dataset.statusText || statusText;
                            showMenuBar = contentElement.dataset.showMenuBar === 'true';

                            // Read data-width and data-height attributes
                            if (contentElement.dataset.width) {
                                windowWidth = contentElement.dataset.width;
                            }
                            if (contentElement.dataset.height) {
                                windowHeight = contentElement.dataset.height;
                            }
                        }

                        // Also check the section element itself for data-width and data-height
                        if (sectionContent.dataset.width && !windowWidth) {
                            windowWidth = sectionContent.dataset.width;
                        }
                        if (sectionContent.dataset.height && !windowHeight) {
                            windowHeight = sectionContent.dataset.height;
                        }
                    }

                    // Build size object - use data attributes if available, otherwise use responsive default
                    const windowSize = {};
                    if (windowWidth) {
                        windowSize.width = windowWidth;
                    } else {
                        // Default to responsive: use viewport width minus padding, max 1000px
                        const desktop = document.getElementById('desktop');
                        const viewportWidth = desktop ? desktop.getBoundingClientRect().width : window.innerWidth;
                        windowSize.width = `${Math.min(1000, viewportWidth - 40)}px`;
                    }

                    if (windowHeight) {
                        windowSize.height = windowHeight;
                    } else {
                        windowSize.height = '800px';
                    }

                    return await this.openPage('hero-banner', {
                        title: windowTitle,
                        section: 'window-hero-banner',
                        size: windowSize,
                        canResize: true,
                        canMaximize: true,
                        showMenuBar: showMenuBar,
                        statusText: statusText
                    });
                }
            } catch (error) {
                console.warn('WindowManager: Could not load hero banner section settings:', error);
            }

            // Fallback if section loading fails - use responsive default
            const desktop = document.getElementById('desktop');
            const viewportWidth = desktop ? desktop.getBoundingClientRect().width : window.innerWidth;
            const fallbackWidth = `${Math.min(1000, viewportWidth - 40)}px`;

            return await this.openPage('hero-banner', {
                title: "Where The '90S Died and The Party Started",
                section: 'window-hero-banner',
                size: { width: fallbackWidth, height: 'auto' },
                canResize: true,
                canMaximize: true,
                showMenuBar: false,
                statusText: "Smash or Pass? For Smash, press F1"
            });
        }

        /**
         * Open about window
         */
        async openAbout(blockId = null) {
            // Try to get content from block template first
            if (blockId) {
                const blockContent = this.getWindowContentFromBlock(blockId);
                if (blockContent) {
                    const windowId = `about-${blockId}`;
                    return await this.open(windowId, {
                        title: blockContent.title,
                        icon: blockContent.icon,
                        content: blockContent.content,
                        size: { width: '800px', height: '700px' },
                        canResize: true,
                        canMaximize: true
                    });
                }
            }

            // Fallback to section rendering API
            return await this.openPage('about', {
                title: "From Slams to Jams",
                section: 'window-about',
                size: { width: '800px', height: '700px' },
                canResize: true,
                canMaximize: true
            });
        }

        /**
         * Open rich text window
         */
        async openRichText(pageHandle, options = {}) {
            const blockId = options.blockId || pageHandle;

            // Try to get content from block template first
            if (blockId) {
                const blockContent = this.getWindowContentFromBlock(blockId);
                if (blockContent) {
                    const windowId = `richtext-${blockId}`;
                    return await this.open(windowId, {
                        title: blockContent.title || options.title || 'Information',
                        icon: blockContent.icon || options.icon,
                        content: blockContent.content,
                        size: options.size || { width: '600px', height: '500px' },
                        canResize: true,
                        canMaximize: true
                    });
                }
            }

            // Fallback to section rendering API
            return await this.openPage(pageHandle || 'richtext', {
                title: options.title || 'Information',
                section: 'window-richtext',
                size: options.size || { width: '600px', height: '500px' },
                canResize: true,
                canMaximize: true
            });
        }

        /**
         * Open media player window
         */
        async openMediaPlayer(options = {}) {
            const blockId = options.blockId;

            // Try to get content from block template first
            if (blockId) {
                const blockContent = this.getWindowContentFromBlock(blockId);
                if (blockContent) {
                    const windowId = `media-player-${blockId}`;
                    return await this.open(windowId, {
                        title: blockContent.title || options.title || 'Media Player',
                        icon: blockContent.icon || options.icon,
                        content: blockContent.content,
                        size: options.size || { width: '600px', height: '500px' },
                        canResize: true,
                        canMaximize: true
                    });
                }
            }

            // Fallback to section rendering API
            return await this.openPage('media-player', {
                title: options.title || 'Media Player',
                section: 'window-media-player',
                sectionParams: options.sectionParams || {},
                size: options.size || { width: '600px', height: '500px' },
                canResize: true,
                canMaximize: true
            });
        }

        /**
         * Close a window
         */
        close(windowId) {
            console.log('[WindowManager] close() called for window:', windowId);

            // Remove from opening set if it's there (cleanup)
            this.openingWindows.delete(windowId);

            const windowData = this.windows.get(windowId);
            if (!windowData) {
                console.warn('[WindowManager] Window not found in registry:', windowId);
                return;
            }

            const element = windowData.element;
            if (element) {
                console.log('[WindowManager] Window element found, cleaning up');
                // Clean up event listeners
                this.cleanupWindowListeners(element);

                // Clean up window shell
                if (element.windowShell && typeof element.windowShell.destroy === 'function') {
                    console.log('[WindowManager] Destroying window shell');
                    element.windowShell.destroy();
                }

                // Trigger close event
                element.dispatchEvent(new CustomEvent('window:close', {
                    detail: { windowId }
                }));

                // Remove from taskbar
                if (window.smashOS && window.smashOS.desktopManager) {
                    console.log('[WindowManager] Removing from taskbar');
                    window.smashOS.desktopManager.removeWindowFromTaskbar(windowId);
                }

                // Mark as closing (don't add class again if already added by windowShell)
                if (!element.classList.contains('st-window--closing')) {
                    element.classList.add('st-window--closing');
                }
                console.log('[WindowManager] Window marked as closing, will remove from DOM in 150ms');
                console.log('[WindowManager] Window element before removal:', element);
                console.log('[WindowManager] Window element parent:', element.parentElement);

                // Store reference to element for removal
                const elementToRemove = element;

                setTimeout(() => {
                    console.log('[WindowManager] Removing window element from DOM');
                    console.log('[WindowManager] Window element still exists?', document.contains(elementToRemove));
                    console.log('[WindowManager] Window element parent:', elementToRemove?.parentElement);
                    if (elementToRemove && elementToRemove.parentElement) {
                        elementToRemove.remove();
                        console.log('[WindowManager] Window element removed, still in DOM?', document.contains(elementToRemove));
                    } else {
                        console.warn('[WindowManager] Window element or parent not found, cannot remove');
                        console.warn('[WindowManager] Element:', elementToRemove);
                        console.warn('[WindowManager] Parent:', elementToRemove?.parentElement);
                    }
                }, 150); // Match CSS transition duration
            }

            // Remove from registry AFTER scheduling DOM removal
            console.log('[WindowManager] Removing window from registry');
            this.windows.delete(windowId);

            // Update active window
            if (this.activeWindow === windowId) {
                console.log('[WindowManager] This was the active window, clearing active window');
                this.activeWindow = null;
                // Bring next window to front if available
                const remainingWindows = Array.from(this.windows.values())
                    .filter(w => w.state === 'open' && !w.isMaximized)
                    .sort((a, b) => b.zIndex - a.zIndex);
                if (remainingWindows.length > 0) {
                    console.log('[WindowManager] Bringing next window to front:', remainingWindows[0].id);
                    this.bringToFront(remainingWindows[0].id);
                }
            }
            console.log('[WindowManager] close() completed for window:', windowId);
        }

        /**
         * Close all open windows
         */
        closeAllWindows() {
            console.log('[WindowManager] closeAllWindows() called');

            // Get all window IDs before closing (to avoid iteration issues)
            const windowIds = Array.from(this.windows.keys());

            // Close each window
            windowIds.forEach(windowId => {
                this.close(windowId);
            });

            // Clear active window
            this.activeWindow = null;

            console.log('[WindowManager] All windows closed');
        }

        /**
         * Minimize all open windows
         */
        minimizeAllWindows() {
            console.log('[WindowManager] minimizeAllWindows() called');

            // Get all window IDs that are not already minimized
            const windowIds = Array.from(this.windows.keys()).filter(windowId => {
                const windowData = this.windows.get(windowId);
                return windowData && windowData.state !== 'minimized';
            });

            // Minimize each window
            windowIds.forEach(windowId => {
                this.minimize(windowId);
            });

            // Clear active window
            this.activeWindow = null;

            console.log('[WindowManager] All windows minimized');
        }

        /**
         * Clean up window event listeners
         */
        cleanupWindowListeners(element) {
            if (element._windowListeners) {
                element._windowListeners.forEach(({ type, handler }) => {
                    element.removeEventListener(type, handler);
                });
                element._windowListeners = [];
            }
        }

        /**
         * Minimize a window
         */
        minimize(windowId) {
            const windowData = this.windows.get(windowId);
            if (!windowData) return;

            const element = windowData.element;
            if (!element) return;

            // Store current position and size before minimizing
            const rect = element.getBoundingClientRect();
            windowData.previousState = {
                position: { x: rect.left, y: rect.top },
                size: { width: rect.width, height: rect.height }
            };

            // Add minimize class for animation
            element.classList.add('st-window--minimizing');

            // Use window shell minimize if available (handles animation)
            if (element.windowShell && typeof element.windowShell.minimize === 'function') {
                element.windowShell.minimize();
            } else {
                // Fallback: direct minimize
                setTimeout(() => {
                    element.classList.remove('st-window--minimizing');
                    element.classList.add('st-window--minimized');
                    windowData.state = 'minimized';

                    // Update taskbar
                    if (window.smashOS && window.smashOS.desktopManager) {
                        window.smashOS.desktopManager.updateWindowTaskbarState(windowId, false);
                    }
                }, 150);
            }

            // Update state immediately
            windowData.state = 'minimized';

            // Update taskbar
            if (window.smashOS && window.smashOS.desktopManager) {
                window.smashOS.desktopManager.updateWindowTaskbarState(windowId, false);
            }
        }

        /**
         * Maximize a window
         */
        maximize(windowId) {
            const windowData = this.windows.get(windowId);
            if (!windowData) return;

            const element = windowData.element;
            if (!element) return;

            // Store previous state before maximizing
            const rect = element.getBoundingClientRect();
            windowData.previousState = {
                position: { x: parseInt(element.style.left) || rect.left, y: parseInt(element.style.top) || rect.top },
                size: { width: element.style.width || `${rect.width}px`, height: element.style.height || `${rect.height}px` }
            };

            // Update position and size in registry
            windowData.position = { x: rect.left, y: rect.top };
            windowData.size = { width: rect.width, height: rect.height };

            // Use window shell maximize if available
            if (element.windowShell && typeof element.windowShell.maximize === 'function') {
                element.windowShell.maximize();
            } else {
                // Fallback: direct maximize
                const desktop = document.getElementById('desktop');
                const taskbar = document.getElementById('taskbar');
                const taskbarHeight = taskbar ? taskbar.offsetHeight : 0;

                element.style.left = '0';
                element.style.top = '0';
                element.style.width = '100%';
                element.style.height = `calc(100% - ${taskbarHeight}px)`;
                element.classList.add('st-window--maximized');
            }

            windowData.isMaximized = true;
            windowData.state = 'maximized';

            // Update z-index to ensure maximized window is on top
            this.bringToFront(windowId);
        }

        /**
         * Restore window from minimized or maximized state
         */
        restore(windowId) {
            const windowData = this.windows.get(windowId);
            if (!windowData) return;

            const element = windowData.element;
            if (!element) return;

            // Use window shell restore if available
            if (element.windowShell && typeof element.windowShell.restore === 'function') {
                element.windowShell.restore();
            } else {
                // Fallback: restore from previous state
                if (windowData.previousState) {
                    if (windowData.previousState.position) {
                        element.style.left = typeof windowData.previousState.position.x === 'string'
                            ? windowData.previousState.position.x
                            : `${windowData.previousState.position.x}px`;
                        element.style.top = typeof windowData.previousState.position.y === 'string'
                            ? windowData.previousState.position.y
                            : `${windowData.previousState.position.y}px`;
                    }
                    if (windowData.previousState.size) {
                        element.style.width = windowData.previousState.size.width;
                        element.style.height = windowData.previousState.size.height;
                    }
                }
                element.classList.remove('st-window--maximized', 'st-window--minimized', 'st-window--minimizing');
            }

            windowData.isMaximized = false;
            windowData.state = 'open';

            // Update taskbar
            if (window.smashOS && window.smashOS.desktopManager) {
                window.smashOS.desktopManager.updateWindowTaskbarState(windowId, true);
            }

            // Bring to front
            this.bringToFront(windowId);
        }

        /**
         * Bring window to front
         */
        bringToFront(windowId) {
            console.log('[WindowManager] bringToFront() called for window:', windowId);

            const windowData = this.windows.get(windowId);
            if (!windowData) {
                console.warn('[WindowManager] Window not found for bringToFront:', windowId);
                return;
            }

            const element = windowData.element;
            if (!element) {
                console.warn('[WindowManager] Window element not found for bringToFront:', windowId);
                return;
            }

            // Check if window is closing
            if (element.classList.contains('st-window--closing')) {
                console.log('[WindowManager] Window is closing, ignoring bringToFront');
                return;
            }

            // Check if window is already removed from registry (double-check)
            if (!this.windows.has(windowId)) {
                console.warn('[WindowManager] Window already removed from registry, ignoring bringToFront:', windowId);
                return;
            }

            // If window is minimized, restore it first
            if (windowData.state === 'minimized') {
                this.restore(windowId);
            }

            // Update z-index (unless window has a fixed z-index)
            if (windowData.fixedZIndex && windowData.zIndex !== undefined) {
                // Keep the fixed z-index, just ensure it's applied
                element.style.zIndex = windowData.zIndex;
            } else {
                // Use auto-incrementing z-index
                windowData.zIndex = this.zIndexCounter++;
                element.style.zIndex = windowData.zIndex;
            }

            // Update active window
            this.activeWindow = windowId;

            // Update taskbar
            if (window.smashOS && window.smashOS.desktopManager) {
                window.smashOS.desktopManager.updateWindowTaskbarState(windowId, true);
            }

            // Focus the window
            element.focus();

            // Update title bar active state
            const titlebar = element.querySelector('.st-window__titlebar');
            if (titlebar) {
                // Remove active state from all windows and add inactive state
                document.querySelectorAll('.st-window__titlebar').forEach(tb => {
                    tb.classList.remove('st-titlebar--active');
                    tb.classList.add('st-titlebar--inactive');
                });
                // Add active state to this window and remove inactive state
                titlebar.classList.add('st-titlebar--active');
                titlebar.classList.remove('st-titlebar--inactive');
            }
        }

        /**
         * Get window data
         */
        getWindowData(windowId) {
            return this.windows.get(windowId) || null;
        }

        /**
         * Update window position
         */
        updatePosition(windowId, position) {
            const windowData = this.windows.get(windowId);
            if (!windowData || !windowData.element) return;

            const element = windowData.element;
            element.style.left = typeof position.x === 'string' ? position.x : `${position.x}px`;
            element.style.top = typeof position.y === 'string' ? position.y : `${position.y}px`;

            windowData.position = { ...position };
        }

        /**
         * Update window size
         */
        updateSize(windowId, size) {
            const windowData = this.windows.get(windowId);
            if (!windowData || !windowData.element) return;

            const element = windowData.element;
            if (size.width) {
                element.style.width = typeof size.width === 'string' ? size.width : `${size.width}px`;
            }
            if (size.height) {
                element.style.height = typeof size.height === 'string' ? size.height : `${size.height}px`;
            }

            windowData.size = { ...size };
        }

        /**
         * Get all open windows
         */
        getAllWindows() {
            return Array.from(this.windows.values());
        }

        /**
         * Get active window
         */
        getActiveWindow() {
            return this.activeWindow ? this.windows.get(this.activeWindow) : null;
        }

        /**
         * Constrain all windows to viewport (prevent overflow)
         * Called when viewport size changes
         */
        constrainAllWindows() {
            const desktop = document.getElementById('desktop');
            if (!desktop) return;

            const desktopRect = desktop.getBoundingClientRect();
            const taskbar = document.getElementById('taskbar');
            const taskbarHeight = taskbar ? taskbar.offsetHeight : parseInt(getComputedStyle(document.documentElement).getPropertyValue('--st-taskbar-height')) || 32;
            const isMobile = this.isMobile();
            const minMargin = isMobile ? 16 : 20; // Minimum spacing from taskbar (same as cart)
            const viewportWidth = desktopRect.width;
            const viewportHeight = desktopRect.height - taskbarHeight - (minMargin * 2); // Top and bottom margins
            const maxBottom = desktopRect.height - taskbarHeight - minMargin; // Maximum bottom position

            this.windows.forEach((windowData, windowId) => {
                const element = windowData.element;
                if (!element || element.classList.contains('st-window--maximized')) return;

                // Check if this is a hero banner window (special top margin on mobile)
                const isHeroBanner = windowId.includes('hero-banner') || element.getAttribute('data-section-name') === 'window-hero-banner';
                const windowMinMarginTop = (isMobile && isHeroBanner) ? 200 : (isMobile ? 16 : 20); // 200px for hero banner on mobile, 16px for others on mobile, 20px on desktop
                const windowViewportHeight = desktopRect.height - taskbarHeight - windowMinMarginTop - minMargin; // Top and bottom margins

                // Check if window is centered (using transform or percentage positioning)
                const isCentered = (element.style.transform && element.style.transform.includes('translate(-50%, -50%)')) ||
                    (element.style.left && element.style.left.includes('%') && element.style.top && element.style.top.includes('%')) ||
                    (windowData.position && windowData.position.centered) ||
                    element.dataset.centered === 'true';

                if (isCentered) {
                    // Re-center the window on resize
                    element.style.position = 'fixed';
                    element.style.left = '50%';
                    element.style.top = '50%';
                    element.style.transform = 'translate(-50%, -50%)';
                    element.style.margin = '0';

                    // Constrain size only (not position)
                    const rect = element.getBoundingClientRect();
                    let newWidth = rect.width;
                    let newHeight = rect.height;
                    let needsSizeUpdate = false;

                    // Constrain width to viewport
                    if (newWidth > viewportWidth) {
                        newWidth = viewportWidth;
                        needsSizeUpdate = true;
                    }

                    // Constrain height to viewport (use window-specific viewport height)
                    const centeredWindowViewportHeight = desktopRect.height - taskbarHeight - windowMinMarginTop - minMargin;
                    if (newHeight > centeredWindowViewportHeight) {
                        newHeight = centeredWindowViewportHeight;
                        needsSizeUpdate = true;
                    }

                    if (needsSizeUpdate) {
                        element.style.width = `${newWidth}px`;
                        element.style.height = `${newHeight}px`;
                    }

                    return; // Skip position constraint for centered windows
                }

                const rect = element.getBoundingClientRect();
                let needsUpdate = false;
                let newX = parseInt(element.style.left) || rect.left;
                let newY = parseInt(element.style.top) || rect.top;
                let newWidth = rect.width;
                let newHeight = rect.height;

                // Constrain width to viewport
                if (newWidth > viewportWidth) {
                    newWidth = viewportWidth;
                    needsUpdate = true;
                }

                // Constrain height to viewport (use window-specific viewport height - already calculated above)
                if (newHeight > windowViewportHeight) {
                    newHeight = windowViewportHeight;
                    needsUpdate = true;
                }

                // Constrain position to viewport with minimum margins
                if (newX + newWidth > viewportWidth) {
                    newX = Math.max(minMargin, viewportWidth - newWidth);
                    needsUpdate = true;
                }
                if (newX < minMargin) {
                    newX = minMargin;
                    needsUpdate = true;
                }

                // Constrain vertical position - ensure minimum spacing from top and bottom (use window-specific top margin)
                const windowBottom = newY + newHeight;
                if (windowBottom > maxBottom) {
                    newY = Math.max(windowMinMarginTop, maxBottom - newHeight);
                    needsUpdate = true;
                }
                if (newY < windowMinMarginTop) {
                    newY = windowMinMarginTop;
                    needsUpdate = true;
                }

                if (needsUpdate) {
                    element.style.width = `${newWidth}px`;
                    element.style.height = `${newHeight}px`;
                    element.style.left = `${newX}px`;
                    element.style.top = `${newY}px`;

                    // Update window data
                    windowData.position = { x: newX, y: newY };
                    windowData.size = { width: newWidth, height: newHeight };
                }
            });
        }

        /**
         * Setup event handlers for collection window
         * Uses event delegation on the window body to persist across content changes
         */
        setupCollectionWindowHandlers(windowElement, collectionHandle) {
            const body = windowElement.querySelector('.st-window__body');
            if (!body) return;

            // Remove existing listeners if any (using a data attribute to track)
            if (body.dataset.collectionHandlersAttached === 'true') {
                // Handlers are already attached via event delegation, no need to reattach
                return;
            }

            // Track touch events for mobile (store on body element)
            const touchData = new Map();
            // A tap that moves more than this many px is a scroll/drag, not a tap.
            const TAP_MOVE_MAX = 12;
            // Suppress activation while the grid is (momentum-)scrolling, and block
            // the synthetic click a scroll-stopping tap can spawn.
            let lastScrollAt = 0;
            let suppressClickUntil = 0;
            body.addEventListener('scroll', () => { lastScrollAt = Date.now(); }, true);
            const isScrolling = () => (Date.now() - lastScrollAt) < 300;

            // Product tile clicks - use event delegation
            body.addEventListener('click', (e) => {
                const productTile = e.target.closest('.st-collection-window__product');
                if (!productTile) return;

                // Ignore the synthetic click that follows a scroll-stopping tap.
                if (Date.now() < suppressClickUntil) { suppressClickUntil = 0; return; }

                // Don't open product if clicking on color swatch
                if (e.target.closest('.st-collection-window__product-color')) {
                    return;
                }

                e.preventDefault();
                e.stopPropagation();
                const productHandle = productTile.dataset.productHandle;
                if (productHandle && this.openProduct) {
                    console.log('[WindowManager] Product tile clicked:', productHandle);
                    const dataAttributes = this.getProductDataAttributes(productHandle);
                    this.openProduct(productHandle, { dataAttributes: dataAttributes });
                }
            });

            // Product tile touch events for mobile - use event delegation
            body.addEventListener('touchstart', (e) => {
                const productTile = e.target.closest('.st-collection-window__product');
                if (!productTile) return;

                const productHandle = productTile.dataset.productHandle;
                if (productHandle) {
                    const t = e.touches[0];
                    touchData.set(productHandle, {
                        time: Date.now(),
                        target: e.target,
                        x: t ? t.clientX : 0,
                        y: t ? t.clientY : 0,
                        scrolledAtStart: isScrolling() // touch began during momentum scroll
                    });
                }
            }, { passive: true });

            body.addEventListener('touchend', (e) => {
                const productTile = e.target.closest('.st-collection-window__product');
                if (!productTile) return;

                const productHandle = productTile.dataset.productHandle;
                if (!productHandle) return;

                const touchInfo = touchData.get(productHandle);
                if (touchInfo) {
                    const touchDuration = Date.now() - touchInfo.time;
                    const t = e.changedTouches[0];
                    const moved = t
                        ? Math.hypot(t.clientX - touchInfo.x, t.clientY - touchInfo.y)
                        : 0;
                    // A real tap: quick, barely moved, and NOT while the grid was
                    // scrolling (a tap that stops momentum must not open a product).
                    const scrolled = touchInfo.scrolledAtStart || isScrolling();
                    const isTap = touchDuration < 300 && moved < TAP_MOVE_MAX && !scrolled;
                    if (isTap) {
                        // Don't open product if tapping on color swatch
                        if (e.target.closest('.st-collection-window__product-color')) {
                            touchData.delete(productHandle);
                            return;
                        }
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('[WindowManager] Product tile tapped:', productHandle);
                        if (this.openProduct) {
                            const dataAttributes = this.getProductDataAttributes(productHandle);
                            this.openProduct(productHandle, { dataAttributes: dataAttributes });
                        }
                    } else {
                        // Scroll / flick / momentum-stop — block its synthetic click.
                        suppressClickUntil = Date.now() + 500;
                    }
                    touchData.delete(productHandle);
                }
            }, { passive: false });

            // Product tile keyboard events
            body.addEventListener('keydown', (e) => {
                const productTile = e.target.closest('.st-collection-window__product');
                if (!productTile) return;

                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    const productHandle = productTile.dataset.productHandle;
                    if (productHandle && this.openProduct) {
                        const dataAttributes = this.getProductDataAttributes(productHandle);
                        this.openProduct(productHandle, { dataAttributes: dataAttributes });
                    }
                }
            });

            // Color swatch clicks - use event delegation
            body.addEventListener('click', (e) => {
                const colorSwatch = e.target.closest('.st-collection-window__product-color');
                if (!colorSwatch) return;

                // Ignore the synthetic click that follows a scroll-stopping tap.
                if (Date.now() < suppressClickUntil) { suppressClickUntil = 0; return; }

                e.preventDefault();
                e.stopPropagation();
                const productHandle = colorSwatch.dataset.productHandle;
                if (productHandle && this.openProduct) {
                    const dataAttributes = this.getProductDataAttributes(productHandle);
                    this.openProduct(productHandle, { dataAttributes: dataAttributes });
                }
            });

            // Color swatch touch events for mobile - use event delegation
            const swatchTouchData = new Map();
            body.addEventListener('touchstart', (e) => {
                const colorSwatch = e.target.closest('.st-collection-window__product-color');
                if (!colorSwatch) return;

                const productHandle = colorSwatch.dataset.productHandle;
                if (productHandle) {
                    const t = e.touches[0];
                    swatchTouchData.set(productHandle, {
                        time: Date.now(),
                        target: e.target,
                        x: t ? t.clientX : 0,
                        y: t ? t.clientY : 0,
                        scrolledAtStart: isScrolling()
                    });
                }
            }, { passive: true });

            body.addEventListener('touchend', (e) => {
                const colorSwatch = e.target.closest('.st-collection-window__product-color');
                if (!colorSwatch) return;

                const productHandle = colorSwatch.dataset.productHandle;
                if (!productHandle) return;

                const touchInfo = swatchTouchData.get(productHandle);
                if (touchInfo) {
                    const touchDuration = Date.now() - touchInfo.time;
                    const t = e.changedTouches[0];
                    const moved = t
                        ? Math.hypot(t.clientX - touchInfo.x, t.clientY - touchInfo.y)
                        : 0;
                    const scrolled = touchInfo.scrolledAtStart || isScrolling();
                    if (touchDuration < 300 && moved < TAP_MOVE_MAX && !scrolled) {
                        e.preventDefault();
                        e.stopPropagation();
                        if (this.openProduct) {
                            const dataAttributes = this.getProductDataAttributes(productHandle);
                            this.openProduct(productHandle, { dataAttributes: dataAttributes });
                        }
                    } else {
                        suppressClickUntil = Date.now() + 500;
                    }
                    swatchTouchData.delete(productHandle);
                }
            }, { passive: false });

            // Mark handlers as attached
            body.dataset.collectionHandlersAttached = 'true';
        }

        /**
         * Load product data via Shopify API
         */
        async loadProductData(container, productHandle, windowElement, options = {}) {
            if (!productHandle) return;

            try {
                // Fetch from custom JSON template
                const url = `/products/${productHandle}?view=json`;
                const response = await fetch(url, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    cache: 'no-store',
                    redirect: 'follow'
                });

                if (response.ok) {
                    // Get raw text - Shopify wraps JSON template in HTML
                    const text = await response.text();

                    // Extract JSON from HTML body or parse directly if it's pure JSON
                    let jsonText = text;
                    const bodyMatch = text.match(/<body[^>]*>([\s\S]*)<\/body>/i);
                    if (bodyMatch) {
                        jsonText = bodyMatch[1].trim();
                    } else if (text.trim().startsWith('{')) {
                        // Pure JSON response
                        jsonText = text.trim();
                    }

                    let product;
                    try {
                        product = JSON.parse(jsonText);
                    } catch (parseError) {
                        console.warn('WindowManager: Failed to parse JSON, falling back to .js endpoint:', parseError);
                        // Fall back to Shopify .js endpoint
                        const jsResponse = await fetch(`/products/${productHandle}.js`, {
                            headers: {
                                'Accept': 'application/json'
                            }
                        });
                        if (jsResponse.ok) {
                            product = await jsResponse.json();
                        } else {
                            throw new Error('Failed to fetch product');
                        }
                    }

                    // Update window title
                    if (windowElement && product.title) {
                        const titleElement = windowElement.querySelector('.st-titlebar__title');
                        if (titleElement) {
                            titleElement.textContent = product.title;
                        }
                    }

                    // Update address bar with breadcrumb path
                    if (windowElement && product.collections && product.collections.length > 0) {
                        const addressBar = windowElement.querySelector('.st-window__addressbar');
                        if (addressBar) {
                            const addressSelect = addressBar.querySelector('.st-addressbar__select');
                            if (addressSelect) {
                                // Build breadcrumb path: Desktop/Collection/Product.txt
                                const collection = product.collections[0]; // Use first collection
                                const collectionTitle = collection.title || collection.handle;
                                const path = `Desktop/${collectionTitle}/${product.handle}.txt`;
                                addressSelect.innerHTML = `<option value="${this.escapeHtml(path)}">${this.escapeHtml(path)}</option>`;
                            }
                        }
                    }

                    // Render product content
                    this.renderProductContent(container, product, {
                        ...options,
                        productHandle: productHandle
                    });

                    // Setup handlers after rendering
                    setTimeout(() => {
                        this.setupProductWindowEventHandlers(windowElement || container.closest('.st-window'));
                    }, 100);
                } else {
                    console.warn('WindowManager: Failed to fetch product JSON:', response.status, response.statusText);
                    container.innerHTML = '<div class="st-product-window__empty"><p>Product not found.</p></div>';
                }
            } catch (error) {
                console.error('Error loading product:', error);
                container.innerHTML = '<div class="st-product-window__empty"><p>Error loading product. Please try again.</p></div>';
            }
        }

        /**
         * Render product content from JSON data
         */
        renderProductContent(container, product, options = {}) {
            if (!product || !container) return;

            // Build product HTML with three-pane layout
            let html = '<div class="st-product-window__main">';

            // Left pane: Image slideshow with thumbnails
            html += '<div class="st-product-window__pane st-product-window__pane--left">';
            html += '<div class="st-product-window__images">';

            const featuredImg = product.featured_image ? (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src || product.featured_image) : null;
            const images = product.images || (featuredImg ? [featuredImg] : []);

            if (images.length > 0) {
                const mainImageUrl = typeof images[0] === 'string' ? images[0] : (images[0].src || images[0]);
                html += `<div class="st-product-window__main-image">
          <img src="${this.escapeHtml(mainImageUrl)}" alt="${this.escapeHtml(product.title)}" id="product-main-image" loading="eager">
        </div>`;

                // Thumbnails at bottom (always show, even if only one image)
                html += '<div class="st-product-window__thumbnails">';
                images.forEach((image, index) => {
                    const imgUrl = typeof image === 'string' ? image : (image.src || image);
                    const thumbUrl = imgUrl.replace(/(\.(jpg|png|gif|webp|jpeg))(\?.*)?$/i, '_150x150$1');
                    html += `<button type="button" class="st-product-window__thumbnail ${index === 0 ? 'st-product-window__thumbnail--active' : ''}" 
               data-image-url="${this.escapeHtml(imgUrl)}" aria-label="View ${this.escapeHtml(product.title)} image">
          <img src="${this.escapeHtml(thumbUrl)}" alt="${this.escapeHtml(product.title)}" loading="lazy">
        </button>`;
                });
                html += '</div>';
            }
            html += '</div>'; // End images
            html += '</div>'; // End left pane

            // Middle pane: Zoom controls
            html += '<div class="st-product-window__pane st-product-window__pane--middle">';
            html += '<div class="st-product-window__zoom">';
            html += '<button type="button" class="st-product-window__zoom-btn st-product-window__zoom-btn--in" aria-label="Zoom in" id="product-zoom-in">🔍+</button>';
            html += '<button type="button" class="st-product-window__zoom-btn st-product-window__zoom-btn--out" aria-label="Zoom out" id="product-zoom-out">🔍-</button>';
            html += '</div>';
            html += '</div>'; // End middle pane

            // Right pane: Product info
            html += '<div class="st-product-window__pane st-product-window__pane--right">';
            html += '<div class="st-product-window__info">';

            // Title
            html += `<h1 class="st-product-window__title">${this.escapeHtml(product.title)}</h1>`;

            // Short description (metafield)
            if (product.short_description) {
                html += `<div class="st-product-window__short-description">${this.escapeHtml(product.short_description)}</div>`;
            }

            // Price
            html += '<div class="st-product-window__price">';
            html += `<span class="st-product-window__price-current">${this.formatMoney(product.price)}</span>`;
            if (product.compare_at_price && product.compare_at_price > product.price) {
                html += `<span class="st-product-window__price-compare">${this.formatMoney(product.compare_at_price)}</span>`;
            }
            html += '</div>';

            // Variant Selector (Form - always visible, positioned between price and description)
            html += `<form class="st-product-window__form" id="product-form-${product.id}" data-product-id="${product.id}">`;

            // Color swatches from related_products metafield
            if (product.related_products && product.related_products.length > 0) {
                html += '<div class="st-product-window__colors">';
                html += '<label class="st-product-window__colors-label">Color:</label>';
                html += '<div class="st-product-window__color-swatches">';
                product.related_products.forEach((relatedProduct, index) => {
                    const color = relatedProduct.color || '';
                    const colorHex = color ? this.colorNameToHex(color) : '#cccccc';
                    const isCurrentProduct = relatedProduct.handle === product.handle;
                    html += `<button type="button" class="st-product-window__color-swatch ${isCurrentProduct ? 'st-product-window__color-swatch--active' : ''}" 
               style="background-color: ${colorHex};" 
               data-product-handle="${this.escapeHtml(relatedProduct.handle)}"
               data-color="${this.escapeHtml(color)}" 
               title="${this.escapeHtml(color || relatedProduct.title)}" 
               aria-label="${this.escapeHtml(color || relatedProduct.title)}"></button>`;
                });
                html += '</div>';
                html += '</div>';
            }

            // Size selector (if size option exists)
            if (product.options && product.variants && product.variants.length > 1) {
                // Find size option
                let sizeOption = null;
                let sizeOptionIndex = -1;

                product.options.forEach((opt, index) => {
                    const optName = typeof opt === 'string' ? opt : (opt.name || opt);
                    if (optName.toLowerCase().includes('size') && !sizeOption) {
                        sizeOption = opt;
                        sizeOptionIndex = index;
                    }
                });

                if (sizeOption && sizeOptionIndex >= 0) {
                    const optionName = typeof sizeOption === 'string' ? sizeOption : (sizeOption.name || sizeOption);
                    const sizeValues = typeof sizeOption === 'object' && sizeOption.values
                        ? sizeOption.values
                        : [...new Set(product.variants.map(v => {
                            const optValue = v[`option${sizeOptionIndex + 1}`];
                            return optValue || '';
                        }).filter(v => v))];

                    html += '<div class="st-product-window__size">';
                    html += `<label class="st-product-window__size-label">Size:</label>`;
                    html += '<div class="st-product-window__size-buttons">';
                    sizeValues.forEach((value, index) => {
                        html += `<button type="button" class="st-product-window__size-button st-button ${index === 0 ? 'st-product-window__size-button--active' : ''}" 
               data-size="${this.escapeHtml(value)}" 
               data-option-name="${this.escapeHtml(optionName)}"
               data-option-index="${sizeOptionIndex}"
               name="options[${this.escapeHtml(optionName)}]"
               value="${this.escapeHtml(value)}">${this.escapeHtml(value)}</button>`;
                    });
                    html += '</div>';
                    html += '</div>';
                }
            }

            // Hidden variant ID input (will be updated based on selections)
            if (product.variants && product.variants.length > 0) {
                html += `<input type="hidden" name="id" value="${product.variants[0].id}" id="product-variant-id-${product.id}">`;
            }

            // Quantity selector (Win95 style with +/- buttons)
            html += '<div class="st-product-window__quantity">';
            html += `<label class="st-product-window__quantity-label" for="quantity-${product.id}">Quantity:</label>`;
            html += '<div class="st-product-window__quantity-controls">';
            html += `<button type="button" class="st-product-window__quantity-btn st-product-window__quantity-btn--decrease" aria-label="Decrease quantity" data-quantity-target="quantity-${product.id}">−</button>`;
            html += `<input type="number" 
               id="quantity-${product.id}"
               name="quantity" 
               value="1" 
               min="1" 
               class="st-input st-product-window__quantity-input"
               readonly>`;
            html += `<button type="button" class="st-product-window__quantity-btn st-product-window__quantity-btn--increase" aria-label="Increase quantity" data-quantity-target="quantity-${product.id}">+</button>`;
            html += '</div>';
            html += '</div>';

            // Add to Cart Button (full width)
            html += `<button type="submit" class="st-button st-button--primary st-product-window__add-to-cart" 
             ${!product.available ? 'disabled' : ''}>
      ${product.available ? 'Add to Cart' : 'Sold Out'}
    </button>`;

            html += `<div class="st-product-window__error" id="product-error-${product.id}" style="display: none;"></div>`;
            html += '</form>';

            // Description (scrollable, after form)
            if (product.description) {
                html += `<div class="st-product-window__description">${product.description}</div>`;
            }

            html += '</div>'; // End info
            html += '</div>'; // End right pane
            html += '</div>'; // End main

            container.innerHTML = html;

            // Store product data on window element for variant selection
            const windowElement = container.closest('.st-window');
            if (windowElement) {
                windowElement.dataset.productData = JSON.stringify(product);
            }
        }

        /**
         * Format money (simple version - could be enhanced)
         */
        formatMoney(cents) {
            return '$' + (cents / 100).toFixed(2);
        }

        /**
         * Escape HTML to prevent XSS
         */
        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }

        /**
         * Setup event handlers for product window
         */
        setupProductWindowHandlers(windowElement, productHandle) {
            // Check if product needs to be loaded via JavaScript
            const productContainer = windowElement.querySelector('.st-product-window[data-product-handle]');
            const body = windowElement.querySelector('.st-window__body');
            const container = productContainer || body;

            if (container && (container.querySelector('.st-product-window__loading') || !container.querySelector('.st-product-window__main'))) {
                // Load product data via JSON template
                this.loadProductData(container, productHandle, windowElement).then(() => {
                    // Setup handlers after product is loaded
                    this.setupProductWindowEventHandlers(windowElement);
                });
                return;
            }

            // Setup handlers if product is already loaded
            this.setupProductWindowEventHandlers(windowElement);
        }

        /**
         * Setup event handlers for product window (after product is loaded)
         */
        setupProductWindowEventHandlers(windowElement) {
            // Image thumbnail clicks
            const thumbnails = windowElement.querySelectorAll('.st-product-window__thumbnail');
            const mainImage = windowElement.querySelector('#product-main-image');
            const mainImageContainer = mainImage ? mainImage.closest('.st-product-window__main-image') : null;

            thumbnails.forEach(thumbnail => {
                thumbnail.addEventListener('click', () => {
                    const imageUrl = thumbnail.dataset.imageUrl;
                    if (imageUrl && mainImage) {
                        mainImage.src = imageUrl;
                        // Reset zoom when changing images
                        mainImage.style.transform = 'scale(1.0)';
                        // Update active state
                        thumbnails.forEach(t => t.classList.remove('st-product-window__thumbnail--active'));
                        thumbnail.classList.add('st-product-window__thumbnail--active');
                    }
                });
            });

            // Zoom in/out buttons (incremental zoom) with drag support
            const zoomInBtn = windowElement.querySelector('#product-zoom-in');
            const zoomOutBtn = windowElement.querySelector('#product-zoom-out');

            if (mainImage && mainImageContainer) {
                let zoomLevel = 1.0;
                const zoomStep = 0.5;
                const minZoom = 1.0;
                const maxZoom = 3.0;
                let isDragging = false;
                let startX = 0;
                let startY = 0;
                let currentX = 0;
                let currentY = 0;

                const updateZoom = () => {
                    if (zoomLevel > 1.0) {
                        mainImage.style.transform = `scale(${zoomLevel}) translate(${currentX}px, ${currentY}px)`;
                        mainImage.style.cursor = 'move';
                        mainImageContainer.style.overflow = 'auto';
                    } else {
                        mainImage.style.transform = 'scale(1.0)';
                        mainImage.style.cursor = 'zoom-in';
                        mainImageContainer.style.overflow = 'hidden';
                        currentX = 0;
                        currentY = 0;
                    }
                    mainImage.style.transformOrigin = 'center center';
                };

                // Mouse drag handlers
                const handleMouseDown = (e) => {
                    if (zoomLevel > 1.0) {
                        isDragging = true;
                        startX = e.clientX - currentX;
                        startY = e.clientY - currentY;
                        mainImage.style.cursor = 'grabbing';
                    }
                };

                const handleMouseMove = (e) => {
                    if (isDragging && zoomLevel > 1.0) {
                        e.preventDefault();
                        currentX = e.clientX - startX;
                        currentY = e.clientY - startY;
                        updateZoom();
                    }
                };

                const handleMouseUp = () => {
                    if (isDragging) {
                        isDragging = false;
                        mainImage.style.cursor = zoomLevel > 1.0 ? 'move' : 'zoom-in';
                    }
                };

                // Add drag event listeners
                mainImage.addEventListener('mousedown', handleMouseDown);
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);

                if (zoomInBtn) {
                    zoomInBtn.addEventListener('click', () => {
                        if (zoomLevel < maxZoom) {
                            zoomLevel = Math.min(zoomLevel + zoomStep, maxZoom);
                            updateZoom();
                        }
                    });
                }

                if (zoomOutBtn) {
                    zoomOutBtn.addEventListener('click', () => {
                        if (zoomLevel > minZoom) {
                            zoomLevel = Math.max(zoomLevel - zoomStep, minZoom);
                            updateZoom();
                        }
                    });
                }
            }

            // Color swatch clicks - navigate to related product
            const colorSwatches = windowElement.querySelectorAll('.st-product-window__color-swatch');
            colorSwatches.forEach(swatch => {
                swatch.addEventListener('click', () => {
                    const productHandle = swatch.dataset.productHandle;
                    if (productHandle && window.smashOS && window.smashOS.windowManager) {
                        // Open the related product window
                        const dataAttributes = window.smashOS.windowManager.getProductDataAttributes(productHandle);
                        window.smashOS.windowManager.openProduct(productHandle, { dataAttributes: dataAttributes });
                    }
                });
            });

            // Form submission (add to cart) - setup first, then size/quantity buttons after form is cloned
            const form = windowElement.querySelector('.st-product-window__form');
            let newForm = form;
            if (form) {
                // Remove existing submit handler to prevent duplicates
                newForm = form.cloneNode(true);
                form.parentNode.replaceChild(newForm, form);

                newForm.addEventListener('submit', async (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const submitButton = newForm.querySelector('button[type="submit"]');
                    const errorDiv = windowElement.querySelector('.st-product-window__error');
                    const quantityInput = newForm.querySelector('input[name="quantity"]');
                    const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

                    // Get selected variant
                    let variantId = null;
                    const hiddenId = newForm.querySelector('input[name="id"]');
                    if (hiddenId) {
                        variantId = hiddenId.value;
                    } else {
                        // Need to find variant based on selected options
                        // For now, use first available variant
                        const productId = newForm.dataset.productId;
                        if (productId) {
                            // This would need product data - simplified for now
                            console.warn('Variant selection not fully implemented');
                        }
                    }

                    if (!variantId) {
                        if (errorDiv) {
                            errorDiv.textContent = 'Please select a variant';
                            errorDiv.style.display = 'block';
                        }
                        return;
                    }

                    // Disable button and show loading state
                    if (submitButton) {
                        submitButton.disabled = true;
                        submitButton.textContent = 'Adding...';
                        submitButton.style.color = 'var(--st-text-inverse)';
                        submitButton.style.opacity = '1';
                    }

                    // Add to cart via Shopify Ajax API
                    try {
                        const response = await fetch('/cart/add.js', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                id: variantId,
                                quantity: quantity
                            })
                        });

                        if (response.ok) {
                            const data = await response.json();
                            // Update cart count
                            if (window.smashOS && window.smashOS.desktopManager) {
                                window.smashOS.desktopManager.updateCartCount();
                            }
                            // Open cart window (only if not already opening)
                            if (this.openCart && !this.openingCart) {
                                this.openingCart = true;
                                try {
                                    await this.openCart();
                                } finally {
                                    this.openingCart = false;
                                }
                            }
                            // Show success
                            if (submitButton) {
                                submitButton.textContent = 'Added!';
                                setTimeout(() => {
                                    submitButton.textContent = 'Add to Cart';
                                    submitButton.disabled = false;
                                    submitButton.style.color = '';
                                    submitButton.style.opacity = '';
                                }, 2000);
                            }
                            if (errorDiv) {
                                errorDiv.style.display = 'none';
                            }
                        } else {
                            throw new Error('Failed to add to cart');
                        }
                    } catch (error) {
                        console.error('Error adding to cart:', error);
                        if (errorDiv) {
                            errorDiv.textContent = 'Failed to add to cart. Please try again.';
                            errorDiv.style.display = 'block';
                        }
                        if (submitButton) {
                            submitButton.disabled = false;
                            submitButton.textContent = 'Add to Cart';
                            submitButton.style.color = '';
                            submitButton.style.opacity = '';
                        }
                    }
                });
            }

            // Size button clicks - setup AFTER form is cloned to ensure handlers persist
            const sizeButtons = newForm.querySelectorAll('.st-product-window__size-button'); // Query from newForm
            const productId = newForm.dataset.productId;
            const hiddenVariantInput = newForm.querySelector('input[name="id"]');

            // Get product data from window element (stored during render)
            const productData = windowElement.dataset.productData ? JSON.parse(windowElement.dataset.productData) : null;

            sizeButtons.forEach(button => {
                // Add both click and touchstart for mobile support
                const handleSizeSelect = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Update active state
                    sizeButtons.forEach(b => b.classList.remove('st-product-window__size-button--active'));
                    button.classList.add('st-product-window__size-button--active');

                    // Update variant ID based on selected size
                    if (productData && productData.variants && hiddenVariantInput) {
                        const selectedSize = button.dataset.size;
                        const optionIndex = parseInt(button.dataset.optionIndex, 10);
                        const optionName = `option${optionIndex + 1}`;

                        // Find variant matching the selected size
                        const matchingVariant = productData.variants.find(variant => {
                            return variant[optionName] === selectedSize;
                        });

                        if (matchingVariant && matchingVariant.id) {
                            hiddenVariantInput.value = matchingVariant.id;
                            console.log('[WindowManager] Variant updated to:', matchingVariant.id, 'for size:', selectedSize);
                        }
                    }
                };
                button.addEventListener('click', handleSizeSelect);
                button.addEventListener('touchstart', handleSizeSelect, { passive: false });
            });

            // Quantity control buttons - setup AFTER form is cloned to ensure handlers persist
            const quantityDecreaseBtns = newForm.querySelectorAll('.st-product-window__quantity-btn--decrease');
            const quantityIncreaseBtns = newForm.querySelectorAll('.st-product-window__quantity-btn--increase');

            quantityDecreaseBtns.forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const targetId = btn.dataset.quantityTarget;
                    const input = newForm.querySelector(`#${targetId}`);
                    if (input) {
                        const currentValue = parseInt(input.value, 10) || 1;
                        const newValue = Math.max(1, currentValue - 1);
                        input.value = String(newValue);
                    }
                });
            });

            quantityIncreaseBtns.forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const targetId = btn.dataset.quantityTarget;
                    const input = newForm.querySelector(`#${targetId}`);
                    if (input) {
                        const currentValue = parseInt(input.value, 10) || 1;
                        const newValue = currentValue + 1;
                        input.value = String(newValue);
                    }
                });
            });
        }

        /**
         * Load collection data via Shopify JSON API
         */
        async loadCollectionData(container, collectionHandle, windowElement, options = {}) {
            if (!collectionHandle) return;

            try {
                // Fetch from custom JSON template
                const url = `/collections/${collectionHandle}?view=json`;
                // Don't set Accept header - let Shopify return the template as-is
                // X-Requested-With helps identify as AJAX request
                const response = await fetch(url, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    cache: 'no-store', // Prevent caching issues
                    redirect: 'follow' // Follow redirects
                });

                if (response.ok) {
                    // Get raw text - Shopify wraps JSON template in HTML
                    const text = await response.text();

                    // Extract JSON from HTML body or parse directly if it's pure JSON
                    let jsonText = text;
                    const bodyMatch = text.match(/<body[^>]*>([\s\S]*)<\/body>/i);
                    if (bodyMatch) {
                        jsonText = bodyMatch[1].trim();
                    } else if (text.trim().startsWith('{')) {
                        // Pure JSON response
                        jsonText = text.trim();
                    }

                    let data;
                    try {
                        data = JSON.parse(jsonText);
                    } catch (parseError) {
                        console.warn('WindowManager: Failed to parse JSON, falling back to section rendering API:', parseError);
                        // Fall back to section rendering API
                        const sectionUrl = `/?section_id=window-collection&collection=${collectionHandle}`;
                        const sectionResponse = await fetch(sectionUrl, {
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                                'Accept': 'text/html'
                            }
                        });

                        if (sectionResponse.ok) {
                            const html = await sectionResponse.text();
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(html, 'text/html');
                            const sectionContent = doc.getElementById('shopify-section-window-collection');

                            if (sectionContent) {
                                const collectionWindow = sectionContent.querySelector('.st-collection-window');
                                if (collectionWindow) {
                                    const titleElement = windowElement.querySelector('.st-titlebar__title');
                                    const collectionTitle = sectionContent.querySelector('[data-collection-title]')?.textContent ||
                                        collectionWindow.querySelector('h1, h2')?.textContent;
                                    if (titleElement && collectionTitle) {
                                        titleElement.textContent = collectionTitle;
                                    }

                                    container.innerHTML = collectionWindow.innerHTML;
                                    setTimeout(() => {
                                        this.setupCollectionWindowHandlers(windowElement, collectionHandle);
                                    }, 100);
                                    return; // Success
                                }
                            }
                        }
                        throw parseError; // Re-throw if section API also fails
                    }

                    // Shopify wraps the response in {collection: {...}}, unwrap it
                    let collection = data;
                    if (data.collection) {
                        collection = data.collection;

                        // If unwrapped collection doesn't have products, the template might not be used
                        // Fall back to section rendering API
                        if (!collection.products && collection.products_count > 0) {
                            // Use section rendering API which has products via Liquid
                            const sectionUrl = `/?section_id=window-collection&collection=${collectionHandle}`;
                            const sectionResponse = await fetch(sectionUrl, {
                                headers: {
                                    'X-Requested-With': 'XMLHttpRequest',
                                    'Accept': 'text/html'
                                }
                            });

                            if (sectionResponse.ok) {
                                const html = await sectionResponse.text();
                                const parser = new DOMParser();
                                const doc = parser.parseFromString(html, 'text/html');
                                const sectionContent = doc.getElementById('shopify-section-window-collection');

                                if (sectionContent) {
                                    const collectionWindow = sectionContent.querySelector('.st-collection-window');
                                    if (collectionWindow) {
                                        // Update window title
                                        const titleElement = windowElement.querySelector('.st-titlebar__title');
                                        if (titleElement && collection.title) {
                                            titleElement.textContent = collection.title;
                                        }

                                        container.innerHTML = collectionWindow.innerHTML;
                                        setTimeout(() => {
                                            this.setupCollectionWindowHandlers(windowElement, collectionHandle);
                                        }, 100);
                                        return; // Success
                                    }
                                }
                            }
                        }

                        // Try accessing the original data structure
                        if (!collection.products && data.products) {
                            collection = data;
                        }
                    } else if (data.products) {
                        // Response is already the collection object directly (from JSON template)
                        collection = data;
                    }

                    if (collection && collection.products && Array.isArray(collection.products) && collection.products.length > 0) {
                        // Update window title with collection title
                        const titleElement = windowElement.querySelector('.st-titlebar__title');
                        if (titleElement && collection.title) {
                            titleElement.textContent = collection.title;
                        }

                        // Render collection content with options
                        this.renderCollectionContent(container, collection, {
                            ...options,
                            collectionHandle: collectionHandle
                        });

                        // Setup handlers after rendering
                        setTimeout(() => {
                            this.setupCollectionWindowHandlers(windowElement, collectionHandle);
                        }, 100);
                    } else {
                        console.warn('WindowManager: Collection JSON invalid or no products:', collection);
                        container.innerHTML = '<div class="st-collection-window__empty"><p>No products found in this collection.</p></div>';
                    }
                } else {
                    console.warn('WindowManager: Failed to fetch collection JSON:', response.status, response.statusText);
                    container.innerHTML = '<div class="st-collection-window__empty"><p>Collection not found.</p></div>';
                }
            } catch (error) {
                console.error('Error loading collection JSON:', error);
                container.innerHTML = '<div class="st-collection-window__empty"><p>Error loading collection. Please try again.</p></div>';
            }
        }

        /**
         * Get collections list from theme settings
         */
        getCollectionsList() {
            // Get theme settings from global theme settings object or DOM
            const themeSettings = window.smashOS?.themeSettings || {};
            let collectionsList = themeSettings.collections_list || [];
            const listTitle = themeSettings.collections_list_title || 'Smash Tennis';

            // collection_list returns an array - could be handles (strings) or objects
            // Normalize to objects with handle and title
            const normalizedCollections = collectionsList.map(item => {
                if (typeof item === 'string') {
                    // It's a handle, return object with handle
                    return { handle: item, title: item };
                } else if (item && item.handle) {
                    // It's already an object with handle
                    return { handle: item.handle, title: item.title || item.handle };
                } else {
                    // Fallback
                    return { handle: String(item), title: String(item) };
                }
            });

            // If no collections in settings, try to get from Shopify collections
            if (normalizedCollections.length === 0 && window.Shopify && window.Shopify.collections) {
                const shopifyCollections = Object.values(window.Shopify.collections).slice(0, 10);
                return {
                    title: listTitle,
                    collections: shopifyCollections.map(c => ({
                        handle: c.handle || c,
                        title: c.title || c.handle || c
                    }))
                };
            }

            return {
                title: listTitle,
                collections: normalizedCollections
            };
        }

        /**
         * Render collections list (left pane)
         */
        renderCollectionsList(container, currentCollectionHandle) {
            const collectionsData = this.getCollectionsList();
            const collections = collectionsData.collections || [];

            // Check if mobile (matches CSS breakpoint)
            const isMobile = window.innerWidth <= 767;
            const rootItemClasses = isMobile
                ? 'st-collection-window__tree-item st-collection-window__tree-item--root st-collection-window__tree-item--collapsed'
                : 'st-collection-window__tree-item st-collection-window__tree-item--root';

            let html = '<div class="st-collection-window__sidebar st-scrollbar">';
            html += `<div class="st-collection-window__tree">`;
            html += `<div class="${rootItemClasses}">`;
            html += `<span class="st-collection-window__tree-icon">🎾</span>`;
            html += `<span class="st-collection-window__tree-label">${this.escapeHtml(collectionsData.title)}</span>`;
            html += `<span class="st-collection-window__tree-toggle" role="button" tabindex="0" aria-label="Toggle tree"></span>`;
            html += `</div>`;
            html += `<div class="st-collection-window__tree-children">`;

            collections.forEach(collection => {
                const handle = collection.handle || collection;
                const title = collection.title || handle;
                const isActive = handle === currentCollectionHandle;
                html += `<div class="st-collection-window__tree-item ${isActive ? 'st-collection-window__tree-item--active' : ''}" data-collection-handle="${this.escapeHtml(handle)}" role="button" tabindex="0">`;
                html += `<span class="st-collection-window__tree-icon">${this.getCollectionIcon(handle)}</span>`;
                html += `<span class="st-collection-window__tree-label">${this.escapeHtml(title)}</span>`;
                html += `</div>`;
            });

            html += `</div></div></div>`;
            container.innerHTML = html;

            // Setup tree toggle handler - make entire root item clickable
            const rootItem = container.querySelector('.st-collection-window__tree-item--root');
            if (rootItem) {
                // Helper to update tree state based on mobile threshold
                const updateTreeForMobile = () => {
                    const isMobile = window.innerWidth <= 767;
                    if (isMobile) {
                        rootItem.classList.add('st-collection-window__tree-item--collapsed');
                    } else {
                        // On desktop, expand the tree
                        rootItem.classList.remove('st-collection-window__tree-item--collapsed');
                    }
                };

                let tapStartTime = 0;
                const TAP_THRESHOLD = 300; // ms

                const handleToggle = (e) => {
                    // For touch events, only trigger if it's a quick tap
                    if (e.type === 'touchend' && (Date.now() - tapStartTime > TAP_THRESHOLD || tapStartTime === 0)) {
                        return;
                    }
                    e.preventDefault();
                    e.stopPropagation();

                    const isMobile = window.innerWidth <= 767;
                    if (isMobile) {
                        // On mobile, always collapse after toggle (toggle to expand, but auto-collapse on resize)
                        rootItem.classList.toggle('st-collection-window__tree-item--collapsed');
                    } else {
                        // On desktop, allow free toggling
                        rootItem.classList.toggle('st-collection-window__tree-item--collapsed');
                    }
                    console.log('[WindowManager] Tree toggled, collapsed:', rootItem.classList.contains('st-collection-window__tree-item--collapsed'));
                };

                const handleTapStart = () => {
                    tapStartTime = Date.now();
                };

                // Make entire root item clickable/touchable
                rootItem.addEventListener('touchstart', handleTapStart, { passive: true });
                rootItem.addEventListener('touchend', handleToggle, { passive: false });
                rootItem.addEventListener('click', handleToggle);
                rootItem.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle(e);
                    }
                });
                // Make root item appear clickable
                rootItem.style.cursor = 'pointer';

                // Add resize listener to collapse when crossing mobile threshold
                const handleResize = () => {
                    updateTreeForMobile();
                };
                window.addEventListener('resize', handleResize);
                // Store cleanup function
                if (!rootItem._resizeHandler) {
                    rootItem._resizeHandler = handleResize;
                }
            }

            // Setup collection list click handlers
            const treeItems = container.querySelectorAll('.st-collection-window__tree-item[data-collection-handle]');
            treeItems.forEach(item => {
                item.addEventListener('click', (e) => {
                    // Don't trigger if clicking on toggle button
                    if (e.target.closest('.st-collection-window__tree-toggle')) {
                        return;
                    }
                    const handle = item.dataset.collectionHandle;
                    if (handle && handle !== currentCollectionHandle) {
                        this.openCollection(handle);
                    }
                });
            });
        }

        /**
         * Get icon for collection based on handle
         */
        getCollectionIcon(handle) {
            const iconMap = {
                'tees': '👕',
                'socks': '🧦',
                'caps': '🧢',
                'all': '📦'
            };
            return iconMap[handle.toLowerCase()] || '📁';
        }

        /**
         * Render collection content from JSON data
         */
        renderCollectionContent(container, collection, options = {}) {
            if (!collection || !container) return;

            // Get products from collection
            const products = collection.products || [];
            const productsPerRow = options.productsPerRow || 3; // Default to 3
            const currentCollectionHandle = collection.handle || options.collectionHandle;

            // Preload all product images to prevent flickering
            const imagePromises = [];
            products.forEach(product => {
                const productImage = product.featured_image || (product.images && product.images[0]);
                const imageUrl = productImage ? (typeof productImage === 'string' ? productImage : productImage.src) : null;
                if (imageUrl) {
                    const img = new Image();
                    const promise = new Promise((resolve) => {
                        img.onload = resolve;
                        img.onerror = resolve; // Resolve even on error to not block rendering
                        img.src = imageUrl;
                    });
                    imagePromises.push(promise);
                }
            });

            // Wait for all images to start loading, then render
            // If no images, render immediately
            const renderContent = () => {
                // Build collection HTML with two-pane layout
                let html = '<div class="st-collection-window">';
                html += '<div class="st-collection-window__panes">';

                // Left pane: Collections list
                html += '<div class="st-collection-window__pane st-collection-window__pane--left st-scrollbar">';
                const collectionsData = this.getCollectionsList();
                const collections = collectionsData.collections || [];

                // Check if mobile (matches CSS breakpoint)
                const isMobile = window.innerWidth <= 767;
                const rootItemClasses = isMobile
                    ? 'st-collection-window__tree-item st-collection-window__tree-item--root st-collection-window__tree-item--collapsed'
                    : 'st-collection-window__tree-item st-collection-window__tree-item--root';

                html += `<div class="st-collection-window__tree">`;
                html += `<div class="${rootItemClasses}">`;
                html += `<span class="st-collection-window__tree-icon">🎾</span>`;
                html += `<span class="st-collection-window__tree-label">${this.escapeHtml(collectionsData.title)}</span>`;
                html += `<span class="st-collection-window__tree-toggle" role="button" tabindex="0" aria-label="Toggle tree"></span>`;
                html += `</div>`;
                html += `<div class="st-collection-window__tree-children">`;

                collections.forEach(collectionItem => {
                    // Handle both string handles and objects
                    const handle = typeof collectionItem === 'string' ? collectionItem : (collectionItem.handle || collectionItem);
                    const title = typeof collectionItem === 'string' ? collectionItem : (collectionItem.title || collectionItem.handle || collectionItem);
                    const isActive = handle === currentCollectionHandle;
                    html += `<div class="st-collection-window__tree-item ${isActive ? 'st-collection-window__tree-item--active' : ''}" data-collection-handle="${this.escapeHtml(handle)}" role="button" tabindex="0">`;
                    html += `<span class="st-collection-window__tree-icon">${this.getCollectionIcon(handle)}</span>`;
                    html += `<span class="st-collection-window__tree-label">${this.escapeHtml(title)}</span>`;
                    html += `</div>`;
                });

                html += `</div></div></div>`;

                // Right pane: Products grid
                html += '<div class="st-collection-window__pane st-collection-window__pane--right st-scrollbar">';

                if (products.length > 0) {
                    html += `<div class="st-collection-window__grid" data-products-per-row="${productsPerRow}">`;

                    products.forEach(product => {
                        const productImage = product.featured_image || (product.images && product.images[0]);
                        const imageUrl = productImage ? (typeof productImage === 'string' ? productImage : productImage.src) : null;
                        const imageAlt = productImage && typeof productImage === 'object' ? (productImage.alt || product.title) : product.title;
                        const shortDescription = product.short_description || '';

                        html += `<div class="st-collection-window__product" data-product-handle="${this.escapeHtml(product.handle)}" role="button" tabindex="0" aria-label="View ${this.escapeHtml(product.title)}">`;

                        if (imageUrl) {
                            html += `<div class="st-collection-window__product-image">
            <img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(imageAlt)}" width="400" height="400" loading="eager">
          </div>`;
                        } else {
                            html += `<div class="st-collection-window__product-image" style="background: var(--st-bg-dark);"></div>`;
                        }

                        html += `<div class="st-collection-window__product-info">
          <h3 class="st-collection-window__product-title">${this.escapeHtml(product.title)}</h3>`;

                        if (shortDescription) {
                            html += `<p class="st-collection-window__product-description">${this.escapeHtml(shortDescription)}</p>`;
                        }

                        html += `<p class="st-collection-window__product-price">`;

                        if (product.price) {
                            html += this.formatMoney(product.price);
                        }

                        if (product.compare_at_price && product.compare_at_price > product.price) {
                            html += ` <span class="st-collection-window__product-price-compare">${this.formatMoney(product.compare_at_price)}</span>`;
                        }

                        html += `</p>`;

                        // Color swatches from related_products metafield
                        if (product.related_products && product.related_products.length > 0) {
                            html += `<div class="st-collection-window__product-colors">`;
                            product.related_products.forEach(relatedProduct => {
                                const color = relatedProduct.color || '';
                                const colorHex = color ? this.colorNameToHex(color) : '#cccccc';
                                html += `<span class="st-collection-window__product-color" 
                    style="background-color: ${colorHex};" 
                    data-product-handle="${this.escapeHtml(relatedProduct.handle)}"
                    title="${this.escapeHtml(color || relatedProduct.title)}" 
                    aria-label="${this.escapeHtml(color || relatedProduct.title)}"></span>`;
                            });
                            html += `</div>`;
                        }

                        html += `</div></div>`;
                    });

                    html += '</div>';
                } else {
                    html += '<div class="st-collection-window__empty"><p>No products found in this collection.</p></div>';
                }

                html += '</div>'; // End right pane
                html += '</div>'; // End panes
                html += '</div>'; // End collection window

                container.innerHTML = html;

                // Initialize scrollbar states for collection panes
                setTimeout(() => {
                    const leftPane = container.querySelector('.st-collection-window__pane--left');
                    const rightPane = container.querySelector('.st-collection-window__pane--right');

                    if (leftPane) {
                        this.updateScrollbarStates(leftPane);
                        leftPane.addEventListener('scroll', () => {
                            this.updateScrollbarStates(leftPane);
                        });
                        const leftResizeObserver = new ResizeObserver(() => {
                            this.updateScrollbarStates(leftPane);
                        });
                        leftResizeObserver.observe(leftPane);
                    }

                    if (rightPane) {
                        this.updateScrollbarStates(rightPane);
                        rightPane.addEventListener('scroll', () => {
                            this.updateScrollbarStates(rightPane);
                        });
                        const rightResizeObserver = new ResizeObserver(() => {
                            this.updateScrollbarStates(rightPane);
                        });
                        rightResizeObserver.observe(rightPane);
                    }
                }, 0);

                // Update status bar with product count
                const windowElement = container.closest('.st-window');
                if (windowElement) {
                    const statusText = windowElement.querySelector('.st-statusbar__text');
                    if (statusText) {
                        statusText.textContent = `${products.length} Product${products.length !== 1 ? 's' : ''}`;
                    }
                }

                // Setup tree toggle handler - make entire root item clickable
                const rootItem = container.querySelector('.st-collection-window__tree-item--root');
                if (rootItem) {
                    // Helper to update tree state based on mobile threshold
                    const updateTreeForMobile = () => {
                        const isMobile = window.innerWidth <= 767;
                        if (isMobile) {
                            rootItem.classList.add('st-collection-window__tree-item--collapsed');
                        } else {
                            // On desktop, expand the tree
                            rootItem.classList.remove('st-collection-window__tree-item--collapsed');
                        }
                    };

                    let tapStartTime = 0;
                    const TAP_THRESHOLD = 300; // ms

                    const handleToggle = (e) => {
                        // For touch events, only trigger if it's a quick tap
                        if (e.type === 'touchend' && (Date.now() - tapStartTime > TAP_THRESHOLD || tapStartTime === 0)) {
                            return;
                        }
                        e.preventDefault();
                        e.stopPropagation();

                        const isMobile = window.innerWidth <= 767;
                        if (isMobile) {
                            // On mobile, always collapse after toggle (toggle to expand, but auto-collapse on resize)
                            rootItem.classList.toggle('st-collection-window__tree-item--collapsed');
                        } else {
                            // On desktop, allow free toggling
                            rootItem.classList.toggle('st-collection-window__tree-item--collapsed');
                        }
                        console.log('[WindowManager] Tree toggled, collapsed:', rootItem.classList.contains('st-collection-window__tree-item--collapsed'));
                    };

                    const handleTapStart = () => {
                        tapStartTime = Date.now();
                    };

                    // Make entire root item clickable/touchable
                    rootItem.addEventListener('touchstart', handleTapStart, { passive: true });
                    rootItem.addEventListener('touchend', handleToggle, { passive: false });
                    rootItem.addEventListener('click', handleToggle);
                    rootItem.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggle(e);
                        }
                    });
                    // Make root item appear clickable
                    rootItem.style.cursor = 'pointer';

                    // Add resize listener to collapse when crossing mobile threshold
                    const handleResize = () => {
                        updateTreeForMobile();
                    };
                    window.addEventListener('resize', handleResize);
                    // Store cleanup function
                    if (!rootItem._resizeHandler) {
                        rootItem._resizeHandler = handleResize;
                    }
                }

                // Setup collection list click handlers
                const treeItems = container.querySelectorAll('.st-collection-window__tree-item[data-collection-handle]');
                treeItems.forEach(item => {
                    item.addEventListener('click', (e) => {
                        // Don't trigger if clicking on toggle button
                        if (e.target.closest('.st-collection-window__tree-toggle')) {
                            return;
                        }
                        const handle = item.dataset.collectionHandle;
                        if (handle && handle !== currentCollectionHandle) {
                            this.openCollection(handle, { productsPerRow: productsPerRow });
                        }
                    });
                });

                // Ensure all images are visible (opacity: 1)
                const images = container.querySelectorAll('.st-collection-window__product-image img');
                images.forEach(img => {
                    // Always set opacity to 1 immediately
                    img.style.opacity = '1';
                    img.style.transition = 'opacity 0.2s ease-in';

                    // Also handle load/error events to ensure opacity stays at 1
                    if (!img.complete) {
                        img.addEventListener('load', () => {
                            img.style.opacity = '1';
                        }, { once: true });
                        img.addEventListener('error', () => {
                            img.style.opacity = '1'; // Show even if error
                        }, { once: true });
                    }
                });
            };

            // If we have images to preload, wait for them; otherwise render immediately
            if (imagePromises.length > 0) {
                Promise.all(imagePromises).then(renderContent).catch(renderContent);
            } else {
                renderContent();
            }
        }

        /**
         * Convert color name to hex (simplified - can be enhanced)
         */
        colorNameToHex(color) {
            if (!color) return '#808080';
            const colorLower = color.toLowerCase().trim();

            // Common color mappings
            const colorMap = {
                'yellow': '#ffff00',
                'neon yellow': '#ffff00',
                'purple': '#800080',
                'white': '#ffffff',
                'black': '#000000',
                'red': '#ff0000',
                'blue': '#0000ff',
                'green': '#008000',
                'orange': '#ffa500',
                'pink': '#ffc0cb'
            };

            if (colorMap[colorLower]) {
                return colorMap[colorLower];
            }

            // If it's already a hex code, return it
            if (color.startsWith('#')) {
                return color;
            }

            // Default gray
            return '#808080';
        }

        /**
         * Update scrollbar disabled states based on scroll position
         * Adds/removes classes to disable buttons when scrolling isn't possible
         */
        updateScrollbarStates(element) {
            if (!element || !element.classList.contains('st-scrollbar')) {
                return;
            }

            const scrollTop = element.scrollTop;
            const scrollLeft = element.scrollLeft;
            const scrollHeight = element.scrollHeight;
            const scrollWidth = element.scrollWidth;
            const clientHeight = element.clientHeight;
            const clientWidth = element.clientWidth;

            // Vertical scrollbar states
            if (scrollTop <= 0) {
                element.classList.add('scroll-disabled-up');
            } else {
                element.classList.remove('scroll-disabled-up');
            }

            if (scrollTop >= scrollHeight - clientHeight - 1) {
                element.classList.add('scroll-disabled-down');
            } else {
                element.classList.remove('scroll-disabled-down');
            }

            // Horizontal scrollbar states
            if (scrollLeft <= 0) {
                element.classList.add('scroll-disabled-left');
            } else {
                element.classList.remove('scroll-disabled-left');
            }

            if (scrollLeft >= scrollWidth - clientWidth - 1) {
                element.classList.add('scroll-disabled-right');
            } else {
                element.classList.remove('scroll-disabled-right');
            }
        }

        /**
         * Initialize scrollbar state management for all scrollbars
         */
        initScrollbarStates() {
            // Update all existing scrollbars
            document.querySelectorAll('.st-scrollbar').forEach(scrollbar => {
                this.updateScrollbarStates(scrollbar);

                // Add scroll listeners to update states dynamically
                scrollbar.addEventListener('scroll', () => {
                    this.updateScrollbarStates(scrollbar);
                });

                // Also check on resize
                const resizeObserver = new ResizeObserver(() => {
                    this.updateScrollbarStates(scrollbar);
                });
                resizeObserver.observe(scrollbar);
            });
        }

        /**
         * Check for and auto-open windows with display_by_default flag
         * This should be called after desktop is shown
         */
        async openDefaultWindows() {
            // A one-time announcement (the drop update wizard) can ask to show
            // first. While that flag is set, hold off auto-opening default
            // windows (e.g. the hero banner); the wizard calls this again to
            // release them once it's dismissed.
            if (window.__smashDeferDefaultWindows) {
                return;
            }

            // Check all app sections first (they contain desktop icons and window settings)
            // Look for the hidden content divs with data-display-by-default="true"
            const appContentDivs = document.querySelectorAll('[data-section-id][data-display-by-default="true"]');

            for (const contentDiv of appContentDivs) {
                const sectionId = contentDiv.dataset.sectionId;
                // Find the desktop icon that has this section ID
                const desktopIcon = document.querySelector(`.st-desktop-icon[data-section-id="${sectionId}"]`);
                const actionType = desktopIcon?.dataset.actionType;

                if (sectionId && actionType) {
                    try {
                        console.log(`[WindowManager] Opening default window: ${actionType} section ${sectionId}`);
                        // Open the app window
                        await this.openAppWindow(actionType, sectionId);
                    } catch (error) {
                        console.warn(`WindowManager: Could not open default window for ${actionType} section ${sectionId}:`, error);
                    }
                }
            }

            // Also check window sections directly (for backwards compatibility)
            const windowSections = [
                'window-hero-banner',
                'window-about',
                'window-richtext',
                'window-media-player'
            ];

            for (const sectionName of windowSections) {
                try {
                    const sectionUrl = `/?section_id=${sectionName}`;
                    const response = await fetch(sectionUrl, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'text/html'
                        }
                    });

                    if (response.ok) {
                        const html = await response.text();
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(html, 'text/html');
                        const sectionContent = doc.getElementById(`shopify-section-${sectionName}`);

                        if (sectionContent) {
                            const contentElement = sectionContent.querySelector('[data-display-by-default]');
                            if (contentElement && contentElement.dataset.displayByDefault === 'true') {
                                // Map section name to open method
                                if (sectionName === 'window-hero-banner') {
                                    await this.openHeroBanner();
                                } else if (sectionName === 'window-about') {
                                    await this.openAbout();
                                } else if (sectionName === 'window-richtext') {
                                    await this.openRichText(sectionName.replace('window-', ''));
                                } else if (sectionName === 'window-media-player') {
                                    await this.openMediaPlayer();
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.warn(`WindowManager: Could not check ${sectionName} for display_by_default:`, error);
                }
            }
        }

        /**
         * Setup responsive text sizing for hero banner based on window size
         */
        setupHeroBannerResponsiveText(windowElement) {
            const heroDiv = windowElement.querySelector('.st-hero-banner__hero');
            const slogan = windowElement.querySelector('.st-hero-banner__slogan');
            const cta = windowElement.querySelector('.st-hero-banner__cta');

            if (!heroDiv) return;

            const updateTextSizes = () => {
                const heroRect = heroDiv.getBoundingClientRect();
                const heroWidth = heroRect.width;
                const heroHeight = heroRect.height;

                // Calculate font sizes based on hero banner dimensions
                // Use a scale factor based on the smaller dimension to maintain proportions
                const scaleFactor = Math.min(heroWidth / 800, heroHeight / 600); // Base size at 800x600

                // Slogan: scale between 24px (mobile) and 48px (desktop) based on window size
                const sloganBaseSize = 48;
                const sloganMinSize = 24;
                const sloganSize = Math.max(sloganMinSize, sloganBaseSize * scaleFactor);

                if (slogan) {
                    slogan.style.fontSize = `${sloganSize}px`;
                }

                // CTA: scale proportionally
                const ctaBaseSize = 18; // var(--st-font-size-lg) is typically 18px
                const ctaMinSize = 14;
                const ctaSize = Math.max(ctaMinSize, ctaBaseSize * scaleFactor);

                if (cta) {
                    cta.style.fontSize = `${ctaSize}px`;
                }
            };

            // Initial calculation
            updateTextSizes();

            // Update on window resize
            const resizeObserver = new ResizeObserver(() => {
                updateTextSizes();
            });

            resizeObserver.observe(windowElement);
            resizeObserver.observe(heroDiv);

            // Also listen to window resize events
            const handleResize = () => {
                updateTextSizes();
            };
            window.addEventListener('resize', handleResize);

            // Store cleanup function
            if (!windowElement._heroBannerResizeCleanup) {
                windowElement._heroBannerResizeCleanup = () => {
                    resizeObserver.disconnect();
                    window.removeEventListener('resize', handleResize);
                };
            }
        }

        /**
         * Show error message for unimplemented menu items
         */
        showMenuNotImplementedError(menuLabel) {
            const errorWindowId = 'error-menu-not-implemented';

            // Check if error window already exists
            const existingWindow = this.windows.get(errorWindowId);
            if (existingWindow && existingWindow.element) {
                this.bringToFront(errorWindowId);
                return;
            }

            // Create error window with Windows 95 style and tennis-inspired messaging
            this.open(errorWindowId, {
                title: 'Menu Item Out of Bounds',
                icon: null,
                content: `
                    <div class="st-error-dialog">
                        <div class="st-error-dialog__content">
                            <div class="st-error-dialog__icon">⚠️</div>
                            <div class="st-error-dialog__message">
                                <p class="st-error-dialog__title">"${this.escapeHtml(menuLabel)}" is still warming up.</p>
                                <p class="st-error-dialog__text">This menu item needs more practice before it's ready to play. Check back soon for the next match!</p>
                            </div>
                        </div>
                        <div class="st-error-dialog__actions">
                            <button 
                                class="st-button st-button--primary" 
                                onclick="if(window.smashOS?.windowManager){window.smashOS.windowManager.close('${errorWindowId}');}"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                `,
                size: { width: '400px', height: 'auto' },
                position: { x: '50%', y: '50%' },
                canResize: false,
                canMaximize: false,
                showMenuBar: false,
                showStatusBar: false,
                isModal: true
            });
        }
    }

    // Export to global scope
    window.WindowManager = WindowManager;
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = WindowManager;
}

